export const PROGRAM_PROFILES = {
  "BSc Accounting": {

    programmeIntelligence: {
        description:
            "Develops knowledge and practical competence in financial reporting, auditing, taxation, management accounting, corporate finance and accounting information systems, preparing graduates to analyse financial information, support organisational decision-making and uphold professional accounting standards with integrity.",

        knowledgeAreas: [
            "Financial Accounting",
            "Management Accounting",
            "Auditing",
            "Taxation",
            "Corporate Reporting",
            "Business Law",
            "Economics",
            "Finance",
            "Quantitative Methods",
            "Accounting Information Systems"
        ],

        learningEnvironment: [
            "Structured",
            "Academic",
            "Analytical",
            "Professional",
            "Collaborative"
        ],

        learningActivities: [
            "Lectures",
            "Tutorials",
            "Case Studies",
            "Financial Analysis",
            "Spreadsheet Exercises",
            "Individual Assignments",
            "Group Assignments",
            "Research",
            "Presentations"
        ],

        teachingMethods: [
            "Lectures",
            "Guided Practice",
            "Case-Based Learning",
            "Problem Solving",
            "Class Discussions",
            "Independent Study"
        ],

        assessmentMethods: [
            "Written Examinations",
            "Continuous Assessment",
            "Assignments",
            "Case Analysis",
            "Presentations",
            "Group Projects"
        ],

        programmeDemands: [
            "Analyse financial information",
            "Interpret accounting data",
            "Apply accounting standards",
            "Solve numerical problems",
            "Communicate financial findings",
            "Work accurately under deadlines",
            "Collaborate on academic projects"
        ],

        professionalCompetencies: [
            "Financial Reporting",
            "Financial Analysis",
            "Auditing",
            "Tax Compliance",
            "Ethical Decision-Making",
            "Business Communication",
            "Problem Solving"
        ],

        graduateExpectations: [
            "Professional Integrity",
            "Critical Thinking",
            "Analytical Reasoning",
            "Communication",
            "Teamwork",
            "Lifelong Learning"
        ],

        careerPathways: [
            "Accountant",
            "Auditor",
            "Tax Consultant",
            "Financial Analyst",
            "Management Accountant",
            "Internal Auditor"
        ],

        workEnvironment: [
            "Office-based",
            "Structured",
            "Regulated",
            "Deadline-driven",
            "Data-intensive",
            "Team-oriented"
        ],

        // UI Summary Fields
        environment:
            "Structured, analytical and professionally oriented learning environment that emphasises accuracy, ethical practice and evidence-based decision-making.",

        workStyle:
            "Requires organised, detail-oriented and systematic work, with consistent application of accounting standards, logical reasoning and professional judgement.",

        demands:
            "Success requires analytical thinking, numerical accuracy, attention to detail, ethical judgement, effective communication and the ability to meet deadlines.",

        realityCheck: {
            workload: "Moderate to High",
            mathematics: "Moderate",
            reading: "High",
            writing: "Moderate",
            teamwork: "Moderate",
            presentations: "Moderate"
        }
    },

    behaviouralRequirements: {

        evidenceAreas: {

            analyticalReasoning: {

                description:
                    "Ability to interpret, analyse and draw conclusions from numerical and financial information.",

                requiredBecause:
                    "Students regularly analyse financial statements, evaluate accounting data and solve quantitative business problems.",

                observableBehaviours: [
                    "Interprets numerical information accurately",
                    "Identifies patterns in financial data",
                    "Draws evidence-based conclusions",
                    "Compares alternative solutions",
                    "Uses logical reasoning"
                ],

                assessmentIndicators: [
                    "Chooses evidence before making decisions",
                    "Analyses information before acting",
                    "Looks for patterns and inconsistencies",
                    "Evaluates alternatives logically"
                ],

                evidenceWeight: 10,

                behaviouralDimensions: {
                    thinking: 0.70,
                    control: 0.20,
                    flexibility: 0.10
                }

            },

            proceduralDiscipline: {

                description:
                    "Ability to work accurately within established rules, standards and procedures.",

                requiredBecause:
                    "Accounting requires consistent application of professional standards, regulations and reporting procedures.",

                observableBehaviours: [
                    "Follows established procedures",
                    "Works systematically",
                    "Maintains consistency",
                    "Pays attention to compliance"
                ],

                assessmentIndicators: [
                    "Prefers organised approaches",
                    "Checks instructions carefully",
                    "Completes tasks methodically"
                ],

            },

            attentionToDetail: {

                description:
                    "Ability to notice small differences, errors and inconsistencies.",

                requiredBecause:
                    "Small mistakes in accounting can significantly affect financial reporting and organisational decisions.",

                observableBehaviours: [
                    "Detects inconsistencies",
                    "Reviews work carefully",
                    "Produces accurate outputs"
                ],

                assessmentIndicators: [
                    "Checks work before submission",
                    "Spots missing information",
                    "Reviews calculations carefully"
                ],

            },

            communication: {

                description:
                    "Ability to explain financial information clearly and professionally.",

                requiredBecause:
                    "Accounting professionals communicate financial information to colleagues, management and clients.",

                observableBehaviours: [
                    "Explains information clearly",
                    "Organises ideas logically",
                    "Communicates professionally"
                ],

                assessmentIndicators: [
                    "Explains reasoning clearly",
                    "Presents ideas logically",
                    "Adapts explanations to others"
                ],

            },

            collaboration: {

                description:
                    "Ability to work effectively with others to achieve shared objectives.",

                requiredBecause:
                    "Students complete collaborative projects and professionals work within multidisciplinary teams.",

                observableBehaviours: [
                    "Shares responsibilities",
                    "Supports team members",
                    "Contributes constructively"
                ],

                assessmentIndicators: [
                    "Participates in group decisions",
                    "Works cooperatively",
                    "Helps achieve team goals"
                ],

            },

            timeManagement: {

                description:
                    "Ability to organise work and meet deadlines consistently.",

                requiredBecause:
                    "Accounting involves multiple assignments, reporting deadlines and time-sensitive responsibilities.",

                observableBehaviours: [
                    "Plans work effectively",
                    "Prioritises tasks",
                    "Meets deadlines"
                ],

                assessmentIndicators: [
                    "Plans ahead",
                    "Completes work on schedule",
                    "Balances competing priorities"
                ],

            },

            ethicalJudgement: {

                description:
                    "Ability to make responsible decisions guided by integrity and professional ethics.",

                requiredBecause:
                    "Accounting professionals are entrusted with financial information and must apply ethical judgement consistently.",

                observableBehaviours: [
                    "Acts honestly",
                    "Accepts responsibility",
                    "Applies ethical reasoning"
                ],

                assessmentIndicators: [
                    "Chooses ethical actions",
                    "Considers consequences",
                    "Maintains professional integrity"
                ],

            }

        }

    },

    programmeBehaviouralModel: {

        inclusion: {
            score: 40,
            derivedFrom: [
                "Collaboration",
                "Professional Communication"
            ]
        },

        control: {
            score: 92,
            derivedFrom: [
                "Procedural Discipline",
                "Time Management",
                "Professional Responsibility"
            ]
        },

        affection: {
            score: 30,
            derivedFrom: [
                "Team Collaboration"
            ]
        },

        thinking: {
            score: 95,
            derivedFrom: [
                "Analytical Reasoning",
                "Attention to Detail",
                "Evidence-Based Decision Making"
            ]
        },

        flexibility: {
            score: 35,
            derivedFrom: [
                "Structured Working",
                "Regulatory Compliance"
            ]
        },

        traits: [
            "structured",
            "analytical",
            "detail-oriented"
        ]
    },

    behaviourProfile: {

        inclusion: 40,

        control: 92,

        affection: 30,

        thinking: 95,

        flexibility: 35,

        traits: [

            "analytical",

            "structured",

            "precise",

            "methodical",

            "ethical"

        ]

    },

    fitExplanations: {

        High:
            "Accounting requires analytical thinking, structured problem solving, accuracy and sound professional judgement. Your behavioural profile aligns strongly with these demands, suggesting you are likely to feel comfortable working with financial information and applying established accounting standards.",

        Moderate:
            "Accounting combines analytical reasoning, disciplined working, attention to detail and evidence-based decision making. Your behavioural profile aligns well with several of these demands, although some areas may require additional development as you progress through the programme.",

        Low:
            "Accounting requires consistent analytical thinking, disciplined work habits, accuracy and confidence working within established professional standards. Your behavioural profile suggests these may not be your strongest natural preferences, meaning you may need to put in extra effort to develop them and succeed in the programme."

    },

    applicationNarratives: {

        High: {

            title:
                "Why this may suit your learning style",

            message:
                "Your natural behavioural tendencies appear well suited to the disciplined and analytical nature of Accounting. The programme rewards logical reasoning, structured problem-solving, careful attention to detail and the ability to work accurately with complex financial information."
        },

        Moderate: {

            title:
                "Why this may suit your learning style",

            message:
                "Many aspects of Accounting are likely to suit the way you learn and work, particularly where analysis, organisation and evidence-based thinking are involved. Some areas may require greater consistency, precision and comfort with highly structured processes."
        },

        Low: {

            title:
                "Why this may suit your learning style",

            message:
                "Accounting may feel demanding if you naturally prefer environments that emphasise flexibility, experimentation or creative expression over structure and precision. Succeeding in the programme will require developing disciplined work habits, analytical accuracy and confidence working within established professional standards."
        }
    },

    fitDimensions: {
        analytical: 94,
        social: 48,
        structure: 96,
        creativity: 42,
        emotionalDemand: 34,
        adaptability: 52
    },

    environmentTraits: {
        structureLevel: 94,
        collaborationLevel: 50,
        ambiguityLevel: 28,
        technicalIntensity: 82,
        creativityDemand: 42,
        emotionalDemand: 26,
        leadershipDemand: 36,
        communicationIntensity: 54
    },

    learningEnvironment: {
        pace: 72,
        independence: 74,
        practicalLearning: 76,
        teamwork: 52,
        assessmentStructure: 94,
        feedbackFrequency: 62
    },

    semanticTypes: [
        "analytical_program",
        "structured_program",
        "business_program",
        "finance_program",
        "evidence_based_program",
        "professional_program",
        "systems_oriented_program"
    ],

    skillsToStrengthen: [
        "Attention to detail",
        "Financial communication",
        "Data interpretation"
    ],

    explanationBlueprint: {

        programmeIdentity: {

            title: "BSc. Accounting",

            summary:
                "Accounting is centred on understanding, analysing and communicating financial information to support organisational decision-making.",

            purpose:
                "The programme prepares students to interpret financial information, apply professional standards and provide reliable evidence that supports business and financial decisions."

        },

        behaviouralStory: {

            whoThrives: {

                summary:
                    "Students who naturally enjoy analysing information carefully, working methodically and producing accurate work often adapt well to the behavioural demands of Accounting.",

                keyBehaviours: [

                    "Analytical reasoning",

                    "Procedural discipline",

                    "Attention to detail",

                    "Professional responsibility"

                ]

            },

            whyBehaviourMatters: {

                summary:
                    "Accounting depends on behaviours that support careful analysis, consistent application of standards and responsible professional judgement.",

                evidenceAreas: [

                    "analyticalReasoning",

                    "proceduralDiscipline",

                    "attentionToDetail",

                    "ethicalJudgement"

                ]

            },

            learningJourney: {

                summary:
                    "Students gradually progress from understanding accounting principles to analysing increasingly complex financial information, applying professional standards and making evidence-based recommendations."

            },

            professionalMindset: {

                summary:
                    "Successful accountants combine analytical thinking with accuracy, integrity, consistency and professional responsibility."

            }

        },

        fitInterpretation: {

            high: {

                message:
                    "Your behavioural profile already reflects many of the natural behaviours that Accounting regularly rewards.",

                coachingFocus: [

                    "Professional communication",

                    "Leadership",

                    "Continuous professional development"

                ]

            },

            moderate: {

                message:
                    "Your behavioural profile provides a good foundation for Accounting while highlighting several behaviours that will continue developing throughout your studies.",

                coachingFocus: [

                    "Analytical discipline",

                    "Attention to detail",

                    "Professional consistency"

                ]

            },

            low: {

                message:
                    "Some of the behavioural demands of Accounting may initially feel less natural, although these behaviours can be strengthened through deliberate practice and experience.",

                coachingFocus: [

                    "Analytical reasoning",

                    "Procedural discipline",

                    "Working systematically",

                    "Evidence-based decision making"

                ]

            }

        },

        futureOutlook: {

            summary:
                "Graduates who develop these behaviours are well prepared for careers requiring professional judgement, financial accountability and evidence-based decision-making.",

            careerMindset:
                "Accounting professionals are trusted to provide accurate financial information that supports organisations, governments and society."

        }

    },

    behaviouralNarrative: {

        High({ applicantInterpretation }) {

        return `
    Your assessment suggests that many of your natural behavioural tendencies already align well with the disciplined and analytical nature of Accounting. You appear comfortable approaching problems thoughtfully, working in an organised manner and making decisions based on evidence rather than guesswork.

    These behaviours provide a strong foundation for a programme that regularly requires students to analyse financial information, apply accounting standards and produce accurate work. Throughout your studies, you will encounter structured learning activities that reward careful reasoning, consistency and attention to detail rather than quick or impulsive decision-making.

    As your knowledge develops, you will also be expected to communicate financial information clearly, work effectively with others on projects and apply professional judgement in increasingly complex situations. Continuing to strengthen these behaviours will prepare you not only for success at university but also for the professional responsibilities expected of accountants.

    Overall, your behavioural profile suggests that you already possess many of the natural tendencies that Accounting is designed to develop further.
    `;

    },

        Moderate: {

            opening:
                "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Accounting, although some behaviours will continue developing as you progress through the programme.",

            strengths:
                "You already demonstrate behaviours that support analytical thinking and organised working, both of which are important throughout your studies.",

            programmeConnection:
                "Accounting requires students to analyse evidence carefully, work accurately within established standards and make logical, evidence-based judgements. While many of these behaviours already appear in your profile, some may require greater consistency as academic expectations increase.",

            coaching:
                "Focus on becoming more systematic when solving problems, checking your work carefully and maintaining consistency when working under deadlines.",

            conclusion:
                "With continued practice and deliberate development, your behavioural profile can support successful study in Accounting."

        },

        Low: {

            opening:
                "Your assessment suggests that some of the behaviours most frequently rewarded in Accounting may not yet be your strongest natural preferences.",

            strengths:
                "This does not prevent success, but it means you may need to approach some aspects of the programme more deliberately than students whose natural preferences already align closely with its demands.",

            programmeConnection:
                "Accounting places strong emphasis on analytical reasoning, careful attention to detail, structured working and consistent application of professional standards. These behaviours become increasingly important throughout the programme.",

            coaching:
                "Consciously practising disciplined study habits, analytical reasoning, accuracy and systematic problem-solving will make the programme significantly more manageable over time.",

            conclusion:
                "Behaviour develops through learning and experience. Students who deliberately strengthen these habits can still become successful Accounting graduates."

        }

    },
    
},

  "LLB Law": {

        programmeIntelligence: {

        description:
            "The Bachelor of Laws (LLB) programme develops a comprehensive understanding of Ghanaian and international legal systems, legal institutions and the principles governing public and private law. Through progressive study of legal doctrine, legal reasoning, constitutional governance, commercial law, international law, legal research and specialised areas of practice, students learn to analyse legal issues, interpret legislation, evaluate judicial decisions and apply legal principles to increasingly complex legal and societal problems.",

        knowledgeAreas: [
            "Ghanaian Legal System",
            "Legal Method",
            "Constitutional Law",
            "Administrative Law",
            "Criminal Law",
            "Law of Contract",
            "Law of Torts",
            "Company Law",
            "Commercial Law",
            "Labour and Employment Law",
            "Law of Immovable Property",
            "Tax Law",
            "Law of Equity and Succession",
            "Jurisprudence",
            "Public International Law",
            "Private International Law",
            "Human Rights Law",
            "International Trade and Investment Law",
            "Environmental Law",
            "Natural Resources Law",
            "Energy Law",
            "Information Technology Law",
            "Intellectual Property Law",
            "Consumer Protection Law",
            "Procurement Law",
            "Alternative Dispute Resolution",
            "Insurance Law",
            "Media Law",
            "Law and Accountable Institutions"
        ],

        learningEnvironment: [
            "Highly structured",
            "Reading-intensive",
            "Analytical",
            "Research-oriented",
            "Discussion-based",
            "Argument-driven",
            "Writing-intensive",
            "Ethically grounded",
            "Independent learning",
            "Professionally focused"
        ],

        learningActivities: [
            "Reading statutes and legal authorities",
            "Studying judicial decisions",
            "Interpreting constitutional and statutory provisions",
            "Analysing legal problems",
            "Evaluating legal principles",
            "Conducting legal research",
            "Writing legal analyses",
            "Preparing research papers",
            "Applying legal rules to practical situations",
            "Participating in tutorials and class discussions",
            "Presenting legal arguments",
            "Completing independent project work",
            "Examining legal institutions",
            "Comparing domestic and international legal frameworks"
        ],

        teachingMethods: [
            "Lectures",
            "Tutorials",
            "Case-based learning",
            "Legal problem analysis",
            "Seminars",
            "Guided discussions",
            "Research supervision",
            "Independent project work",
            "Practical legal exercises"
        ],

        assessmentMethods: [
            "Written examinations",
            "Continuous assessment",
            "Legal problem-solving questions",
            "Research assignments",
            "Essays",
            "Term papers",
            "Project work",
            "Legal analysis exercises",
            "Oral presentations"
        ],

        programmeDemands: [
            "Interpret statutes and constitutional provisions",
            "Analyse judicial decisions",
            "Apply legal principles to practical situations",
            "Evaluate competing legal arguments",
            "Conduct independent legal research",
            "Read and synthesise extensive legal materials",
            "Write structured legal analyses",
            "Construct logical legal arguments",
            "Interpret complex legal concepts",
            "Apply legal reasoning across different areas of law",
            "Critically evaluate public institutions and legal systems",
            "Maintain high standards of accuracy and precision"
        ],

        professionalCompetencies: [
            "Legal reasoning",
            "Legal research",
            "Legal analysis",
            "Statutory interpretation",
            "Critical thinking",
            "Problem solving",
            "Analytical writing",
            "Professional communication",
            "Policy analysis",
            "Ethical judgement",
            "Decision making",
            "Argument development",
            "Legal evaluation",
            "Institutional analysis"
        ],

        graduateExpectations: [
            "Interpret and apply legal rules appropriately",
            "Analyse complex legal issues objectively",
            "Conduct independent legal research",
            "Prepare well-supported legal opinions",
            "Communicate legal ideas clearly in writing and speech",
            "Evaluate legal institutions and governance systems critically",
            "Apply legal knowledge across public and private sectors",
            "Demonstrate professionalism and ethical responsibility",
            "Address legal problems using sound analytical reasoning",
            "Contribute effectively to legal, corporate and public institutions"
        ],

        careerPathways: [
            "Legal Practice",
            "Corporate Legal Services",
            "Judicial Service",
            "Public Service",
            "Regulatory Authorities",
            "Corporate Governance",
            "Compliance",
            "Legal Consultancy",
            "Public Policy",
            "International Organisations",
            "Human Rights Organisations",
            "Procurement and Contract Management",
            "Labour Relations",
            "Tax Advisory",
            "Environmental and Natural Resource Regulation",
            "Academia and Research"
        ],

        workEnvironment: [
            "Law firms",
            "Courts and judicial institutions",
            "Government ministries and agencies",
            "Regulatory authorities",
            "Corporate legal departments",
            "International organisations",
            "Public institutions",
            "Research and policy institutes",
            "Compliance and governance offices",
            "Alternative dispute resolution centres"
        ],

        // UI Summary Fields

        environment:
            "The programme provides a highly structured and intellectually rigorous learning environment centred on legal reasoning, statutory interpretation, constitutional governance and legal research. Students progressively study multiple branches of law while examining how legal systems, institutions and public policy operate within Ghana and internationally.",

        workStyle:
            "Students spend much of the programme analysing legal materials, interpreting legislation, evaluating judicial decisions, conducting independent research and producing structured written analyses. Learning combines individual study with classroom discussions, tutorials, research projects and practical legal problem-solving.",

        demands:
            "Success requires sustained reading, disciplined study habits, critical thinking, precise interpretation of legal materials and the ability to construct logical, evidence-based legal arguments. Students are expected to synthesise complex information, communicate clearly and apply legal principles across diverse legal contexts.",

        realityCheck: {
            workload: "Very High",
            mathematics: "Low",
            reading: "Very High",
            writing: "Very High",
            teamwork: "Moderate",
            presentations: "Moderate to High"
        }

    },

    behaviouralRequirements: {

        evidenceAreas: {

            legalReasoning: {

                description:
                    "Ability to interpret legal principles, analyse legal issues and apply the law logically to practical situations.",

                requiredBecause:
                    "Students consistently study legal rules, interpret statutes, analyse judicial decisions and apply legal principles across multiple areas of law throughout the programme.",

                observableBehaviours: [
                    "Analyses legal issues systematically",
                    "Interprets legal information logically",
                    "Applies principles to practical situations",
                    "Constructs logical arguments",
                    "Evaluates alternative legal positions"
                ],

                assessmentIndicators: [
                    "Analyses information before making decisions",
                    "Uses evidence to support conclusions",
                    "Identifies relevant facts",
                    "Evaluates multiple perspectives logically"
                ],

                evidenceWeight: 10,

                behaviouralDimensions: {
                    thinking: 0.70,
                    control: 0.20,
                    flexibility: 0.10
                }

            },

            legalResearch: {

                description:
                    "Ability to locate, evaluate and synthesise legal information from multiple authoritative sources.",

                requiredBecause:
                    "The curriculum requires continuous legal research, interpretation of legal authorities and independent investigation of legal problems.",

                observableBehaviours: [
                    "Investigates issues thoroughly",
                    "Seeks relevant evidence",
                    "Synthesises information accurately",
                    "Works independently"
                ],

                assessmentIndicators: [
                    "Looks beyond obvious answers",
                    "Researches before concluding",
                    "Examines information critically"
                ],

            },

            analyticalPrecision: {

                description:
                    "Ability to examine legal materials carefully while recognising important distinctions and inconsistencies.",

                requiredBecause:
                    "Students regularly interpret legislation, judicial decisions and legal concepts where accuracy and careful analysis are essential.",

                observableBehaviours: [
                    "Pays close attention to detail",
                    "Recognises inconsistencies",
                    "Interprets information accurately"
                ],

                assessmentIndicators: [
                    "Reviews information carefully",
                    "Identifies important details",
                    "Produces accurate analyses"
                ],

            },

            communication: {

                description:
                    "Ability to communicate legal ideas clearly, logically and persuasively in both written and oral form.",

                requiredBecause:
                    "Students prepare legal analyses, research assignments, presentations and structured legal arguments throughout the programme.",

                observableBehaviours: [
                    "Expresses ideas clearly",
                    "Organises arguments logically",
                    "Communicates professionally"
                ],

                assessmentIndicators: [
                    "Explains reasoning clearly",
                    "Structures ideas logically",
                    "Communicates confidently"
                ],

            },

            collaboration: {

                description:
                    "Ability to engage constructively with others during discussions, tutorials and collaborative academic activities.",

                requiredBecause:
                    "Although much of the programme involves independent study, students also participate in discussions, tutorials and collaborative learning activities.",

                observableBehaviours: [
                    "Participates respectfully",
                    "Listens to others",
                    "Contributes constructively"
                ],

                assessmentIndicators: [
                    "Works cooperatively",
                    "Shares ideas appropriately",
                    "Supports productive discussions"
                ],

            },

            timeManagement: {

                description:
                    "Ability to organise learning effectively, manage workload and meet demanding academic deadlines.",

                requiredBecause:
                    "The programme involves extensive reading, legal research, written assignments and sustained independent study across multiple areas of law.",

                observableBehaviours: [
                    "Plans work effectively",
                    "Prioritises responsibilities",
                    "Maintains consistent study habits"
                ],

                assessmentIndicators: [
                    "Plans ahead",
                    "Completes work on schedule",
                    "Balances multiple responsibilities"
                ],

            },

            ethicalJudgement: {

                description:
                    "Ability to make responsible decisions guided by legal principles, integrity and professional ethics.",

                requiredBecause:
                    "The curriculum emphasises legal ethics, responsible professional conduct and the administration of justice across legal practice and public institutions.",

                observableBehaviours: [
                    "Acts with integrity",
                    "Accepts responsibility",
                    "Considers ethical implications"
                ],

                assessmentIndicators: [
                    "Chooses responsible actions",
                    "Demonstrates sound judgement",
                    "Maintains professional standards"
                ],

            }

        }

    },

    programmeBehaviouralModel: {

        inclusion: {
            score: 45,
            derivedFrom: [
                "Professional Communication",
                "Collaborative Learning"
            ]
        },

        control: {
            score: 88,
            derivedFrom: [
                "Legal Research",
                "Time Management",
                "Professional Responsibility"
            ]
        },

        affection: {
            score: 35,
            derivedFrom: [
                "Collaboration",
                "Professional Ethics"
            ]
        },

        thinking: {
            score: 98,
            derivedFrom: [
                "Legal Reasoning",
                "Analytical Precision",
                "Critical Legal Analysis"
            ]
        },

        flexibility: {
            score: 50,
            derivedFrom: [
                "Legal Problem Solving",
                "Interpretation of Legal Principles"
            ]
        },

        traits: [
            "analytical",
            "logical",
            "disciplined"
        ]

    },

    behaviourProfile: {

        inclusion: 48,

        control: 88,

        affection: 35,

        thinking: 98,

        flexibility: 50,

        traits: [

            "analytical",

            "disciplined",

            "logical",

            "objective",

            "precise"

        ]

    },

    fitExplanations: {

        High:
            "Law requires analytical reasoning, disciplined research, precise communication and logical argument development. Your behavioural profile aligns strongly with these demands, suggesting you are likely to thrive in the programme's structured and intellectually rigorous learning environment.",

        Moderate:
            "Law combines analytical thinking, structured research, critical evaluation and professional communication. Your behavioural profile aligns well with several of these demands, although some areas may require additional development as you progress through the programme.",

        Low:
            "Law requires sustained analytical reasoning, disciplined study, precise communication and logical argument development. Your behavioural profile suggests these may not be your strongest natural preferences, meaning you may need to put in extra effort to develop them and succeed in the programme."

    },

    applicationNarratives: {
        High: {
            title:
                "Why this may suit your learning style",

            message:
                "Your personality appears strongly aligned with the disciplined and intellectually demanding nature of Law. The field rewards structured reasoning, analytical thinking, communication, and the ability to work effectively under pressure."
        },

        Moderate: {
            title:
                "Why this may suit your learning style",

            message:
                "Several aspects of Law may align naturally with your strengths, especially where reasoning, communication, and structured analysis are involved. However, the pressure, workload intensity, and argumentative nature of the field may occasionally require adaptation."
        },

        Low: {
            title:
                "Why this may suit your learning style",

            message:
                "Law may feel highly rigid, pressure-driven, or intellectually confrontational if you naturally prefer flexible, emotionally expressive, or less structured environments. Developing comfort with sustained analytical discipline and high-pressure reasoning may require significant adjustment."
        }
    },

    fitDimensions: {
        analytical: 94,
        social: 78,
        structure: 92,
        creativity: 52,
        emotionalDemand: 68,
        adaptability: 70
    },

    environmentTraits: {

        structureLevel: 92,

        collaborationLevel: 58,

        ambiguityLevel: 68,

        technicalIntensity: 46,

        creativityDemand: 34,

        emotionalDemand: 74,

        leadershipDemand: 84,

        communicationIntensity: 98

    },

    semanticTypes: [
        "analytical_program",
        "structured_program",
        "communication_program",
        "leadership_oriented_program",
        "policy_oriented_program"
    ],

    learningEnvironment: {

        pace: 82,

        independence: 84,

        practicalLearning: 72,

        teamwork: 56,

        assessmentStructure: 92,

        feedbackFrequency: 64

    },

    skillsToStrengthen: [
        "Critical Reasoning",
        "Argument Development",
        "Research Writing",
        "Public Speaking",
        "Analytical Thinking"
    ],

    explanationBlueprint: {

        programmeIdentity: {

            title: "LLB Law",

            summary:
                "Law is centred on understanding, interpreting and applying legal principles to analyse complex issues, resolve disputes and support justice within society.",

            purpose:
                "The programme prepares students to interpret legislation, evaluate judicial decisions, construct evidence-based legal arguments and apply legal reasoning across diverse legal, corporate and public contexts."

        },

        behaviouralStory: {

            whoThrives: {

                summary:
                    "Students who naturally enjoy analysing complex information, thinking critically, reading carefully and constructing logical arguments often adapt well to the intellectual demands of Law.",

                keyBehaviours: [

                    "Legal reasoning",

                    "Critical analysis",

                    "Disciplined independent study",

                    "Logical argument development"

                ]

            },

            whyBehaviourMatters: {

                summary:
                    "Law depends on behaviours that support careful interpretation, objective reasoning, ethical judgement and the ability to justify conclusions using evidence rather than opinion.",

                evidenceAreas: [

                    "legalReasoning",

                    "legalResearch",

                    "analyticalPrecision",

                    "ethicalJudgement"

                ]

            },

            learningJourney: {

                summary:
                    "Students progressively develop from learning foundational legal principles to interpreting complex legislation, analysing judicial decisions, conducting independent legal research and applying legal reasoning across increasingly sophisticated legal problems."

            },

            professionalMindset: {

                summary:
                    "Successful legal professionals combine intellectual discipline, analytical reasoning, ethical responsibility and clear communication to interpret the law objectively and support fair decision-making."

            }

        },

        fitInterpretation: {

            high: {

                message:
                    "Your behavioural profile already reflects many of the habits that Law consistently rewards, particularly analytical thinking, structured reasoning and disciplined problem-solving.",

                coachingFocus: [

                    "Advanced legal writing",

                    "Professional advocacy",

                    "Leadership in legal practice"

                ]

            },

            moderate: {

                message:
                    "Your behavioural profile provides a solid foundation for studying Law while highlighting behaviours that will continue developing through sustained legal research, critical analysis and structured argumentation.",

                coachingFocus: [

                    "Analytical precision",

                    "Independent legal research",

                    "Structured legal communication"

                ]

            },

            low: {

                message:
                    "Some of the intellectual demands of Law may initially feel less natural, particularly where sustained reading, structured reasoning and evidence-based legal analysis are required. These behaviours, however, can be strengthened through deliberate practice and disciplined study.",

                coachingFocus: [

                    "Critical reasoning",

                    "Reading discipline",

                    "Logical argument development",

                    "Analytical writing"

                ]

            }

        },

        futureOutlook: {

            summary:
                "Graduates who develop these behaviours are well prepared to interpret complex legal issues, advise responsibly, uphold professional ethics and contribute meaningfully to legal practice, governance and public decision-making.",

            careerMindset:
                "Legal professionals are trusted to interpret the law objectively, exercise sound judgement and communicate reasoned decisions that influence individuals, organisations and society."

        }

    },

    behaviouralNarrative: {

        High({ applicantInterpretation }) {

            return `
    Your assessment suggests that many of your natural behavioural tendencies already align well with the intellectual demands of Law. You appear comfortable approaching complex problems thoughtfully, organising information carefully and making decisions based on logic and evidence rather than assumption.

    These behaviours provide a strong foundation for a programme that requires students to interpret legislation, analyse judicial decisions, evaluate competing legal arguments and apply legal principles across increasingly complex situations. Throughout your studies, you will engage with extensive legal reading, independent research and structured legal writing that consistently reward disciplined reasoning and careful analysis.

    As your legal knowledge develops, you will also learn to communicate legal arguments clearly, evaluate multiple perspectives objectively and exercise sound professional judgement when addressing legal and societal issues. Continuing to strengthen these behaviours will prepare you not only for academic success but also for the professional responsibilities expected within legal practice, corporate governance and public institutions.

    Overall, your behavioural profile suggests that you already demonstrate many of the behavioural tendencies that the study and practice of Law are designed to develop further.
    `;

        },

        Moderate: {

            opening:
                "Your assessment indicates that several aspects of your behavioural profile provide a solid foundation for studying Law, although some behaviours will continue developing as the intellectual demands of the programme increase.",

            strengths:
                "You already demonstrate behaviours that support careful reasoning, organised learning and analytical thinking, all of which contribute positively to legal study.",

            programmeConnection:
                "Law requires students to interpret complex legal materials, analyse competing arguments, conduct independent research and communicate conclusions using clear legal reasoning. While many of these behaviours already appear within your profile, others will become stronger through sustained reading, structured analysis and practical legal problem-solving.",

            coaching:
                "Focus on strengthening disciplined reading habits, analytical writing, logical argument development and confidence when evaluating complex legal issues from multiple perspectives.",

            conclusion:
                "With consistent practice and deliberate development, your behavioural profile can support successful study and long-term growth within the legal profession."

        },

        Low: {

            opening:
                "Your assessment suggests that some of the behaviours most consistently rewarded throughout the study of Law may not yet be among your strongest natural preferences.",

            strengths:
                "This does not prevent success, but it means you may need to approach some aspects of the programme more deliberately than students whose behavioural tendencies already align closely with its intellectual demands.",

            programmeConnection:
                "Law places sustained emphasis on critical reasoning, careful interpretation, disciplined reading, structured analysis and evidence-based legal argument. These behaviours become increasingly important as students progress through more advanced areas of legal study.",

            coaching:
                "Deliberately strengthening analytical reasoning, disciplined study habits, structured writing and confidence when evaluating complex legal problems will make the programme progressively more manageable over time.",

            conclusion:
                "Behaviour develops through learning and experience. Students who intentionally cultivate these habits can develop into capable legal professionals and contribute meaningfully to the administration of justice."

        }

    },

},

  "BSc Business Administration": {

    programmeIntelligence: {

        description:
            "Develops students' understanding of how organisations are established, managed and improved within local and global business environments. The curriculum integrates management, finance, accounting, marketing, economics, operations, human resource management, information systems, law, governance and entrepreneurship to prepare students to analyse business problems, coordinate organisational resources, make informed managerial decisions and contribute to organisational performance. Throughout the programme, students progressively develop strategic, analytical, ethical and leadership capabilities while applying business knowledge through research, projects and workplace experience.",

        knowledgeAreas: [

            "Management principles and organisational theory",
            "Leadership and organisational behaviour",
            "Business communication and professional writing",
            "Business economics and managerial economics",
            "Business finance and corporate finance",
            "Financial and management accounting",
            "Marketing principles and marketing management",
            "Business statistics and quantitative methods",
            "Business research methods",
            "Business information systems",
            "Information technology applications",
            "Human resource management",
            "International human resource management",
            "Operations management",
            "Supply chain management",
            "Project management",
            "Entrepreneurship development",
            "Small and Medium Scale Enterprise management",
            "Business policy and strategic management",
            "Corporate governance",
            "Business ethics",
            "Risk and insurance management",
            "Business law and company law",
            "Public administration",
            "Environmental management",
            "Quality management",
            "International business",
            "Electronic commerce",
            "Taxation"

        ],

        learningEnvironment: [

            "Structured classroom instruction",
            "Business-oriented learning environment",
            "Collaborative group learning",
            "Case-based discussions",
            "Practical business problem solving",
            "Research-driven learning",
            "Technology-supported learning",
            "Project-based learning",
            "Professional presentations",
            "Industrial attachment and workplace experience",
            "Independent study",
            "Continuous coursework and practical application"

        ],

        learningActivities: [

            "Analysing business cases",
            "Solving managerial and organisational problems",
            "Conducting business research",
            "Writing academic and business reports",
            "Preparing business presentations",
            "Participating in group discussions",
            "Completing group projects",
            "Applying quantitative analysis to business decisions",
            "Evaluating organisational performance",
            "Developing business strategies",
            "Planning and managing projects",
            "Conducting organisational analysis",
            "Using information systems and business technologies",
            "Preparing research proposals and project reports",
            "Industrial internship and workplace observation"

        ],

        teachingMethods: [

            "Lectures",
            "Tutorials",
            "Case study analysis",
            "Class discussions",
            "Group work",
            "Research supervision",
            "Project supervision",
            "Practical exercises",
            "Presentations",
            "Technology-assisted instruction",
            "Industrial attachment supervision"

        ],

        assessmentMethods: [

            "Written examinations",
            "Individual assignments",
            "Group assignments",
            "Case analyses",
            "Business reports",
            "Research proposals",
            "Research project",
            "Project work",
            "Oral presentations",
            "Class participation",
            "Practical exercises",
            "Internship reports"

        ],

        programmeDemands: [

            "Analysing complex business situations",
            "Applying theory to organisational practice",
            "Integrating knowledge across business disciplines",
            "Making evidence-based managerial decisions",
            "Working effectively in teams",
            "Communicating professionally in written and oral forms",
            "Managing multiple academic projects simultaneously",
            "Conducting independent research",
            "Interpreting quantitative and financial information",
            "Planning and organising work systematically",
            "Evaluating organisational performance",
            "Considering ethical, legal and governance implications in business decisions",
            "Adapting to changing business environments"

        ],

        professionalCompetencies: [

            "Business analysis",
            "Strategic thinking",
            "Managerial decision making",
            "Leadership",
            "Organisational planning",
            "Problem solving",
            "Professional communication",
            "Business research",
            "Project management",
            "Human resource management",
            "Financial interpretation",
            "Operational planning",
            "Marketing analysis",
            "Risk awareness",
            "Corporate governance awareness",
            "Ethical judgement",
            "Technology utilisation in business",
            "Collaboration",
            "Critical thinking"

        ],

        graduateExpectations: [

            "Apply integrated business knowledge to organisational challenges",
            "Analyse organisational problems using appropriate business tools",
            "Support managerial and strategic decision making",
            "Communicate effectively with diverse stakeholders",
            "Demonstrate professional and ethical judgement",
            "Work effectively within multidisciplinary teams",
            "Lead projects and organisational initiatives responsibly",
            "Adapt to evolving business environments",
            "Conduct business research and apply findings appropriately",
            "Contribute to organisational improvement and sustainable performance"

        ],

        careerPathways: [

            "Business Administrator",
            "Operations Officer",
            "Management Trainee",
            "Business Development Officer",
            "Human Resource Officer",
            "Marketing Officer",
            "Business Analyst",
            "Project Coordinator",
            "Operations Manager",
            "Supply Chain Officer",
            "Corporate Services Officer",
            "Entrepreneur",
            "Management Consultant",
            "Public Sector Administrator",
            "NGO Programme Officer",
            "Business Research Officer"

        ],

        workEnvironment: [

            "Corporate organisations",
            "Small and Medium Enterprises (SMEs)",
            "Financial institutions",
            "Government agencies",
            "Public sector organisations",
            "Non-governmental organisations",
            "Consulting firms",
            "Multinational organisations",
            "Entrepreneurial ventures",
            "Business support services",
            "Project-based organisations",
            "Cross-functional business teams"

        ],

        // =====================================================
        // UI Summary Fields
        // =====================================================

        environment:
            "A structured, collaborative and professionally oriented business environment where students analyse organisational challenges, integrate knowledge across business functions and develop practical management capabilities.",

        workStyle:
            "Business Administration students regularly balance analytical thinking, planning, collaboration, leadership and professional communication while solving organisational problems and coordinating people, processes and resources.",

        demands:
            "Success requires strong organisational skills, analytical reasoning, effective communication, teamwork, ethical judgement, research capability and the ability to make informed managerial decisions across diverse business contexts.",

        realityCheck: {

            workload:
                "High. Students manage coursework across multiple business disciplines alongside research, presentations, projects and internship requirements.",

            mathematics:
                "Moderate. Quantitative reasoning is required through Business Statistics, Quantitative Methods, Finance, Economics, Management Accounting and Corporate Finance rather than advanced mathematics.",

            reading:
                "High. Students engage extensively with business theories, organisational concepts, legal materials, research literature, case studies and professional reports.",

            writing:
                "High. The programme requires academic writing, business reports, research proposals, project work, presentations and professional communication.",

            teamwork:
                "High. Group discussions, collaborative assignments, organisational projects and leadership activities are recurring throughout the programme.",

            presentations:
                "High. Students are expected to present research findings, business analyses, project work and strategic recommendations throughout the programme."

        }

    },

    behaviouralRequirements: {

        evidenceAreas: {

            strategicDecisionMaking: {

                description:
                    "Evaluates information systematically to make informed, evidence-based organisational decisions that balance objectives, risks and available resources.",

                requiredBecause:
                    "Business Administration students repeatedly analyse financial, operational, economic and strategic information before making managerial decisions across diverse organisational contexts.",

                observableBehaviours: [

                    "Analyses available information before deciding",
                    "Compares alternative solutions objectively",
                    "Balances short-term and long-term outcomes",
                    "Considers organisational implications before acting",
                    "Makes reasoned decisions under uncertainty"

                ],

                assessmentIndicators: [

                    "Uses evidence rather than assumptions",
                    "Recognises trade-offs between alternatives",
                    "Evaluates consequences logically",
                    "Prioritises organisational objectives",
                    "Justifies decisions using available information"

                ],

                evidenceWeight: 12,

                behaviouralDimensions: {

                    inclusion: 45,
                    control: 82,
                    affection: 32,
                    thinking: 95,
                    flexibility: 68

                }

            },

            organisationalLeadership: {

                description:
                    "Provides direction, coordinates people and motivates teams towards achieving organisational objectives.",

                requiredBecause:
                    "Leadership is progressively developed through management, organisational behaviour, human resource management, project management and strategic management courses.",

                observableBehaviours: [

                    "Coordinates group activities",
                    "Accepts responsibility for outcomes",
                    "Supports team performance",
                    "Organises people effectively",
                    "Provides direction when needed"

                ],

                assessmentIndicators: [

                    "Demonstrates initiative",
                    "Accepts accountability",
                    "Encourages collective achievement",
                    "Coordinates resources effectively",
                    "Balances authority with collaboration"

                ],

                evidenceWeight: 11,

                behaviouralDimensions: {

                    inclusion: 70,
                    control: 92,
                    affection: 62,
                    thinking: 78,
                    flexibility: 60

                }

            },

            organisationalPlanning: {

                description:
                    "Plans, organises and coordinates organisational activities, resources and processes to achieve operational objectives efficiently.",

                requiredBecause:
                    "Operations Management, Project Management, Supply Chain Management, Strategic Management and Total Quality Management all require systematic planning and coordination.",

                observableBehaviours: [

                    "Plans activities before execution",
                    "Organises tasks systematically",
                    "Coordinates multiple priorities",
                    "Maintains structured workflows",
                    "Monitors progress towards objectives"

                ],

                assessmentIndicators: [

                    "Creates logical plans",
                    "Prioritises effectively",
                    "Maintains organisation under pressure",
                    "Coordinates resources efficiently",
                    "Reviews progress systematically"

                ],

                evidenceWeight: 11,

                behaviouralDimensions: {

                    inclusion: 48,
                    control: 90,
                    affection: 36,
                    thinking: 84,
                    flexibility: 58

                }

            },

            professionalCommunication: {

                description:
                    "Communicates ideas clearly and professionally through written, verbal and interpersonal communication within organisational settings.",

                requiredBecause:
                    "Communication, scholarly writing, research, presentations, internship and business reporting are recurring requirements throughout the programme.",

                observableBehaviours: [

                    "Expresses ideas clearly",
                    "Presents information confidently",
                    "Writes organised reports",
                    "Communicates appropriately with others",
                    "Adjusts communication for different audiences"

                ],

                assessmentIndicators: [

                    "Communicates with clarity",
                    "Organises information logically",
                    "Demonstrates active listening",
                    "Explains reasoning effectively",
                    "Maintains professional communication"

                ],

                evidenceWeight: 10,

                behaviouralDimensions: {

                    inclusion: 72,
                    control: 58,
                    affection: 72,
                    thinking: 70,
                    flexibility: 64

                }

            },

            businessAnalysis: {

                description:
                    "Examines business information critically to identify patterns, evaluate performance and recommend practical organisational solutions.",

                requiredBecause:
                    "The curriculum consistently develops analytical capability through statistics, economics, accounting, finance, marketing and information systems.",

                observableBehaviours: [

                    "Interprets data objectively",
                    "Identifies business problems",
                    "Evaluates organisational performance",
                    "Recognises relationships between variables",
                    "Develops evidence-based recommendations"

                ],

                assessmentIndicators: [

                    "Interprets information accurately",
                    "Identifies underlying causes",
                    "Applies logical reasoning",
                    "Supports conclusions with evidence",
                    "Evaluates business performance objectively"

                ],

                evidenceWeight: 12,

                behaviouralDimensions: {

                    inclusion: 40,
                    control: 68,
                    affection: 28,
                    thinking: 96,
                    flexibility: 66

                }

            },

            researchAndInquiry: {

                description:
                    "Investigates organisational issues systematically by collecting, analysing and interpreting evidence before reaching conclusions.",

                requiredBecause:
                    "Research Methods, Project Work and quantitative courses require students to investigate organisational problems using appropriate research methodologies.",

                observableBehaviours: [

                    "Seeks relevant evidence",
                    "Investigates problems thoroughly",
                    "Evaluates information critically",
                    "Draws logical conclusions",
                    "Supports recommendations with evidence"

                ],

                assessmentIndicators: [

                    "Uses systematic investigation",
                    "Evaluates information quality",
                    "Synthesises evidence effectively",
                    "Draws justified conclusions",
                    "Maintains objectivity"

                ],

                evidenceWeight: 9,

                behaviouralDimensions: {

                    inclusion: 38,
                    control: 62,
                    affection: 30,
                    thinking: 94,
                    flexibility: 70

                }

            },

            ethicalResponsibility: {

                description:
                    "Acts responsibly by considering ethical principles, governance responsibilities and the wider organisational consequences of decisions.",

                requiredBecause:
                    "Business Ethics, Corporate Governance, Law, Public Administration and Risk Management consistently reinforce responsible organisational conduct.",

                observableBehaviours: [

                    "Considers ethical consequences",
                    "Acts responsibly",
                    "Balances stakeholder interests",
                    "Recognises governance responsibilities",
                    "Maintains professional integrity"

                ],

                assessmentIndicators: [

                    "Identifies ethical issues",
                    "Demonstrates fairness",
                    "Considers organisational accountability",
                    "Recognises legal implications",
                    "Supports responsible decisions"

                ],

                evidenceWeight: 9,

                behaviouralDimensions: {

                    inclusion: 60,
                    control: 72,
                    affection: 78,
                    thinking: 74,
                    flexibility: 56

                }

            },

            collaborationAndPeopleManagement: {

                description:
                    "Works effectively with others while supporting teamwork, managing relationships and coordinating collective organisational performance.",

                requiredBecause:
                    "Organisational Behaviour, Human Resource Management, Leadership, Internship and Project Work repeatedly require collaboration and people management.",

                observableBehaviours: [

                    "Works cooperatively",
                    "Supports team members",
                    "Coordinates group activities",
                    "Builds productive relationships",
                    "Manages interpersonal differences constructively"

                ],

                assessmentIndicators: [

                    "Encourages participation",
                    "Maintains productive relationships",
                    "Resolves disagreements constructively",
                    "Supports shared objectives",
                    "Works effectively within teams"

                ],

                evidenceWeight: 10,

                behaviouralDimensions: {

                    inclusion: 84,
                    control: 62,
                    affection: 86,
                    thinking: 60,
                    flexibility: 72

                }

            },

            entrepreneurialInitiative: {

                description:
                    "Identifies opportunities, develops innovative solutions and takes initiative to create organisational or business value.",

                requiredBecause:
                    "Entrepreneurship Development, SME Management, E-Commerce, Marketing and Strategic Management repeatedly encourage opportunity recognition and innovation.",

                observableBehaviours: [

                    "Identifies opportunities",
                    "Suggests new ideas",
                    "Initiates improvements",
                    "Thinks beyond existing practices",
                    "Acts proactively"

                ],

                assessmentIndicators: [

                    "Recognises opportunities",
                    "Generates practical ideas",
                    "Dembraces innovation",
                    "Shows initiative",
                    "Balances innovation with feasibility"

                ],

                evidenceWeight: 8,

                behaviouralDimensions: {

                    inclusion: 64,
                    control: 68,
                    affection: 50,
                    thinking: 82,
                    flexibility: 90

                }

            },

            adaptabilityAndContinuousImprovement: {

                description:
                    "Responds effectively to organisational change while continuously improving processes, performance and organisational effectiveness.",

                requiredBecause:
                    "Quality Management, Environmental Management, Supply Chain Management, Information Systems and Risk Management all require adaptation and continuous organisational improvement.",

                observableBehaviours: [

                    "Responds positively to change",
                    "Adjusts plans when necessary",
                    "Learns from feedback",
                    "Improves existing processes",
                    "Adapts to changing organisational demands"

                ],

                assessmentIndicators: [

                    "Demonstrates learning agility",
                    "Responds constructively to change",
                    "Improves performance continuously",
                    "Maintains effectiveness during uncertainty",
                    "Applies feedback productively"

                ],

                evidenceWeight: 8,

                behaviouralDimensions: {

                    inclusion: 56,
                    control: 58,
                    affection: 48,
                    thinking: 76,
                    flexibility: 94

                }

            }

        }

    },

    programmeBehaviouralModel: {

        inclusion: {

            score: 66,

            derivedFrom: [

                "Organisational Leadership",
                "Professional Communication",
                "Collaboration & People Management",
                "Ethical Responsibility",
                "Entrepreneurial Initiative"

            ]

        },

        control: {

            score: 75,

            derivedFrom: [

                "Strategic Decision Making",
                "Organisational Leadership",
                "Organisational Planning",
                "Ethical Responsibility"

            ]

        },

        affection: {

            score: 52,

            derivedFrom: [

                "Professional Communication",
                "Collaboration & People Management",
                "Ethical Responsibility"

            ]

        },

        thinking: {

            score: 84,

            derivedFrom: [

                "Strategic Decision Making",
                "Business Analysis",
                "Research & Inquiry",
                "Organisational Planning",
                "Entrepreneurial Initiative"

            ]

        },

        flexibility: {

            score: 72,

            derivedFrom: [

                "Adaptability & Continuous Improvement",
                "Entrepreneurial Initiative",
                "Research & Inquiry",
                "Strategic Decision Making"

            ]

        },

        traits: [

            "Strategic thinker",
            "Evidence-based decision maker",
            "Organised planner",
            "Collaborative leader",
            "Business problem solver",
            "Professional communicator",
            "Adaptable manager",
            "Ethically responsible",
            "Analytical evaluator",
            "Continuous improvement mindset"

        ]

    },

    behaviourProfile: {

        inclusion: 68,

        control: 78,

        affection: 52,

        thinking: 84,

        flexibility: 72,

        traits: [

            "strategic",

            "organised",

            "analytical",

            "collaborative",

            "adaptable"

        ]

    },

    fitExplanations: {

        High:
            "Business Administration requires strategic thinking, effective communication, collaboration and evidence-based decision making. Your behavioural profile aligns strongly with these demands, suggesting you are likely to feel comfortable working in dynamic organisational environments.",

        Moderate:
            "Business Administration combines analytical thinking, teamwork, leadership and adaptability across a wide range of business situations. Your behavioural profile aligns well with several of these demands, although some areas may require additional development as you progress through the programme.",

        Low:
            "Business Administration requires strong communication, collaboration, strategic thinking and the ability to adapt to changing organisational situations. Your behavioural profile suggests these may not be your strongest natural preferences, meaning you may need to put in extra effort to develop them and succeed in the programme."

    },

    applicationNarratives: {
        High: {
            title:
                "Why this may suit your learning style",

            message:
                "Your personality appears well-suited to the collaborative and decision-oriented nature of Business Administration. The program rewards adaptability, communication, leadership potential, and the ability to navigate fast-moving environments with confidence."
        },

        Moderate: {
            title:
                "Why this may suit your learning style",

            message:
                "Several aspects of Business Administration may align with your strengths, particularly where communication, coordination, and strategic thinking are involved. However, the highly social and fast-paced nature of business environments may occasionally require adjustment."
        },

        Low: {
            title:
                "Why this may suit your learning style",

            message:
                "Business Administration may sometimes feel overly people-driven or unpredictable if you naturally prefer highly structured, independent, or technically focused environments. Adapting to leadership dynamics and constant collaboration may require conscious growth."
        }
    },

    fitDimensions: {
        analytical: 72,
        social: 90,
        structure: 70,
        creativity: 68,
        emotionalDemand: 66,
        adaptability: 88
    },

    environmentTraits: {

        structureLevel: 70,

        collaborationLevel: 88,

        ambiguityLevel: 72,

        technicalIntensity: 48,

        creativityDemand: 62,

        emotionalDemand: 58,

        leadershipDemand: 90,

        communicationIntensity: 88

    },

    semanticTypes: [
        "business_program",
        "leadership_oriented_program",
        "coordination_program",
        "communication_program"
    ],

    learningEnvironment: {

        pace: 78,

        independence: 70,

        practicalLearning: 84,

        teamwork: 88,

        assessmentStructure: 68,

        feedbackFrequency: 72

    },

    skillsToStrengthen: [
        "Strategic Thinking",
        "Public Speaking",
        "Leadership Communication",
        "Data Interpretation",
        "Decision-Making",
        "Project Coordination"
    ],

    explanationBlueprint: {

        programmeIdentity: {

            title: "Business Administration",

            summary:
                "Business Administration focuses on understanding how organisations are planned, managed and improved by combining strategic thinking, leadership, communication and evidence-based decision-making.",

            purpose:
                "The programme prepares students to analyse organisational problems, coordinate people and resources, make informed business decisions and contribute to sustainable organisational performance."

        },

        behaviouralStory: {

            whoThrives: {

                summary:
                    "Students who enjoy solving business problems, organising activities, working with people and making thoughtful decisions often adapt well to Business Administration.",

                keyBehaviours: [

                    "Strategic thinking",
                    "Leadership",
                    "Organisation",
                    "Communication"

                ]

            },

            whyBehaviourMatters: {

                summary:
                    "Business environments constantly require people who can analyse situations, coordinate teams, communicate clearly and make balanced decisions despite changing circumstances.",

                evidenceAreas: [

                    "strategicDecisionMaking",
                    "organisationalLeadership",
                    "businessAnalysis",
                    "professionalCommunication"

                ]

            },

            learningJourney: {

                summary:
                    "Students gradually progress from understanding business concepts to leading projects, analysing organisations, making strategic decisions and solving increasingly complex management problems."

            },

            professionalMindset: {

                summary:
                    "Successful business professionals combine analytical thinking with leadership, adaptability and strong interpersonal skills to guide organisations towards their objectives."

            }

        },

        fitInterpretation: {

            high: {

                message:
                    "Your behavioural profile already reflects many of the habits that Business Administration consistently rewards.",

                coachingFocus: [

                    "Strategic leadership",

                    "Business influence",

                    "Executive decision-making"

                ]

            },

            moderate: {

                message:
                    "Your behavioural profile provides a solid platform for Business Administration while highlighting behaviours that will continue developing as you gain experience managing people, projects and organisational decisions.",

                coachingFocus: [

                    "Leadership confidence",

                    "Decision-making",

                    "Professional communication"

                ]

            },

            low: {

                message:
                    "Some aspects of Business Administration may initially feel less natural because the programme regularly rewards leadership, communication, organisational planning and collaborative decision-making. These behaviours can be developed through practice and experience.",

                coachingFocus: [

                    "Leadership",

                    "Organisation",

                    "Communication",

                    "Adaptability"

                ]

            }

        },

        futureOutlook: {

            summary:
                "Graduates who strengthen these behaviours become increasingly prepared to lead teams, solve organisational problems and contribute to business growth across public and private organisations.",

            careerMindset:
                "Business professionals are expected to combine sound judgement, effective leadership and continuous learning when responding to organisational challenges."

        }

    },

    behaviouralNarrative: {

        High({ applicantInterpretation }) {

            return `
            Your assessment suggests that many of your natural behavioural tendencies already align well with the dynamic and people-oriented nature of Business Administration. You appear comfortable analysing situations before making decisions, organising work systematically and balancing independent thinking with collaboration.

            These behaviours provide an excellent foundation for a programme that requires students to coordinate projects, evaluate business problems, communicate ideas professionally and make evidence-based managerial decisions. Throughout your studies you will regularly work with teams, analyse organisational challenges and integrate knowledge from finance, marketing, management and economics to solve practical business problems.

            As your knowledge develops, you will increasingly strengthen your leadership abilities, strategic thinking and professional judgement while learning to manage uncertainty within changing organisational environments. Continuing to build these behaviours will prepare you for leadership responsibilities across a wide range of industries.

            Overall, your behavioural profile already reflects many of the habits that Business Administration is designed to develop further.
            `;
        },

        Moderate: {

            opening:
            "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Business Administration, although some behaviours will continue developing as organisational challenges become more complex.",

            strengths:
            "You already demonstrate behaviours that support analytical thinking, organisation and professional communication, all of which contribute positively throughout the programme.",

            programmeConnection:
            "Business Administration regularly requires students to analyse business situations, work effectively with others, coordinate projects and make informed managerial decisions. While many of these behaviours already appear within your profile, leadership confidence, adaptability and organisational coordination typically strengthen through practical experience and continued learning.",

            coaching:
            "Focus on developing confidence when leading teams, communicating decisions, organising multiple priorities and adapting to changing business situations.",

            conclusion:
            "With deliberate practice and continued development, your behavioural profile provides a strong platform for succeeding within modern organisational environments."

        },

        Low: {

            opening:
            "Your assessment suggests that some of the behaviours most consistently rewarded throughout Business Administration may not yet be among your strongest natural preferences.",

            strengths:
            "This does not prevent success, but it means you may need to approach leadership, collaboration and organisational decision-making more deliberately than students whose behavioural tendencies already align closely with the programme.",

            programmeConnection:
            "Business Administration regularly rewards strategic thinking, effective communication, adaptability, leadership and coordinated teamwork. These behaviours become increasingly important as students progress from learning business concepts to managing real organisational problems.",

            coaching:
            "Deliberately strengthening leadership confidence, communication, organisational planning and adaptability will make both the academic programme and future professional environments progressively more manageable.",

            conclusion:
            "Business capability develops through learning and experience. Students who intentionally strengthen these behaviours often become effective managers, leaders and organisational problem-solvers."

        }
    },
},

    "BSc Business Economics": {

    programmeIntelligence: {

        description:
            "The BSc Business Economics programme develops students' ability to analyse economic behaviour, interpret quantitative evidence, evaluate business and public policy decisions, and apply economic principles to organisational and national development challenges. The curriculum combines economic theory, business management, quantitative methods and research to prepare graduates for analytical decision-making in business, government and international environments.",

        knowledgeAreas: [
            "Microeconomics",
            "Macroeconomics",
            "Business Economics",
            "Managerial Decision Making",
            "Business Finance",
            "Corporate Finance",
            "Investment and Portfolio Management",
            "Public Finance",
            "Monetary Economics",
            "Development Economics",
            "International Business",
            "International Trade and Finance",
            "Econometrics",
            "Mathematics for Economists",
            "Business Mathematics",
            "Business Statistics",
            "Statistics for Managers",
            "Research Methods",
            "Business Policy and Strategy",
            "Operations Management",
            "Entrepreneurship",
            "Business Ethics",
            "Information Systems",
            "Business Law",
            "Company and Partnership Law",
            "Quality Management",
            "Economy of Ghana"
        ],

        learningEnvironment: [
            "Economics-focused analytical learning",
            "Business decision-making",
            "Quantitative problem solving",
            "Research-driven investigation",
            "Policy analysis",
            "Case-based discussion",
            "Technology-supported data analysis",
            "Independent study",
            "Collaborative learning",
            "Professional workplace exposure through internship"
        ],

        learningActivities: [
            "Analysing economic behaviour",
            "Applying mathematical models",
            "Interpreting statistical evidence",
            "Using econometric techniques",
            "Evaluating business cases",
            "Conducting research projects",
            "Writing analytical reports",
            "Presenting research findings",
            "Analysing public and business policies",
            "Applying finance to decision making",
            "Using statistical software",
            "Participating in discussions and seminars",
            "Completing internship activities"
        ],

        teachingMethods: [
            "Lectures",
            "Tutorials",
            "Case studies",
            "Research supervision",
            "Seminars",
            "Group discussions",
            "Software demonstrations",
            "Practical quantitative exercises",
            "Independent research",
            "Project-based learning",
            "Industry internship"
        ],

        assessmentMethods: [
            "Written examinations",
            "Quantitative problem-solving exercises",
            "Statistical analysis assignments",
            "Research projects",
            "Project work",
            "Case study analyses",
            "Presentations",
            "Coursework",
            "Class discussions",
            "Internship assessment"
        ],

        programmeDemands: [
            "Interpret economic theories accurately",
            "Apply mathematical and statistical methods to business problems",
            "Analyse quantitative evidence objectively",
            "Evaluate organisational and public policy decisions",
            "Conduct structured research",
            "Communicate analytical findings clearly",
            "Integrate economic theory with business practice",
            "Support decision-making using evidence",
            "Work independently on complex analytical tasks",
            "Apply analytical reasoning to real-world economic issues"
        ],

        professionalCompetencies: [
            "Economic analysis",
            "Quantitative analysis",
            "Business decision analysis",
            "Econometric application",
            "Research competence",
            "Statistical interpretation",
            "Policy evaluation",
            "Financial analysis",
            "Strategic analysis",
            "Professional communication",
            "Evidence-based problem solving",
            "Analytical report writing"
        ],

        graduateExpectations: [
            "Analyse economic and business problems using appropriate economic models.",
            "Interpret quantitative evidence to support business and policy decisions.",
            "Conduct independent research using recognised research methodologies.",
            "Evaluate economic policies affecting organisations and national development.",
            "Communicate analytical findings through professional reports and presentations.",
            "Apply economic reasoning within business, financial and public sector environments.",
            "Use statistical and econometric tools in solving practical problems.",
            "Demonstrate professional judgement grounded in evidence and ethical reasoning."
        ],

        careerPathways: [
            "Economist",
            "Business Economist",
            "Economic Analyst",
            "Research Analyst",
            "Policy Analyst",
            "Financial Analyst",
            "Investment Analyst",
            "Business Consultant",
            "Development Planning Officer",
            "Banking Professional",
            "Corporate Strategy Analyst",
            "Public Sector Analyst",
            "International Development Officer"
        ],

        workEnvironment: [
            "Financial institutions",
            "Corporate organisations",
            "Government ministries and agencies",
            "Research institutions",
            "Economic policy organisations",
            "Consulting firms",
            "International organisations",
            "Development agencies",
            "Investment firms",
            "Business advisory organisations"
        ],

        // UI Summary Fields

        environment:
            "An analytical, quantitative and research-oriented programme where students investigate economic behaviour, evaluate business decisions and apply evidence to organisational and public policy challenges.",

        workStyle:
            "Working with data, economic models, research evidence and business information to analyse problems, evaluate alternatives and develop evidence-based recommendations.",

        demands:
            "Requires strong analytical reasoning, quantitative problem solving, disciplined research, objective evaluation, effective written communication and sustained independent learning.",

        realityCheck: {

            workload: "High",

            mathematics: "High",

            reading: "High",

            writing: "High",

            teamwork: "Moderate",

            presentations: "Moderate"

        }

    },

    behaviouralRequirements: {

        evidenceAreas: {

            quantitativeAnalysis: {

                description:
                    "Applies mathematical, statistical and econometric techniques to analyse economic and business problems objectively.",

                requiredBecause:
                    "The curriculum repeatedly requires mathematical modelling, statistical analysis, econometrics and quantitative problem solving across economics and finance courses.",

                observableBehaviours: [
                    "Interprets numerical evidence accurately",
                    "Builds mathematical models",
                    "Analyses statistical outputs",
                    "Uses quantitative methods to solve business problems",
                    "Draws evidence-based conclusions from data"
                ],

                assessmentIndicators: [
                    "Mathematical problem solving",
                    "Statistical assignments",
                    "Econometric analysis",
                    "Quantitative examinations",
                    "Data interpretation exercises"
                ],

                evidenceWeight: 0.18,

                behaviouralDimensions: {
                    inclusion: 0.05,
                    control: 0.20,
                    affection: 0.00,
                    thinking: 0.65,
                    flexibility: 0.10
                }

            },

            economicReasoning: {

                description:
                    "Applies economic theories to explain business behaviour, market outcomes and policy decisions.",

                requiredBecause:
                    "Economic reasoning is developed progressively through microeconomics, macroeconomics, monetary economics, development economics and related subjects.",

                observableBehaviours: [
                    "Evaluates economic relationships",
                    "Explains market behaviour",
                    "Interprets policy impacts",
                    "Applies theoretical models",
                    "Uses evidence to justify conclusions"
                ],

                assessmentIndicators: [
                    "Theory-based examinations",
                    "Economic analysis assignments",
                    "Policy evaluation",
                    "Applied economic case studies"
                ],

                evidenceWeight: 0.15,

                behaviouralDimensions: {
                    inclusion: 0.05,
                    control: 0.15,
                    affection: 0.00,
                    thinking: 0.70,
                    flexibility: 0.10
                }

            },

            researchInvestigation: {

                description:
                    "Investigates economic and business problems through structured research and evidence collection.",

                requiredBecause:
                    "Research Methods and Project Work require students to formulate questions, gather evidence, analyse findings and present defensible conclusions.",

                observableBehaviours: [
                    "Plans investigations",
                    "Collects reliable evidence",
                    "Evaluates research findings",
                    "Synthesises information",
                    "Produces analytical reports"
                ],

                assessmentIndicators: [
                    "Research proposals",
                    "Project work",
                    "Research reports",
                    "Literature reviews",
                    "Research presentations"
                ],

                evidenceWeight: 0.15,

                behaviouralDimensions: {
                    inclusion: 0.05,
                    control: 0.25,
                    affection: 0.00,
                    thinking: 0.55,
                    flexibility: 0.15
                }

            },

            evidenceBasedDecisionMaking: {

                description:
                    "Evaluates alternatives and supports business decisions using quantitative and economic evidence.",

                requiredBecause:
                    "Finance, investment, strategy and managerial courses consistently require analytical decision making supported by evidence.",

                observableBehaviours: [
                    "Evaluates alternatives objectively",
                    "Balances risks and outcomes",
                    "Supports recommendations with evidence",
                    "Applies analytical judgement",
                    "Makes structured decisions"
                ],

                assessmentIndicators: [
                    "Business case analysis",
                    "Finance assignments",
                    "Investment analysis",
                    "Strategic decision exercises"
                ],

                evidenceWeight: 0.14,

                behaviouralDimensions: {
                    inclusion: 0.05,
                    control: 0.30,
                    affection: 0.00,
                    thinking: 0.50,
                    flexibility: 0.15
                }

            },

            strategicBusinessAnalysis: {

                description:
                    "Examines organisational problems from operational, financial and strategic perspectives.",

                requiredBecause:
                    "Business Policy, Operations Management, International Business and Corporate Finance require integrated business analysis.",

                observableBehaviours: [
                    "Evaluates organisational performance",
                    "Integrates multiple business factors",
                    "Identifies strategic opportunities",
                    "Assesses business risks",
                    "Develops practical recommendations"
                ],

                assessmentIndicators: [
                    "Case studies",
                    "Strategic analysis",
                    "Business reports",
                    "Scenario evaluation"
                ],

                evidenceWeight: 0.12,

                behaviouralDimensions: {
                    inclusion: 0.10,
                    control: 0.25,
                    affection: 0.05,
                    thinking: 0.45,
                    flexibility: 0.15
                }

            },

            professionalCommunication: {

                description:
                    "Communicates analytical ideas clearly through professional writing, presentations and discussion.",

                requiredBecause:
                    "Business Communication, Scholarly Writing, research reporting and presentations are recurring components throughout the programme.",

                observableBehaviours: [
                    "Writes structured reports",
                    "Explains analytical findings",
                    "Delivers presentations",
                    "Participates in discussions",
                    "Communicates professionally"
                ],

                assessmentIndicators: [
                    "Reports",
                    "Presentations",
                    "Written assignments",
                    "Research documentation"
                ],

                evidenceWeight: 0.10,

                behaviouralDimensions: {
                    inclusion: 0.35,
                    control: 0.15,
                    affection: 0.20,
                    thinking: 0.15,
                    flexibility: 0.15
                }

            },

            professionalCollaboration: {

                description:
                    "Works effectively with others during discussions, projects and workplace learning.",

                requiredBecause:
                    "Group discussions, collaborative learning activities and internship require productive interaction with others.",

                observableBehaviours: [
                    "Contributes to group work",
                    "Shares information",
                    "Supports team objectives",
                    "Engages professionally",
                    "Collaborates respectfully"
                ],

                assessmentIndicators: [
                    "Group assignments",
                    "Seminars",
                    "Internship",
                    "Collaborative projects"
                ],

                evidenceWeight: 0.08,

                behaviouralDimensions: {
                    inclusion: 0.35,
                    control: 0.10,
                    affection: 0.35,
                    thinking: 0.05,
                    flexibility: 0.15
                }

            },

            professionalJudgement: {

                description:
                    "Applies ethical, legal and professional standards when analysing business and economic issues.",

                requiredBecause:
                    "Business Ethics, Business Law and Company Law require responsible professional judgement.",

                observableBehaviours: [
                    "Applies ethical reasoning",
                    "Recognises legal implications",
                    "Exercises professional responsibility",
                    "Balances competing considerations"
                ],

                assessmentIndicators: [
                    "Ethics case studies",
                    "Legal analysis",
                    "Professional judgement exercises"
                ],

                evidenceWeight: 0.08,

                behaviouralDimensions: {
                    inclusion: 0.05,
                    control: 0.35,
                    affection: 0.15,
                    thinking: 0.30,
                    flexibility: 0.15
                }

            }

        }

    },    

    programmeBehaviouralModel: {

        inclusion: {

            score: 52,

            derivedFrom: [
                "Professional Communication",
                "Professional Collaboration"
            ]

        },

        control: {

            score: 84,

            derivedFrom: [
                "Research Investigation",
                "Evidence-Based Decision Making",
                "Strategic Business Analysis",
                "Professional Judgement"
            ]

        },

        affection: {

            score: 42,

            derivedFrom: [
                "Professional Communication",
                "Professional Collaboration"
            ]

        },

        thinking: {

            score: 95,

            derivedFrom: [
                "Quantitative Analysis",
                "Economic Reasoning",
                "Research Investigation",
                "Evidence-Based Decision Making",
                "Strategic Business Analysis",
                "Professional Judgement"
            ]

        },

        flexibility: {

            score: 63,

            derivedFrom: [
                "Economic Reasoning",
                "Strategic Business Analysis",
                "Professional Collaboration"
            ]

        },

        traits: [

            "Analytical",

            "Evidence-Oriented",

            "Economically Rational",

            "Structured Decision Maker",

            "Research Driven",

            "Critical Thinker",

            "Quantitatively Confident",

            "Objective",

            "Strategic",

            "Methodical"

        ]

    },

    behaviourProfile: {

        inclusion: 55,

        control: 84,

        affection: 42,

        thinking: 95,

        flexibility: 63,

        traits: [

            "analytical",

            "evidence-driven",

            "methodical",

            "strategic",

            "objective"

        ]

    }, 

    fitExplanations: {
        High:
            "Business Economics requires analytical reasoning, quantitative problem solving, structured analysis and evidence-based decision making. Your behavioural profile aligns strongly with these demands, suggesting you are likely to thrive in the programme's analytical and research-oriented learning environment.",

        Moderate:
            "Business Economics combines analytical reasoning, quantitative problem solving, business strategy and evidence-based decision making. Your behavioural profile aligns well with several of these demands, although some areas may require additional development as you progress through the programme.",
        
        Low:
            "Business Economics demands strong analytical thinking, structured problem solving and evidence-based decision making. Your behavioural style may not naturally align with these demands, so you may need to invest extra effort in developing these skills to thrive in the programme."
    },

        applicationNarratives: {
            High: {
                title:
                    "Why this may suit your learning style",

                message:
                    "Your behavioural profile appears well aligned with the strategic and analytical nature of Business Economics. The programme rewards logical reasoning, business awareness, structured decision-making, and the ability to interpret economic information to solve real business problems."
            },

            Moderate: {
                title:
                    "Why this may suit your learning style",

                message:
                    "Many aspects of Business Economics align well with your behavioural strengths, particularly where analytical thinking, structured decision making and business strategy are required. Some areas may require additional development as you become more comfortable applying quantitative evidence to complex business and economic problems.",
            },

            Low: {
                title:
                    "Why this may suit your learning style",

                message:
                    "Business Economics may feel highly analytical or conceptually demanding if you naturally prefer practical execution, highly creative work, or people-centered environments. Developing confidence with structured analysis and economic reasoning will be important."
            }
        },

        fitDimensions: {
            analytical: 92,
            social: 64,
            structure: 88,
            creativity: 56,
            emotionalDemand: 38,
            adaptability: 70
        },

        environmentTraits: {

            structureLevel: 86,

            collaborationLevel: 58,

            ambiguityLevel: 62,

            technicalIntensity: 68,

            creativityDemand: 45,

            emotionalDemand: 30,

            leadershipDemand: 60,

            communicationIntensity: 58

        },

        semanticTypes: [
            "business_program",
            "analytical_program",
            "strategy_oriented_program",
            "systems_oriented_program"
        ],

        learningEnvironment: {

            pace: 72,

            independence: 75,

            practicalLearning: 60,

            teamwork: 58,

            assessmentStructure: 88,

            feedbackFrequency: 55

        },

        skillsToStrengthen: [
            "Economic Analysis",
            "Business Strategy",
            "Critical Thinking",
            "Data Interpretation",
            "Decision Making"
        ],

        explanationBlueprint: {

            programmeIdentity: {

                title: "Business Economics",

                summary:
                    "Business Economics combines economic reasoning, business strategy and quantitative analysis to understand how organisations, markets and economies behave and to support evidence-based decision-making.",

                purpose:
                    "The programme prepares students to investigate economic and business problems, interpret quantitative evidence and recommend practical solutions for organisations, governments and society."

            },

            behaviouralStory: {

                whoThrives: {

                    summary:
                        "Students who enjoy analysing complex problems, working with evidence, interpreting data and understanding why decisions produce different economic outcomes often thrive in Business Economics.",

                    keyBehaviours: [

                        "Analytical reasoning",

                        "Evidence-based thinking",

                        "Research",

                        "Objective decision-making"

                    ]

                },

                whyBehaviourMatters: {

                    summary:
                        "Business Economics requires students to move beyond opinions by investigating problems systematically, evaluating evidence objectively and using economic principles to explain business and policy decisions.",

                    evidenceAreas: [

                        "quantitativeAnalysis",

                        "economicReasoning",

                        "researchInvestigation",

                        "evidenceBasedDecisionMaking"

                    ]

                },

                learningJourney: {

                    summary:
                        "Students gradually progress from learning economic theory and quantitative methods to analysing business performance, evaluating policy decisions and solving increasingly complex economic problems."

                },

                professionalMindset: {

                    summary:
                        "Successful Business Economists remain curious, analytical and objective, combining economic reasoning with quantitative evidence to support sound organisational and public policy decisions."

                }

            },

            fitInterpretation: {

                high: {

                    message:
                        "Your behavioural profile already reflects many of the analytical habits that Business Economics consistently rewards.",

                    coachingFocus: [

                        "Advanced analysis",

                        "Economic judgement",

                        "Strategic reasoning"

                    ]

                },

                moderate: {

                    message:
                        "Your behavioural profile provides a solid analytical foundation while highlighting behaviours that will continue developing through research, quantitative analysis and economic reasoning.",

                    coachingFocus: [

                        "Research confidence",

                        "Analytical precision",

                        "Economic reasoning"

                    ]

                },

                low: {

                    message:
                        "Some of the analytical behaviours consistently rewarded by Business Economics may not yet be among your strongest natural preferences. These habits develop progressively through deliberate practice and structured learning.",

                    coachingFocus: [

                        "Analytical thinking",

                        "Evidence evaluation",

                        "Structured reasoning"

                    ]

                }

            },

            futureOutlook: {

                summary:
                    "Graduates who strengthen these behaviours become increasingly prepared to analyse complex economic issues, advise organisations and contribute to evidence-based business and public policy decisions.",

                careerMindset:
                    "Business Economists are expected to remain objective, analytical and evidence-driven when solving organisational and societal challenges."

            }

        },

        behaviouralNarrative: {

            High({ applicantInterpretation }) {

                return `
        Your assessment suggests that many of your natural behavioural tendencies already align well with the analytical and evidence-driven nature of Business Economics. You appear comfortable examining information carefully, approaching problems logically and making decisions after evaluating available evidence rather than relying on assumptions.

        These behaviours provide a strong foundation for a programme that regularly requires students to interpret economic data, analyse business performance and evaluate complex organisational and policy decisions. Throughout your studies you will investigate why markets behave differently, apply quantitative methods to real business problems and use research evidence to support practical recommendations.

        As your knowledge develops, you will increasingly strengthen your ability to integrate economic theory with business strategy, evaluate uncertainty objectively and communicate analytical conclusions professionally. Continuing to develop these behaviours will prepare you for careers where sound judgement and evidence-based reasoning are highly valued.

        Overall, your behavioural profile already reflects many of the habits that Business Economics is intentionally designed to strengthen.
        `;
            },

            Moderate: {

                opening:
                    "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Business Economics, although some analytical behaviours will continue developing as the programme becomes increasingly quantitative and research-oriented.",

                strengths:
                    "You already demonstrate behaviours that support logical reasoning, structured analysis and objective decision-making, all of which contribute positively throughout the programme.",

                programmeConnection:
                    "Business Economics requires students to investigate economic problems, evaluate quantitative evidence and integrate theory with practical business decision-making. While many of these behaviours already appear within your profile, analytical precision, research confidence and economic judgement typically strengthen through sustained practice and academic experience.",

                coaching:
                    "Focus on strengthening quantitative confidence, evidence evaluation, analytical writing and the ability to explain complex economic ideas using clear reasoning.",

                conclusion:
                    "With continued development and deliberate practice, your behavioural profile provides a strong platform for succeeding within Business Economics."

            },

            Low: {

                opening:
                    "Your assessment suggests that some of the behaviours most consistently rewarded throughout Business Economics may not yet be among your strongest natural preferences.",

                strengths:
                    "This does not prevent success, but it means you may need to approach quantitative reasoning, structured analysis and evidence-based decision-making more deliberately than students whose natural behavioural tendencies already align closely with the programme.",

                programmeConnection:
                    "Business Economics consistently rewards students who investigate problems systematically, evaluate evidence objectively and support conclusions with economic reasoning. These analytical behaviours become increasingly important as students progress into econometrics, finance, policy evaluation and strategic business analysis.",

                coaching:
                    "Deliberately strengthening analytical reasoning, quantitative confidence, research skills and evidence-based decision-making will make both the academic programme and future professional environments progressively more manageable.",

                conclusion:
                    "Economic reasoning develops through learning and experience. Students who intentionally build these analytical habits often become highly effective business analysts, economists and policy professionals."

            }

        },
    },

    "BSc Agribusiness and Finance": {
        behaviourProfile: {
            inclusion: 58,
            control: 86,
            affection: 56,
            thinking: 90,
            flexibility: 64,
            traits: [
                "strategic",
                "entrepreneurial",
                "analytical"
            ]
        },
        category: "Agribusiness & Finance",

        description:
            "Combines business management, finance, and agricultural enterprise to prepare students for strategic decision-making across the agricultural value chain.",

        environment:
            "Business-oriented environments focused on agricultural enterprises, investment, value chain management, sustainability, and financial planning.",

        workStyle:
            "Requires analytical thinking, business planning, strategic decision-making, and balancing commercial opportunities with industry realities.",

        demands:
            "Strong business reasoning, financial awareness, strategic planning, problem solving, and adaptability to changing markets.",

        idealTraits: [
            "analytical",
            "strategic",
            "entrepreneurial",
            "adaptable"
        ],

        fitExplanations: {
            High:
                "Your personality aligns well with the strategic and analytical nature of Agribusiness and Finance. You are likely to enjoy solving business challenges while creating sustainable commercial opportunities.",

            Moderate:
                "Many aspects of Agribusiness and Finance suit your strengths, although balancing business strategy with industry-specific knowledge may require additional effort.",

            Low:
                "Agribusiness and Finance may feel demanding if you naturally prefer highly creative, unstructured, or purely people-centered environments."
        },

        applicationNarratives: {
            High: {
                title:
                    "Why this may suit your learning style",

                message:
                    "Your behavioural strengths align with the programme's emphasis on business strategy, financial planning, analytical thinking, and entrepreneurial decision-making."
            },

            Moderate: {
                title:
                    "Why this may suit your learning style",

                message:
                    "You may enjoy the business and finance aspects of the programme, although developing confidence in strategic analysis and commercial planning will strengthen your performance."
            },

            Low: {
                title:
                    "Why this may suit your learning style",

                message:
                    "The programme's emphasis on structured business analysis and financial reasoning may require significant adjustment if you naturally prefer less analytical environments."
            }
        },

        fitDimensions: {
            analytical: 88,
            social: 62,
            structure: 82,
            creativity: 60,
            emotionalDemand: 40,
            adaptability: 74
        },

        environmentTraits: {
            structureLevel: 82,
            collaborationLevel: 64,
            ambiguityLevel: 46,
            technicalIntensity: 86,
            creativityDemand: 58,
            emotionalDemand: 38,
            leadershipDemand: 60,
            communicationIntensity: 66
        },

        semanticTypes: [
            "business_program",
            "finance_program",
            "entrepreneurship_program",
            "strategy_oriented_program"
        ],

        learningEnvironment: {
            pace: 74,
            independence: 70,
            practicalLearning: 86,
            teamwork: 68,
            assessmentStructure: 78,
            feedbackFrequency: 66
        },

        careerPaths: [
            "Agribusiness Manager",
            "Agricultural Finance Officer",
            "Business Development Officer",
            "Supply Chain Manager",
            "Investment Analyst"
        ],

        realityCheck: {
            workload:
                "Moderate to heavy workload involving business planning, financial analysis, market evaluation, and industry research.",

            environment:
                "Commercial environments requiring strategic thinking, financial awareness, and entrepreneurial problem solving.",

            pressureLevel:
                "Moderate due to decision-making responsibilities and business performance expectations.",

            keyChallenge:
                "Balancing financial performance, strategic planning, and the realities of agricultural industries."
        },

        skillsToStrengthen: [
            "Strategic Thinking",
            "Business Planning",
            "Financial Analysis",
            "Decision Making",
            "Market Analysis"
        ],
    },

    "BSc Applied Statistics": {
        behaviourProfile: {
            inclusion: 36,
            control: 94,
            affection: 30,
            thinking: 98,
            flexibility: 58,
            traits: [
                "analytical",
                "logical",
                "detail-oriented",
                "methodical"
            ]
        },

        category: "Statistics & Data Analysis",

        description:
            "Applies statistical theory, probability, and quantitative analysis to solve practical problems across business, healthcare, finance, government, and scientific research.",

        environment:
            "Highly analytical environments involving statistical modelling, survey design, forecasting, experimental analysis, and evidence-based decision making.",

        workStyle:
            "Requires precision, logical reasoning, mathematical thinking, attention to detail, and structured analytical problem-solving.",

        demands:
            "Strong quantitative reasoning, statistical modelling, data interpretation, critical thinking, and communicating analytical findings clearly.",

        idealTraits: [
            "analytical",
            "logical",
            "detail-oriented",
            "methodical",
            "patient"
        ],

        personalityFit: {
            strengths: [
                "Quantitative reasoning",
                "Pattern recognition",
                "Analytical problem solving",
                "Evidence-based decision making"
            ],

            watchOutFor: [
                "Complex mathematical concepts",
                "Interpreting large datasets",
                "Explaining technical results to non-technical audiences"
            ]
        },

        fitDimensions: {
            analytical: 97,
            social: 50,
            structure: 94,
            creativity: 44,
            emotionalDemand: 34,
            adaptability: 60
        },

        environmentTraits: {
            structureLevel: 92,
            collaborationLevel: 52,
            ambiguityLevel: 24,
            technicalIntensity: 96,
            creativityDemand: 38,
            emotionalDemand: 28,
            leadershipDemand: 30,
            communicationIntensity: 54
        },

        semanticTypes: [
            "statistics_program",
            "research_heavy_program",
            "quantitative_program",
            "analytics_program"
        ],

        learningEnvironment: {
            pace: 78,
            independence: 88,
            practicalLearning: 72,
            teamwork: 52,
            assessmentStructure: 94,
            feedbackFrequency: 62
        },

        careerPaths: [
            "Statistician",
            "Data Analyst",
            "Research Analyst",
            "Biostatistician",
            "Risk Analyst"
        ],

        realityCheck: {
            workload:
                "High quantitative workload involving statistical modelling, data analysis, mathematical reasoning, and interpreting research findings.",

            environment:
                "Analytical environments where evidence, numerical accuracy, and statistical validity drive decision making.",

            pressureLevel:
                "Moderate due to analytical accuracy requirements, project deadlines, and the importance of reliable statistical conclusions.",

            keyChallenge:
                "Applying complex statistical methods correctly while communicating meaningful insights to decision makers."
        },

        skillsToStrengthen: [
            "Statistical Modelling",
            "Probability",
            "Data Analysis",
            "Research Methods",
            "Technical Communication"
        ],
    },

    "BSc Actuarial Science": {

    programmeIntelligence: {

        description:
            "Actuarial Science is a quantitative programme that applies mathematics, statistics, probability, economics and finance to analyse uncertainty, evaluate risk and support financial decision-making. Students develop expertise in mathematical modelling, statistical forecasting, insurance, pensions, investments, financial markets and predictive analytics while learning to solve complex real-world problems through rigorous analytical methods.",

        learningFocus: [
            "Probability Theory",
            "Statistics",
            "Financial Mathematics",
            "Risk Analysis",
            "Insurance",
            "Investments",
            "Mathematical Modelling",
            "Predictive Analytics",
            "Business Finance",
            "Research"
        ],

        learningEnvironment:
            "Students learn in a highly structured and intellectually demanding environment where mathematical reasoning, statistical modelling and analytical problem-solving form the foundation of almost every course. Learning involves lectures, quantitative exercises, computer-based analysis, research projects, internships and independent study requiring sustained concentration and precision.",

        workStyle:
            "Students spend significant time solving quantitative problems, analysing datasets, constructing mathematical models, evaluating financial risks and applying statistical techniques to business and insurance scenarios. Success depends on accuracy, logical thinking, persistence and disciplined independent study.",

        programmeDemands: [
            "Advanced mathematical reasoning",
            "Statistical analysis",
            "Logical problem-solving",
            "Critical thinking",
            "Data interpretation",
            "Financial modelling",
            "Research capability",
            "Analytical precision",
            "Attention to detail",
            "Decision-making under uncertainty"
        ],

        careerOutlook: [
            "Actuary",
            "Risk Analyst",
            "Insurance Analyst",
            "Investment Analyst",
            "Financial Risk Manager",
            "Pension Consultant",
            "Data Analyst",
            "Business Analyst",
            "Quantitative Analyst",
            "Enterprise Risk Consultant"
        ],

        careerPathways: [
            "Actuarial Consulting",
            "Insurance",
            "Pensions",
            "Investment Management",
            "Banking",
            "Risk Management",
            "Financial Analytics",
            "Business Intelligence",
            "Data Science",
            "Corporate Finance",
            "Government Financial Institutions",
            "Research"
        ],

        typicalEmployers: [
            "Insurance Companies",
            "Commercial Banks",
            "Investment Firms",
            "Pension Fund Administrators",
            "Consulting Firms",
            "Government Regulatory Agencies",
            "Financial Institutions",
            "Multinational Corporations"
        ],

        programmeCharacteristics: [
            "Quantitative",
            "Analytical",
            "Research-intensive",
            "Finance-oriented",
            "Mathematically rigorous",
            "Evidence-driven",
            "Technically demanding",
            "Professionally regulated"
        ],

        successFactors: [
            "Strong numerical reasoning",
            "Analytical thinking",
            "Persistence",
            "Attention to detail",
            "Mathematical confidence",
            "Problem-solving ability",
            "Self-discipline",
            "Continuous learning"
        ],

        realityCheck:
            "Actuarial Science is significantly more mathematical than many business programmes. Students should expect continuous engagement with advanced statistics, probability, financial mathematics and quantitative modelling throughout the programme. Those who enjoy solving complex analytical problems and working with numbers are more likely to thrive than students seeking predominantly creative or communication-focused learning environments."

    },

    behaviouralRequirements: {

        coreBehaviours: [

            "Analytical reasoning",

            "Logical problem-solving",

            "Mathematical thinking",

            "Attention to detail",

            "Critical evaluation",

            "Structured decision-making",

            "Evidence-based reasoning",

            "Independent learning"

        ],

        cognitiveRequirements: [

            "Interpret complex quantitative information",

            "Apply statistical reasoning",

            "Develop mathematical models",

            "Evaluate uncertainty using data",

            "Recognise patterns and relationships",

            "Solve multi-step analytical problems",

            "Interpret financial and risk information",

            "Think systematically"

        ],

        interpersonalRequirements: [

            "Communicate technical findings clearly",

            "Collaborate during projects",

            "Participate in professional discussions",

            "Present analytical conclusions",

            "Receive and apply constructive feedback"

        ],

        selfManagementRequirements: [

            "Maintain high levels of accuracy",

            "Manage demanding workloads",

            "Demonstrate persistence",

            "Work independently",

            "Manage time effectively",

            "Remain disciplined during complex problem-solving",

            "Continuously improve technical competence"

        ],

        decisionMakingStyle: [

            "Evidence-driven",

            "Data-informed",

            "Logical",

            "Objective",

            "Risk-aware"

        ],

        adaptabilityRequirements: [

            "Apply mathematical knowledge across multiple domains",

            "Learn new quantitative techniques",

            "Adapt to evolving financial environments",

            "Work with unfamiliar datasets",

            "Respond to changing analytical challenges"

        ],

        majorChallenges: [

            "Advanced mathematical modelling",

            "Statistical analysis",

            "Probability theory",

            "Financial risk evaluation",

            "Long analytical problem-solving sessions",

            "Maintaining precision under pressure",

            "Balancing theory with practical application"

        ]

    },

    programmeBehaviouralModel: {

        inclusion: {
            score: 48,
            derivedFrom: [
                "Professional communication",
                "Collaborative project work",
                "Presentation of analytical findings"
            ]
        },

        control: {
            score: 94,
            derivedFrom: [
                "Analytical precision",
                "Structured decision-making",
                "Time management",
                "Independent learning",
                "Accuracy",
                "Persistence",
                "Research discipline"
            ]
        },

        affection: {
            score: 34,
            derivedFrom: [
                "Professional collaboration",
                "Constructive feedback"
            ]
        },

        thinking: {
            score: 100,
            derivedFrom: [
                "Mathematical reasoning",
                "Probability theory",
                "Statistical analysis",
                "Critical evaluation",
                "Logical problem-solving",
                "Evidence-based reasoning",
                "Financial modelling",
                "Risk analysis",
                "Predictive modelling"
            ]
        },

        flexibility: {
            score: 76,
            derivedFrom: [
                "Application across financial domains",
                "Adaptation to changing datasets",
                "Learning new quantitative techniques",
                "Responding to analytical uncertainty"
            ]
        }

    },

    behaviourProfile: {

        inclusion: 48,

        control: 94,

        affection: 34,

        thinking: 98,

        flexibility: 76,

        traits: [

            "quantitative thinker",

            "analytical problem solver",

            "methodical decision maker",

            "risk-focused analyst",

            "evidence-driven evaluator",

            "logical modeller",

            "detail-oriented practitioner",

            "disciplined independent learner"

        ]

    },

    fitDimensions: {

        analytical: 100,

        social: 38,

        structure: 96,

        creativity: 42,

        emotionalDemand: 32,

        adaptability: 74

    },

    semanticTypes: [

        "quantitative_program",

        "actuarial_program",

        "risk_modelling_program",

        "financial_analytics_program",

        "statistical_program",

        "evidence_driven_program",

        "structured_learning_program"

    ],

    environmentTraits: {

        structure: 96,

        collaboration: 42,

        autonomy: 88,

        pace: 76,

        uncertainty: 72,

        leadership: 40

    },

    learningEnvironment: {

        independence: 90,

        collaboration: 44,

        practical: 70,

        theoretical: 94,

        pace: 76

    },

    skillsToStrengthen: [

        "Mathematical Reasoning",

        "Statistical Modelling",

        "Risk Analysis",

        "Evidence-Based Decision Making",

        "Analytical Persistence",

        "Financial Modelling",

        "Research and Investigation",

        "Attention to Detail"

    ],

    explanationBlueprint: {

        programmeIdentity: {

            title: "Actuarial Science",

            summary:
                "Actuarial Science develops the ability to analyse uncertainty, evaluate financial risk and support complex decisions using mathematics, statistics and evidence-based reasoning.",

            purpose:
                "The programme prepares students to construct mathematical models, interpret quantitative information and make informed financial and risk management decisions across insurance, investments, pensions and broader financial systems."

        },

        behaviouralStory: {

            whoThrives: {

                summary:
                    "Students who naturally enjoy solving complex analytical problems, working with numbers, thinking logically and investigating patterns systematically often thrive in Actuarial Science.",

                keyBehaviours: [

                    "Analytical reasoning",

                    "Mathematical thinking",

                    "Structured problem solving",

                    "Evidence-based judgement"

                ]

            },

            whyBehaviourMatters: {

                summary:
                    "Success depends on behaviours that support sustained concentration, analytical precision, disciplined reasoning and the ability to make objective decisions using quantitative evidence rather than intuition.",

                evidenceAreas: [

                    "Analytical reasoning",

                    "Logical problem-solving",

                    "Mathematical thinking",

                    "Evidence-based reasoning"

                ]

            },

            learningJourney: {

                summary:
                    "Students progressively move from understanding mathematical and statistical foundations to building predictive models, analysing financial uncertainty and solving increasingly sophisticated risk management problems."

            },

            professionalMindset: {

                summary:
                    "Successful actuaries combine intellectual discipline, analytical persistence and objective judgement to help organisations understand uncertainty and make financially responsible decisions."

            }

        },

        fitInterpretation: {

            high: {

                message:
                    "Your behavioural profile already reflects many of the analytical habits that Actuarial Science consistently rewards.",

                coachingFocus: [

                    "Advanced quantitative modelling",

                    "Professional actuarial judgement",

                    "Complex risk evaluation"

                ]

            },

            moderate: {

                message:
                    "Your behavioural profile provides a solid analytical foundation while highlighting behaviours that will continue developing through mathematical modelling, statistical analysis and evidence-based decision making.",

                coachingFocus: [

                    "Mathematical confidence",

                    "Analytical persistence",

                    "Risk reasoning"

                ]

            },

            low: {

                message:
                    "Some of the analytical behaviours consistently rewarded throughout Actuarial Science may not yet be among your strongest natural preferences. These behaviours become stronger through disciplined study, structured practice and repeated exposure to quantitative problem-solving.",

                coachingFocus: [

                    "Analytical reasoning",

                    "Quantitative confidence",

                    "Logical problem solving",

                    "Evidence-based analysis"

                ]

            }

        },

        futureOutlook: {

            summary:
                "Graduates who strengthen these behaviours become increasingly prepared to evaluate financial uncertainty, analyse complex risks and contribute to evidence-based decision-making across financial and insurance industries.",

            careerMindset:
                "Actuarial professionals are trusted to interpret complex data objectively, manage uncertainty responsibly and support long-term financial decision-making."

        }

    },

    behaviouralNarrative: {

        High({ applicantInterpretation }) {

            return `
    Your assessment suggests that many of your natural behavioural tendencies already align well with the highly analytical and evidence-driven nature of Actuarial Science. You appear comfortable approaching complex problems logically, working systematically and making decisions based on careful analysis rather than assumption.

    These behaviours provide an excellent foundation for a programme that requires students to analyse uncertainty, construct mathematical models, interpret statistical evidence and evaluate financial risk with precision. Throughout your studies you will regularly solve quantitative problems, investigate complex datasets and apply mathematical reasoning to real-world financial and insurance decisions.

    As your knowledge develops, you will progressively strengthen your ability to model uncertainty, evaluate risk objectively and communicate analytical conclusions that support important financial decisions. Continuing to develop these behaviours will prepare you for professional environments where accuracy, disciplined reasoning and evidence-based judgement are essential.

    Overall, your behavioural profile already reflects many of the habits that Actuarial Science is intentionally designed to strengthen.
    `;

        },

        Moderate: {

            opening:
                "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Actuarial Science, although some analytical behaviours will continue developing as the programme becomes increasingly mathematical and quantitatively demanding.",

            strengths:
                "You already demonstrate behaviours that support logical reasoning, structured thinking and analytical problem-solving, all of which contribute positively throughout the programme.",

            programmeConnection:
                "Actuarial Science requires students to interpret complex quantitative information, construct mathematical models and evaluate financial uncertainty using evidence. While many of these behaviours already appear within your profile, mathematical confidence, analytical precision and persistence typically strengthen through sustained quantitative practice.",

            coaching:
                "Focus on strengthening mathematical reasoning, statistical confidence, analytical persistence and the ability to interpret quantitative evidence objectively.",

            conclusion:
                "With deliberate practice and continued development, your behavioural profile provides a strong platform for succeeding within the intellectually demanding environment of Actuarial Science."

        },

        Low: {

            opening:
                "Your assessment suggests that some of the behaviours most consistently rewarded throughout Actuarial Science may not yet be among your strongest natural preferences.",

            strengths:
                "This does not prevent success, but it means you may need to approach mathematical reasoning, structured analysis and evidence-based problem solving more deliberately than students whose behavioural tendencies already align closely with the programme.",

            programmeConnection:
                "Actuarial Science consistently rewards analytical thinking, disciplined reasoning, quantitative confidence and careful evaluation of uncertainty. These behaviours become increasingly important as students progress into advanced statistical modelling, financial mathematics and risk analysis.",

            coaching:
                "Deliberately strengthening analytical reasoning, mathematical confidence, quantitative problem-solving and disciplined independent study will make both the academic programme and future professional practice progressively more manageable.",

            conclusion:
                "Analytical capability develops through learning and experience. Students who intentionally cultivate these habits often become highly effective actuarial professionals capable of solving complex financial and risk management problems."

        }

    }
},

    "BSc Logistics and Transport Management": {
        behaviourProfile: {
            inclusion: 62,
            control: 90,
            affection: 58,
            thinking: 88,
            flexibility: 76,
            traits: [
                "organized",
                "analytical",
                "strategic",
                "detail-oriented"
            ]
        },

        category: "Logistics & Supply Chain",

        description:
            "Focuses on logistics, transportation systems, supply chain management, procurement, inventory control, and operational efficiency across local and global markets.",

        environment:
            "Fast-paced operational environments involving planning, coordination, logistics networks, warehousing, procurement, transportation, and supply chain optimization.",

        workStyle:
            "Requires organization, structured planning, analytical thinking, decision-making under time constraints, and coordination of multiple moving parts.",

        demands:
            "Strong problem-solving ability, operational planning, quantitative reasoning, attention to detail, and effective coordination of logistics processes.",

        idealTraits: [
            "organized",
            "analytical",
            "strategic",
            "detail-oriented",
            "efficient"
        ],

        fitDimensions: {
            analytical: 84,
            social: 76,
            structure: 90,
            creativity: 52,
            emotionalDemand: 46,
            adaptability: 82
        },

        environmentTraits: {
            structureLevel: 88,
            collaborationLevel: 82,
            ambiguityLevel: 52,
            technicalIntensity: 80,
            creativityDemand: 54,
            emotionalDemand: 46,
            leadershipDemand: 72,
            communicationIntensity: 84
        },

        semanticTypes: [
            "operations_program",
            "business_program",
            "analytical_program",
            "planning_program"
        ],

        learningEnvironment: {
            pace: 84,
            independence: 72,
            practicalLearning: 88,
            teamwork: 84,
            assessmentStructure: 82,
            feedbackFrequency: 74
        },

        careerPaths: [
            "Supply Chain Analyst",
            "Logistics Manager",
            "Procurement Officer",
            "Operations Manager",
            "Transport Planner"
        ],

        realityCheck: {
            workload:
                "Demanding operational workload involving planning, scheduling, coordination, procurement, logistics analysis, and problem-solving.",

            environment:
                "Dynamic business environments requiring constant coordination across suppliers, transport systems, warehouses, and customers.",

            pressureLevel:
                "High due to delivery deadlines, operational disruptions, and resource optimization.",

            keyChallenge:
                "Balancing efficiency, cost, customer satisfaction, and operational reliability under changing conditions."
        },

        skillsToStrengthen: [
            "Supply Chain Analysis",
            "Operational Planning",
            "Decision Making",
            "Data Analysis",
            "Communication"
        ]
    },

    "BSc Banking and Finance": {

    programmeIntelligence: {
        description:
            "The BSc Banking and Finance programme develops graduates who understand banking operations, financial institutions, corporate finance, investment analysis, financial risk management and financial decision-making. Students learn to analyse financial information, evaluate investment opportunities, assess financial risks, manage banking operations and apply financial principles within regulated financial systems.",

        knowledgeAreas: [
            "Banking Operations",
            "Corporate Finance",
            "Business Finance",
            "Financial Risk Management",
            "Investment and Portfolio Management",
            "Financial Accounting",
            "Financial Modelling",
            "Credit Management",
            "Monetary Economics",
            "Public Finance",
            "International Trade Finance",
            "Financial Markets",
            "Bank Management",
            "Microfinance",
            "Financial Institutions",
            "Regulatory Framework for Financial Institutions",
            "Business Policy and Financial Policy",
            "Research Methods",
            "Quantitative Methods",
            "Business Ethics"
        ],

        learningEnvironment: [
            "Highly analytical financial learning",
            "Evidence-based financial decision making",
            "Structured quantitative analysis",
            "Risk evaluation and management",
            "Regulatory and compliance-oriented study",
            "Case-based financial problem solving",
            "Financial modelling and forecasting",
            "Technology-supported financial analysis",
            "Professional banking practice",
            "Independent research and project work"
        ],

        learningActivities: [
            "Financial analysis",
            "Investment evaluation",
            "Risk assessment",
            "Credit analysis",
            "Financial modelling",
            "Quantitative problem solving",
            "Case study analysis",
            "Research projects",
            "Banking simulations",
            "Financial reporting",
            "Policy evaluation",
            "Strategic financial planning",
            "Internship and workplace learning",
            "Project presentations"
        ],

        teachingMethods: [
            "Lectures",
            "Case studies",
            "Financial simulations",
            "Problem-based learning",
            "Research supervision",
            "Practical financial modelling",
            "Technology-assisted learning",
            "Seminars",
            "Industry internship",
            "Project-based learning"
        ],

        assessmentMethods: [
            "Written examinations",
            "Financial case analyses",
            "Quantitative assignments",
            "Research projects",
            "Financial modelling exercises",
            "Presentations",
            "Investment analysis reports",
            "Practical assessments",
            "Internship assessment",
            "Final year project"
        ],

        behaviouralDemands: [
            "Analytical reasoning",
            "Critical financial judgement",
            "Evidence-based decision making",
            "Quantitative problem solving",
            "Attention to financial accuracy",
            "Professional responsibility",
            "Risk awareness",
            "Ethical judgement",
            "Structured thinking",
            "Independent learning",
            "Strategic evaluation",
            "Clear professional communication"
        ],

        graduateExpectations: {

            knowledge:
                "Graduates are expected to demonstrate comprehensive understanding of banking systems, corporate finance, investment management, financial markets, financial regulation, monetary policy, and quantitative financial analysis.",

            professionalSkills:
                "Graduates should be capable of analysing financial information, evaluating investment opportunities, managing financial risks, interpreting financial regulations, supporting banking operations, and making evidence-based financial recommendations.",

            workplaceReadiness:
                "Graduates are expected to transition effectively into banking, financial services, corporate finance, investment, regulatory and consulting environments through professional judgement, ethical practice, analytical capability and practical financial competence.",

            lifelongLearning:
                "Graduates should possess the research capability, adaptability and continuous learning mindset necessary to respond to evolving financial markets, regulatory changes, technological innovation and emerging financial practices."

        },

        careerPathways: [

            "Commercial Banking",

            "Corporate Banking",

            "Investment Banking",

            "Retail Banking",

            "Treasury Management",

            "Financial Analysis",

            "Investment Analysis",

            "Portfolio Management",

            "Credit Analysis",

            "Risk Management",

            "Financial Consulting",

            "Insurance",

            "Microfinance",

            "Financial Regulation",

            "Central Banking",

            "Capital Markets",

            "Asset Management",

            "Corporate Finance",

            "International Finance",

            "Financial Technology (FinTech)"

        ],

        workEnvironment: [

            "Commercial banks",

            "Investment banks",

            "Universal banks",

            "Rural and community banks",

            "Savings and loans companies",

            "Microfinance institutions",

            "Insurance companies",

            "Investment and asset management firms",

            "Corporate finance departments",

            "Financial consulting firms",

            "Central banks",

            "Financial regulatory institutions",

            "Capital market institutions",

            "Government financial agencies",

            "International financial institutions",

            "Financial technology (FinTech) organisations"

        ],

        // =====================================================
        // UI Summary Fields
        // =====================================================    
        environment:
            "A structured, quantitative and professionally regulated learning environment where students analyse financial information, evaluate risk and support evidence-based financial decision-making.",

        workStyle:
            "Working with financial data, investment information, banking systems and quantitative evidence to evaluate risk, solve financial problems and support organisational decision-making.",

        demands:
            "Requires strong analytical reasoning, quantitative problem solving, financial accuracy, disciplined judgement, professional responsibility and the ability to work confidently within regulated financial environments.",
        realityCheck: {

            workload: "High",

            mathematics: "High",

            reading: "High",

            writing: "Moderate to High",

            teamwork: "Moderate",

            presentations: "Moderate"

        },

        careerPreparation: [
            "Commercial Banking",
            "Investment Banking",
            "Corporate Finance",
            "Financial Analysis",
            "Investment Management",
            "Credit Management",
            "Risk Management",
            "Treasury Management",
            "Financial Consulting",
            "Financial Regulation",
            "Microfinance",
            "Public Financial Management"
        ]
    },
    
    behaviouralRequirements: {

        financialAnalysis: {
            importance: "Very High",
            description: "Interpret financial information, evaluate financial performance and draw evidence-based conclusions.",
            evidence: [
                "Corporate Finance",
                "Advanced Corporate Finance",
                "Financial Accounting",
                "Financial Modelling",
                "Business Finance"
            ]
        },

        quantitativeReasoning: {
            importance: "Very High",
            description: "Apply numerical reasoning, mathematical techniques and quantitative models to financial problems.",
            evidence: [
                "Quantitative Methods",
                "Introduction to Business Statistics",
                "Financial Modelling",
                "Investment and Portfolio Management"
            ]
        },

        riskEvaluation: {
            importance: "Very High",
            description: "Identify, assess and manage financial, investment and operational risks before making decisions.",
            evidence: [
                "Financial Risk Management and Insurance",
                "Investment and Portfolio Management",
                "Lending and Credit Management",
                "Banking Operations"
            ]
        },

        decisionMaking: {
            importance: "Very High",
            description: "Evaluate alternatives and make sound financial decisions using evidence, analysis and professional judgement.",
            evidence: [
                "Business Analysis and Financial Policy",
                "Corporate Finance",
                "Business Finance",
                "Managerial Economics"
            ]
        },

        regulatoryCompliance: {
            importance: "High",
            description: "Apply financial regulations, banking laws and compliance requirements in professional practice.",
            evidence: [
                "Regulatory and Legal Framework for Financial Institutions",
                "Legal Environment of Business",
                "Banking Operations"
            ]
        },

        ethicalJudgement: {
            importance: "High",
            description: "Exercise integrity and ethical responsibility when handling financial decisions and stakeholder interests.",
            evidence: [
                "Business Ethics",
                "Principles of Leadership",
                "Regulatory and Legal Framework for Financial Institutions"
            ]
        },

        strategicThinking: {
            importance: "High",
            description: "Consider long-term financial consequences, organisational objectives and market conditions when developing financial strategies.",
            evidence: [
                "Business Policy and Strategy",
                "Business Analysis and Financial Policy",
                "Corporate Finance"
            ]
        },

        researchCapability: {
            importance: "High",
            description: "Investigate financial problems systematically, interpret evidence and communicate findings clearly.",
            evidence: [
                "Research Methods",
                "Project Work",
                "Internship"
            ]
        },

        technologyApplication: {
            importance: "High",
            description: "Use financial technologies and information systems to support analysis, reporting and banking operations.",
            evidence: [
                "Information Systems",
                "Computerized Accounting Information Systems",
                "E-Banking and E-Commerce",
                "Financial Modelling"
            ]
        },

        professionalCommunication: {
            importance: "Moderate",
            description: "Communicate financial information, recommendations and analyses clearly to professional audiences.",
            evidence: [
                "Communication Skills",
                "Business Communication",
                "Project Work",
                "Internship"
            ]
        },

        independentLearning: {
            importance: "High",
            description: "Work independently to analyse financial problems, complete research and develop professional competence.",
            evidence: [
                "Research Methods",
                "Project Work",
                "Internship"
            ]
        }

    },

    programmeBehaviouralModel: {

        inclusion: {
            score: 58,
            derivedFrom: [
                "Business Communication",
                "Principles of Leadership",
                "Business Policy and Strategy",
                "Internship"
            ]
        },

        control: {
            score: 91,
            derivedFrom: [
                "Financial Risk Management",
                "Regulatory and Legal Framework for Financial Institutions",
                "Banking Operations",
                "Financial Accounting",
                "Business Ethics",
                "Credit Management"
            ]
        },

        affection: {
            score: 34,
            derivedFrom: [
                "Business Ethics",
                "Business Communication",
                "Leadership"
            ]
        },

        thinking: {
            score: 95,
            derivedFrom: [
                "Corporate Finance",
                "Advanced Corporate Finance",
                "Financial Modelling",
                "Investment and Portfolio Management",
                "Quantitative Methods",
                "Research Methods",
                "Managerial Economics",
                "Monetary Economics"
            ]
        },

        flexibility: {
            score: 63,
            derivedFrom: [
                "E-Banking and E-Commerce",
                "Financial Markets",
                "Business Analysis and Financial Policy",
                "International Trade Finance",
                "Microfinance",
                "Project Work"
            ]
        }

    },

    behaviourProfile: {

        inclusion: 58,

        control: 91,

        affection: 34,

        thinking: 95,

        flexibility: 63,

        traits: [

            "analytical",

            "disciplined",

            "risk-aware",

            "objective",

            "financially strategic"

        ]

    },

    fitExplanations: {

        High:
            "Banking and Finance requires strong analytical reasoning, disciplined decision-making, financial accuracy and sound professional judgement. Your behavioural profile aligns strongly with these demands, suggesting you are likely to thrive in the programme's structured and financially driven learning environment.",

        Moderate:
            "Banking and Finance combines financial analysis, quantitative reasoning, risk evaluation and professional responsibility. Your behavioural profile aligns well with several of these demands, although some areas may require additional development as you progress through the programme.",

        Low:
            "Banking and Finance requires strong analytical reasoning, disciplined decision-making, risk awareness and confidence working within structured financial systems. Students whose behavioural strengths are less aligned with these demands may need to invest additional effort in developing these capabilities to succeed in the programme."

    },

    applicationNarratives: {

        High: {

            title:
                "Why this may suit your learning style",

            message:
                "Your behavioural profile aligns well with the demands of Banking and Finance. The programme rewards analytical reasoning, disciplined decision-making, financial accuracy and the ability to evaluate complex financial situations using evidence and professional judgement."

        },

        Moderate: {

            title:
                "Why this may suit your learning style",

            message:
                "Your behavioural profile aligns naturally with several aspects of Banking and Finance, particularly where structured analysis and logical decision-making are required. As the programme progresses, developing greater confidence in financial analysis, risk evaluation and working within regulated financial systems will strengthen your ability to thrive."

        },

        Low: {

            title:
                "Why this may suit your learning style",

            message:
                "Banking and Finance places strong emphasis on financial analysis, disciplined decision-making, risk evaluation and working within structured financial systems. If these demands are less aligned with your current behavioural strengths, some aspects of the programme may require more deliberate effort and practice. Developing confidence in analytical reasoning and evidence-based financial decision-making will help you adapt more successfully."

        }

    },

    fitDimensions: {

        analytical: 96,

        social: 60,

        structure: 92,

        creativity: 42,

        emotionalDemand: 38,

        adaptability: 66

    },

    environmentTraits: {

        structureLevel: 90,

        collaborationLevel: 62,

        ambiguityLevel: 58,

        technicalIntensity: 82,

        creativityDemand: 36,

        emotionalDemand: 34,

        leadershipDemand: 66,

        communicationIntensity: 70

    },

    semanticTypes: [

        "financial_program",

        "analytical_program",

        "risk_management_program",

        "decision_intensive_program"

    ],

    learningEnvironment: {

        pace: 82,

        independence: 78,

        practicalLearning: 76,

        teamwork: 60,

        assessmentStructure: 90,

        feedbackFrequency: 56

    },

    skillsToStrengthen: [
        "Analytical reasoning",

        "Evidence-based financial decision making",

        "Quantitative problem solving",

        "Risk evaluation",

        "Working accurately within structured financial processes"

        ],

        explanationBlueprint: {

            programmeIdentity: {

                title: "Banking and Finance",

                summary:
                    "Banking and Finance develops the ability to analyse financial information, evaluate risk and make sound financial decisions within regulated financial systems.",

                purpose:
                    "The programme prepares students to understand banking operations, evaluate investments, manage financial risks and apply professional financial judgement across banking, corporate finance and financial services."

            },

            behaviouralStory: {

                whoThrives: {

                    summary:
                        "Students who enjoy analysing financial information, solving quantitative problems, evaluating risk and making evidence-based decisions often thrive in Banking and Finance.",

                    keyBehaviours: [

                        "Financial reasoning",

                        "Analytical judgement",

                        "Structured decision-making",

                        "Risk awareness"

                    ]

                },

                whyBehaviourMatters: {

                    summary:
                        "Banking and Finance consistently rewards behaviours that support disciplined financial analysis, objective judgement, professional responsibility and evidence-based decision-making within regulated environments.",

                    evidenceAreas: [

                        "financialAnalysis",

                        "quantitativeReasoning",

                        "riskEvaluation",

                        "decisionMaking"

                    ]

                },

                learningJourney: {

                    summary:
                        "Students progressively develop from understanding financial principles to analysing investments, evaluating risk, interpreting financial markets and making increasingly sophisticated financial decisions."

                },

                professionalMindset: {

                    summary:
                        "Successful finance professionals combine analytical thinking, professional integrity and disciplined judgement to support responsible financial decision-making within dynamic financial environments."

                }

            },

            fitInterpretation: {

                high: {

                    message:
                        "Your behavioural profile already reflects many of the habits that Banking and Finance consistently rewards.",

                    coachingFocus: [

                        "Advanced financial judgement",

                        "Strategic financial decision-making",

                        "Professional financial leadership"

                    ]

                },

                moderate: {

                    message:
                        "Your behavioural profile provides a strong foundation for Banking and Finance while highlighting behaviours that will continue developing through financial analysis, investment evaluation and professional decision-making.",

                    coachingFocus: [

                        "Financial analysis",

                        "Risk evaluation",

                        "Professional judgement"

                    ]

                },

                low: {

                    message:
                        "Some of the behaviours consistently rewarded throughout Banking and Finance may not yet be among your strongest natural preferences. These behaviours become stronger through structured financial learning, analytical practice and professional experience.",

                    coachingFocus: [

                        "Analytical reasoning",

                        "Financial confidence",

                        "Risk evaluation",

                        "Evidence-based decision making"

                    ]

                }

            },

            futureOutlook: {

                summary:
                    "Graduates who strengthen these behaviours become increasingly prepared to evaluate financial opportunities, manage organisational risks and support responsible financial decision-making across banking and financial services.",

                careerMindset:
                    "Financial professionals are trusted to balance opportunity with risk while making objective decisions that protect organisations, customers and financial systems."

            }

        },

        behaviouralNarrative: {

            High({ applicantInterpretation }) {

                return `
        Your assessment suggests that many of your natural behavioural tendencies already align well with the analytical and professionally disciplined nature of Banking and Finance. You appear comfortable evaluating information carefully, making decisions using evidence and approaching complex problems with logical reasoning and structure.

        These behaviours provide a strong foundation for a programme that requires students to analyse financial information, evaluate investment opportunities, assess financial risks and support responsible decision-making within regulated financial environments. Throughout your studies you will interpret financial markets, examine banking operations and develop the judgement required to balance financial opportunity with risk.

        As your knowledge develops, you will strengthen your ability to make informed financial decisions, evaluate uncertainty objectively and communicate financial recommendations professionally. Continuing to develop these behaviours will prepare you for careers where analytical thinking, financial responsibility and ethical judgement are essential.

        Overall, your behavioural profile already reflects many of the habits that Banking and Finance is intentionally designed to strengthen.
        `;

            },

            Moderate: {

                opening:
                    "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Banking and Finance, although some behaviours will continue developing as financial analysis and decision-making become increasingly sophisticated.",

                strengths:
                    "You already demonstrate behaviours that support analytical thinking, structured reasoning and objective decision-making, all of which contribute positively throughout the programme.",

                programmeConnection:
                    "Banking and Finance requires students to evaluate financial information, analyse investment opportunities, assess financial risks and make evidence-based decisions within regulated environments. While many of these behaviours already appear within your profile, professional judgement, financial confidence and risk evaluation typically strengthen through sustained practice and academic experience.",

                coaching:
                    "Focus on strengthening financial analysis, evidence-based decision-making, risk evaluation and confidence when interpreting complex financial information.",

                conclusion:
                    "With deliberate practice and continued development, your behavioural profile provides a strong platform for succeeding within Banking and Finance."

            },

            Low: {

                opening:
                    "Your assessment suggests that some of the behaviours most consistently rewarded throughout Banking and Finance may not yet be among your strongest natural preferences.",

                strengths:
                    "This does not prevent success, but it means you may need to approach analytical reasoning, financial evaluation and structured decision-making more deliberately than students whose behavioural tendencies already align closely with the programme.",

                programmeConnection:
                    "Banking and Finance consistently rewards analytical thinking, disciplined judgement, financial accuracy and responsible risk evaluation. These behaviours become increasingly important as students progress into investment analysis, corporate finance, financial modelling and banking practice.",

                coaching:
                    "Deliberately strengthening analytical reasoning, financial confidence, evidence-based decision-making and risk awareness will make both the academic programme and future professional environments progressively more manageable.",

                conclusion:
                    "Professional financial judgement develops through learning and experience. Students who intentionally cultivate these behaviours often become capable banking and finance professionals able to make responsible financial decisions in complex environments."

            }

        },
    },

    "BSc Real Estate Management and Finance": {
    
        programmeIntelligence: {

            description:
                "Prepares graduates to analyse, value, finance, manage and develop real estate assets through the integration of finance, valuation, land administration, urban planning, property law, construction management and investment analysis. Students develop the analytical, technical and professional competencies required to make informed decisions across the real estate industry.",

            knowledgeAreas: [

                "Real Estate Management",

                "Property Valuation",

                "Real Estate Finance",

                "Real Estate Investment",

                "Property Risk Management",

                "Property Insurance",

                "Land Administration",

                "Customary Land Law",

                "Real Estate Law",

                "Urban Administration and Planning",

                "Real Estate Economics",

                "Property Marketing and Brokerage",

                "Property Rating and Taxation",

                "Property Management",

                "Housing Studies",

                "Building Construction and Management",

                "Bills of Quantities",

                "Geo-Information Systems",

                "Procurement and Contract Management",

                "Project Management",

                "Business Finance",

                "Accounting",

                "Quantitative Methods",

                "Research Methods",

                "Business Strategy",

                "Environmental Management",

                "Corporate Governance"

            ],

            learningEnvironment: [

                "Structured analytical learning",

                "Applied property and financial analysis",

                "Evidence-based decision making",

                "Case-based professional learning",

                "Project-centred learning",

                "Field-oriented property studies",

                "Practical valuation exercises",

                "Professional workplace exposure"

            ],

            learningOutcomes: [

                "Analyse property markets using economic and financial evidence.",

                "Conduct property valuation using recognised valuation approaches.",

                "Evaluate the financial feasibility of real estate investments.",

                "Interpret land administration systems and property legislation.",

                "Apply principles of real estate finance and investment management.",

                "Assess property-related risks and recommend mitigation strategies.",

                "Manage property assets throughout their operational life cycle.",

                "Apply construction, procurement and contract management principles within property development.",

                "Use geo-information systems to support spatial analysis and property decision-making.",

                "Undertake independent research and communicate professional recommendations effectively."

            ],

            curriculumStructure: {

                foundation:
                    "Students begin with communication, critical thinking, economics, statistics, management, finance, accounting, law, leadership and information technology to establish business and analytical competence.",

                intermediate:
                    "The curriculum progresses into geo-information systems, real estate management, land administration, planning, valuation foundations, property law, construction management and applied property economics.",

                advanced:
                    "Students integrate valuation, finance, investment, property management, taxation, brokerage, procurement, development analysis, risk management and strategic management through advanced professional courses.",

                experientialLearning:
                    "Professional learning is reinforced through internship, project work, practical valuation exercises, applied case analysis, field-based learning and independent research."

            },

            teachingMethods: [

                "Lectures",

                "Case studies",

                "Problem-based learning",

                "Practical valuation exercises",

                "Field visits",

                "Research supervision",

                "Project-based learning",

                "Industry internship",

                "Seminars",

                "Technology-assisted learning",

                "Professional presentations"

            ],

            learningActivities: [

                "Property valuation",

                "Investment appraisal",

                "Financial feasibility analysis",

                "Market analysis",

                "GIS analysis",

                "Construction analysis",

                "Development planning",

                "Legal interpretation",

                "Property inspection",

                "Research projects",

                "Case study analysis",

                "Professional report writing",

                "Presentations",

                "Internship",

                "Project work"

            ],

            assessmentMethods: [

                "Written examinations",

                "Valuation reports",

                "Case study analyses",

                "Research projects",

                "Project work",

                "Professional presentations",

                "Practical valuation exercises",

                "Field-based assessments",

                "Internship assessment",

                "Applied assignments"

            ],

            coreCompetencies: [

                "Property valuation",

                "Investment analysis",

                "Financial analysis",

                "Property market evaluation",

                "Risk assessment",

                "Land administration",

                "Property law application",

                "Construction awareness",

                "Strategic property management",

                "Professional judgement",

                "Research capability",

                "Evidence-based decision making"

            ],

            behaviouralDemands: [

                "Analytical reasoning",

                "Evidence-based decision making",

                "Critical judgement",

                "Financial evaluation",

                "Attention to technical accuracy",

                "Professional responsibility",

                "Strategic thinking",

                "Regulatory awareness",

                "Problem solving",

                "Independent learning",

                "Professional communication",

                "Adaptability across multidisciplinary environments"

            ],

            graduateExpectations: {

                knowledge:
                    "Graduates are expected to demonstrate comprehensive understanding of property valuation, real estate finance, investment analysis, land administration, urban planning, construction management, property law and sustainable real estate development.",

                professionalSkills:
                    "Graduates should be capable of evaluating property investments, conducting valuations, analysing real estate markets, managing property assets, interpreting land legislation, supporting development projects and making evidence-based commercial decisions.",

                workplaceReadiness:
                    "Graduates are expected to transition effectively into valuation practice, property management, development, investment, consultancy and public-sector land administration through sound analytical judgement, ethical practice and technical competence.",

                lifelongLearning:
                    "Graduates should possess the research capability, professional adaptability and continuous learning mindset required to respond to evolving property markets, technological innovation, changing legislation and emerging development practices."

            },

            careerPathways: [

                "Property Valuation",

                "Real Estate Investment",

                "Property Management",

                "Estate Surveying",

                "Property Development",

                "Real Estate Consultancy",

                "Property Finance",

                "Asset Management",

                "Property Marketing",

                "Real Estate Brokerage",

                "Housing Management",

                "Urban Development",

                "Land Administration",

                "Property Taxation",

                "Property Risk Management",

                "Construction Project Support",

                "Facilities Management",

                "Property Research",

                "Public Land Administration",

                "Real Estate Advisory"

            ],

            workEnvironment: [

                "Real estate development companies",

                "Property valuation firms",

                "Estate surveying firms",

                "Property management companies",

                "Real estate investment firms",

                "Commercial property organisations",

                "Housing agencies",

                "Urban planning institutions",

                "Land administration agencies",

                "Construction and development firms",

                "Property consultancy firms",

                "Financial institutions",

                "Government regulatory agencies",

                "Municipal authorities",

                "Infrastructure and development organisations"

            ],

            // =====================================================
            // UI Summary Fields
            // =====================================================

            environment:
                "A structured and professionally regulated learning environment where students evaluate property markets, analyse investments, interpret land and property law, conduct valuations and solve real estate development challenges using financial, legal and technical evidence.",

            workStyle:
                "Working with property assets, market information, financial data, legal documentation and technical evidence to support valuation, investment, development and property management decisions.",

            demands:
                "Requires strong analytical reasoning, financial judgement, attention to technical accuracy, evidence-based decision making, professional responsibility and the ability to integrate finance, law, planning and construction knowledge when solving real estate problems.",

            realityCheck: {

                workload: "High",

                mathematics: "Moderate to High",

                reading: "High",

                writing: "High",

                teamwork: "Moderate",

                presentations: "Moderate"

            }

        },

    behaviouralRequirements: {

        propertyValuation: {

                description:
                    "Evaluate the value of property assets using recognised valuation principles, market evidence and professional judgement.",

                requiredBecause:
                    "Students repeatedly conduct valuation exercises, analyse property markets and apply valuation methods throughout the programme.",

                observableBehaviours: [
                    "Analyses market evidence objectively",
                    "Applies valuation methodologies systematically",
                    "Produces evidence-based valuation conclusions",
                    "Exercises professional judgement when comparing valuation approaches"
                ],

                assessmentIndicators: [
                    "Valuation reports",
                    "Practical valuation exercises",
                    "Case analyses",
                    "Applied assignments"
                ],

                evidenceWeight: 10,

                behaviouralDimensions: {
                    inclusion: 45,
                    control: 88,
                    affection: 30,
                    thinking: 95,
                    flexibility: 58
                }

            },

            investmentAnalysis: {

                description:
                    "Evaluate the financial feasibility, risks and long-term performance of real estate investments using analytical and financial evidence.",

                requiredBecause:
                    "The curriculum emphasises real estate finance, investment appraisal, financial feasibility studies and property economics.",

                observableBehaviours: [
                    "Evaluates investment alternatives objectively",
                    "Assesses financial risks before making decisions",
                    "Interprets financial evidence",
                    "Balances risk and return using structured analysis"
                ],

                assessmentIndicators: [
                    "Financial analyses",
                    "Investment case studies",
                    "Research projects",
                    "Written examinations"
                ],

                evidenceWeight: 10,

                behaviouralDimensions: {
                    inclusion: 48,
                    control: 86,
                    affection: 32,
                    thinking: 94,
                    flexibility: 64
                }

            },

            legalAndRegulatoryApplication: {

                description:
                    "Apply land law, property law, taxation and regulatory requirements when analysing, managing and transferring property interests.",

                requiredBecause:
                    "Students study Customary Land Law, Real Estate Law, Property Rating and Taxation and Procurement throughout the programme.",

                observableBehaviours: [
                    "Applies regulations consistently",
                    "Works carefully with legal documentation",
                    "Maintains procedural accuracy",
                    "Makes compliant professional decisions"
                ],

                assessmentIndicators: [
                    "Case analyses",
                    "Legal assignments",
                    "Written examinations",
                    "Project work"
                ],

                evidenceWeight: 9,

                behaviouralDimensions: {
                    inclusion: 42,
                    control: 94,
                    affection: 28,
                    thinking: 88,
                    flexibility: 52
                }

            },

            propertyManagement: {

                description:
                    "Manage property assets, facilities and stakeholder relationships to maximise long-term value and operational effectiveness.",

                requiredBecause:
                    "Property management, housing, facilities, brokerage and asset management are recurring themes within the programme.",

                observableBehaviours: [
                    "Coordinates property operations",
                    "Balances stakeholder interests professionally",
                    "Monitors asset performance",
                    "Organises property management activities systematically"
                ],

                assessmentIndicators: [
                    "Projects",
                    "Case studies",
                    "Internship",
                    "Professional reports"
                ],

                evidenceWeight: 9,

                behaviouralDimensions: {
                    inclusion: 72,
                    control: 82,
                    affection: 60,
                    thinking: 80,
                    flexibility: 70
                }

            },

            developmentPlanning: {

                description:
                    "Analyse, plan and evaluate real estate development projects by integrating financial, technical, legal and environmental considerations.",

                requiredBecause:
                    "Urban planning, building construction, procurement, development analysis and project management are integrated throughout the upper levels.",

                observableBehaviours: [
                    "Integrates multiple sources of evidence",
                    "Plans development activities systematically",
                    "Evaluates technical and financial feasibility",
                    "Considers long-term project implications"
                ],

                assessmentIndicators: [
                    "Development projects",
                    "Case studies",
                    "Research projects",
                    "Presentations"
                ],

                evidenceWeight: 9,

                behaviouralDimensions: {
                    inclusion: 62,
                    control: 84,
                    affection: 42,
                    thinking: 90,
                    flexibility: 82
                }

            },

            spatialAndTechnicalAnalysis: {

                description:
                    "Use spatial information, technical property data and construction knowledge to support professional decision-making.",

                requiredBecause:
                    "The curriculum includes Geo-Information Systems, Bills of Quantities, Building Construction and valuation practice.",

                observableBehaviours: [
                    "Interprets technical information accurately",
                    "Uses spatial evidence effectively",
                    "Connects technical and commercial information",
                    "Applies quantitative reasoning to property analysis"
                ],

                assessmentIndicators: [
                    "Practical exercises",
                    "Technical assignments",
                    "Applied projects",
                    "Written examinations"
                ],

                evidenceWeight: 8,

                behaviouralDimensions: {
                    inclusion: 40,
                    control: 86,
                    affection: 26,
                    thinking: 92,
                    flexibility: 72
                }

            },

            professionalCommunication: {

                description:
                    "Communicate property analyses, valuation outcomes and professional recommendations clearly to clients and stakeholders.",

                requiredBecause:
                    "Students complete valuation reports, presentations, project work, internship and brokerage-related activities throughout the programme.",

                observableBehaviours: [
                    "Produces professional reports",
                    "Explains technical findings clearly",
                    "Presents recommendations confidently",
                    "Communicates with professional stakeholders"
                ],

                assessmentIndicators: [
                    "Presentations",
                    "Valuation reports",
                    "Project work",
                    "Internship assessment"
                ],

                evidenceWeight: 7,

                behaviouralDimensions: {
                    inclusion: 74,
                    control: 72,
                    affection: 58,
                    thinking: 76,
                    flexibility: 68
                }

            },

            researchAndIndependentLearning: {

                description:
                    "Investigate complex property problems independently using appropriate research methods and evidence-based analysis.",

                requiredBecause:
                    "Research Methods, Project Work and Internship require students to undertake independent investigation and professional analysis.",

                observableBehaviours: [
                    "Works independently",
                    "Synthesises evidence systematically",
                    "Investigates property problems thoroughly",
                    "Draws justified conclusions from research"
                ],

                assessmentIndicators: [
                    "Research project",
                    "Project work",
                    "Internship",
                    "Research reports"
                ],

                evidenceWeight: 8,

                behaviouralDimensions: {
                    inclusion: 48,
                    control: 82,
                    affection: 34,
                    thinking: 90,
                    flexibility: 70
                }

            }

        },

        programmeBehaviouralModel: {

            inclusion: {

                score: 66,

                derivedFrom: [

                    "propertyManagement",

                    "professionalCommunication",

                    "developmentPlanning"

                ]

            },

            control: {

                score: 90,

                derivedFrom: [

                    "propertyValuation",

                    "legalAndRegulatoryApplication",

                    "investmentAnalysis",

                    "spatialAndTechnicalAnalysis"

                ]

            },

            affection: {

                score: 42,

                derivedFrom: [

                    "propertyManagement",

                    "professionalCommunication"

                ]

            },

            thinking: {

                score: 94,

                derivedFrom: [

                    "propertyValuation",

                    "investmentAnalysis",

                    "developmentPlanning",

                    "spatialAndTechnicalAnalysis",

                    "researchAndIndependentLearning"

                ]

            },

            flexibility: {

                score: 74,

                derivedFrom: [

                    "developmentPlanning",

                    "propertyManagement",

                    "researchAndIndependentLearning",

                    "investmentAnalysis"

                ]

            }

        },
        
        behaviourProfile: {

            inclusion: 66,

            control: 90,

            affection: 42,

            thinking: 94,

            flexibility: 74,

            traits: [

                "analytical",

                "structured",

                "evidence-driven",

                "commercially strategic",

                "professionally disciplined",

                "multidisciplinary",

                "risk-aware",

                "decision-intensive"

            ]

        },

        fitExplanations: {

            High:
                "Real Estate Management and Finance requires strong analytical reasoning, structured decision-making, technical accuracy and the ability to integrate financial, legal and property information when solving complex real estate problems. Your behavioural profile aligns strongly with these demands, suggesting you are likely to thrive in the programme's multidisciplinary learning environment.",

            Moderate:
                "Real Estate Management and Finance combines property valuation, finance, investment analysis, land administration, planning and professional communication. Your behavioural profile aligns with several of these demands, although some areas may require further development as you progress through the programme.",

            Low:
                "Real Estate Management and Finance places strong emphasis on analytical reasoning, structured judgement, technical accuracy and evidence-based decision-making across property, finance and legal contexts. If these demands are less aligned with your current behavioural strengths, some aspects of the programme may require more deliberate practice. Developing these capabilities can significantly improve your ability to succeed."

        },

        applicationNarratives: {

            High: {

                title:
                    "Why this may suit your learning style",

                message:
                    "Your behavioural profile aligns well with the demands of Real Estate Management and Finance. The programme rewards analytical reasoning, disciplined judgement and the ability to combine financial, legal and technical evidence when making professional property decisions."

            },

            Moderate: {

                title:
                    "Why this may suit your learning style",

                message:
                    "Your behavioural profile aligns naturally with several aspects of Real Estate Management and Finance, particularly where structured analysis and evidence-based decision-making are required. As the programme progresses, developing greater confidence in valuation, investment analysis and multidisciplinary problem solving will strengthen your ability to thrive."

            },

            Low: {

                title:
                    "Why this may suit your learning style",

                message:
                    "Real Estate Management and Finance requires analytical reasoning, technical accuracy, professional judgement and the ability to integrate finance, law, planning and property information when solving complex problems. If these demands are less aligned with your current behavioural strengths, some aspects of the programme may require additional effort. Developing stronger evidence-based decision-making and analytical skills will help you adapt successfully."

            }

        },

        fitDimensions: {

            analytical: 95,

            social: 68,

            structure: 90,

            creativity: 56,

            emotionalDemand: 42,

            adaptability: 78

        },

        environmentTraits: {

            structureLevel: 91,

            collaborationLevel: 70,

            ambiguityLevel: 60,

            technicalIntensity: 86,

            creativityDemand: 58,

            emotionalDemand: 40,

            leadershipDemand: 70,

            communicationIntensity: 78

        },

        semanticTypes: [

            "property_management_program",

            "real_estate_finance_program",

            "valuation_program",

            "investment_analysis_program",

            "asset_management_program",

            "built_environment_program"

        ],

        learningEnvironment: {

            pace: 80,

            independence: 80,

            practicalLearning: 90,

            teamwork: 68,

            assessmentStructure: 88,

            feedbackFrequency: 60

        },

        skillsToStrengthen: [

            "Analytical reasoning",

            "Evidence-based decision making",

            "Property market analysis",

            "Professional communication",

            "Strategic investment thinking",

            "Attention to technical accuracy",

            "Regulatory awareness",

            "Integrating financial, legal and technical information"

        ],

        explanationBlueprint: {

            programmeIdentity: {

                title: "Real Estate Management and Finance",

                summary:
                    "Real Estate Management and Finance develops the ability to evaluate property assets, analyse investments and make informed decisions by integrating financial, legal, technical and market evidence.",

                purpose:
                    "The programme prepares students to value property, manage real estate assets, analyse investment opportunities and support sustainable property development through evidence-based professional judgement."

            },

            behaviouralStory: {

                whoThrives: {

                    summary:
                        "Students who enjoy analysing complex information, solving practical problems and integrating knowledge from multiple disciplines often thrive in Real Estate Management and Finance.",

                    keyBehaviours: [

                        "Analytical reasoning",

                        "Professional judgement",

                        "Strategic evaluation",

                        "Evidence-based decision making"

                    ]

                },

                whyBehaviourMatters: {

                    summary:
                        "Success depends on behaviours that support property valuation, investment analysis, regulatory awareness and the ability to combine financial, legal and technical evidence when making professional decisions.",

                    evidenceAreas: [

                        "propertyValuation",

                        "investmentAnalysis",

                        "developmentPlanning",

                        "legalAndRegulatoryApplication"

                    ]

                },

                learningJourney: {

                    summary:
                        "Students progressively move from understanding property systems and valuation principles to analysing investments, managing assets, planning developments and making increasingly sophisticated real estate decisions."

                },

                professionalMindset: {

                    summary:
                        "Successful real estate professionals combine analytical thinking, commercial awareness, technical accuracy and ethical judgement to create long-term value across property markets."

                }

            },

            fitInterpretation: {

                high: {

                    message:
                        "Your behavioural profile already reflects many of the habits that Real Estate Management and Finance consistently rewards.",

                    coachingFocus: [

                        "Advanced valuation",

                        "Strategic property investment",

                        "Professional consultancy"

                    ]

                },

                moderate: {

                    message:
                        "Your behavioural profile provides a strong foundation while highlighting behaviours that will continue developing through valuation, investment analysis and multidisciplinary property decision-making.",

                    coachingFocus: [

                        "Property analysis",

                        "Investment judgement",

                        "Professional communication"

                    ]

                },

                low: {

                    message:
                        "Some of the behaviours consistently rewarded throughout Real Estate Management and Finance may not yet be among your strongest natural preferences. These behaviours become stronger through practical experience, analytical practice and professional development.",

                    coachingFocus: [

                        "Analytical reasoning",

                        "Evidence-based judgement",

                        "Property analysis",

                        "Strategic thinking"

                    ]

                }

            },

            futureOutlook: {

                summary:
                    "Graduates who strengthen these behaviours become increasingly prepared to evaluate property opportunities, manage assets responsibly and support investment and development decisions across the real estate industry.",

                careerMindset:
                    "Real estate professionals are expected to balance financial performance, legal responsibility, technical accuracy and long-term value when making property decisions."

            }

        },

        behaviouralNarrative: {

            High({ applicantInterpretation }) {

                return `
        Your assessment suggests that many of your natural behavioural tendencies already align well with the multidisciplinary and evidence-driven nature of Real Estate Management and Finance. You appear comfortable analysing information carefully, making structured decisions and evaluating problems from multiple perspectives before reaching conclusions.

        These behaviours provide a strong foundation for a programme that requires students to value property, evaluate investments, interpret land and property regulations and integrate financial, legal and technical evidence when making professional decisions. Throughout your studies you will investigate property markets, assess development opportunities and learn to balance commercial objectives with professional responsibility.

        As your knowledge develops, you will strengthen your ability to evaluate property investments, manage real estate assets and communicate professional recommendations that support sustainable development and long-term value creation. Continuing to develop these behaviours will prepare you for careers where analytical thinking, commercial judgement and technical accuracy are highly valued.

        Overall, your behavioural profile already reflects many of the habits that Real Estate Management and Finance is intentionally designed to strengthen.
        `;

            },

            Moderate: {

                opening:
                    "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Real Estate Management and Finance, although some behaviours will continue developing as property analysis and professional decision-making become increasingly sophisticated.",

                strengths:
                    "You already demonstrate behaviours that support analytical reasoning, structured thinking and evidence-based decision-making, all of which contribute positively throughout the programme.",

                programmeConnection:
                    "Real Estate Management and Finance requires students to evaluate property markets, analyse investments, interpret regulations and integrate financial, legal and technical information when solving complex property problems. While many of these behaviours already appear within your profile, professional judgement, multidisciplinary thinking and strategic evaluation typically strengthen through practical learning and experience.",

                coaching:
                    "Focus on strengthening property analysis, investment evaluation, professional communication and confidence when integrating financial, legal and technical evidence.",

                conclusion:
                    "With deliberate practice and continued development, your behavioural profile provides a strong platform for succeeding within Real Estate Management and Finance."

            },

            Low: {

                opening:
                    "Your assessment suggests that some of the behaviours most consistently rewarded throughout Real Estate Management and Finance may not yet be among your strongest natural preferences.",

                strengths:
                    "This does not prevent success, but it means you may need to approach analytical reasoning, multidisciplinary problem-solving and evidence-based decision-making more deliberately than students whose behavioural tendencies already align closely with the programme.",

                programmeConnection:
                    "Real Estate Management and Finance consistently rewards analytical thinking, structured judgement, technical accuracy and the ability to combine financial, legal and property information when making professional decisions. These behaviours become increasingly important as students progress into valuation, investment, development and asset management.",

                coaching:
                    "Deliberately strengthening analytical reasoning, property market analysis, strategic thinking and confidence when integrating evidence from multiple disciplines will make both the academic programme and future professional practice progressively more manageable.",

                conclusion:
                    "Professional judgement develops through learning and experience. Students who intentionally cultivate these behaviours often become capable real estate professionals able to evaluate property opportunities and make responsible long-term decisions."

            }

        },
            
    },

    "BSc Marketing": {

    programmeIntelligence: {

        description:
            "The BSc Marketing programme prepares graduates to understand customer behaviour, develop marketing strategies, manage brands, conduct market research and create value through effective communication, customer engagement and evidence-based decision-making. Students develop the knowledge and professional competencies required to analyse markets, build customer relationships and support organisational growth across diverse industries.",

        knowledgeAreas: [

            "Marketing Management",

            "Understanding Customer Dynamics",

            "Marketing Research",

            "Strategic Marketing Planning",

            "Brand Management",

            "Integrated Marketing Communication",

            "Digital Marketing",

            "Sales Management",

            "Retail Marketing",

            "Business-to-Business Marketing",

            "International Marketing",

            "Services Marketing",

            "Tourism Marketing",

            "Sports Marketing",

            "Social Marketing",

            "Financial Information for Marketing",

            "Business Strategy",

            "Business Ethics",

            "Research Methods",

            "Quantitative Methods"

        ],

        learningEnvironment: [

            "Customer-focused learning",

            "Market-oriented problem solving",

            "Case-based business learning",

            "Collaborative learning activities",

            "Research-driven decision making",

            "Practical marketing planning",

            "Communication-intensive learning",

            "Brand and campaign development",

            "Digital marketing applications",

            "Industry internship",

            "Project-based learning",

            "Independent research"

        ],

        learningActivities: [

            "Market research",

            "Customer analysis",

            "Marketing strategy development",

            "Brand development",

            "Marketing campaign planning",

            "Sales planning",

            "Consumer behaviour analysis",

            "Business case analysis",

            "Marketing presentations",

            "Group projects",

            "Research projects",

            "Digital marketing activities",

            "Internship",

            "Project work"

        ],

        teachingMethods: [

            "Lectures",

            "Case studies",

            "Problem-based learning",

            "Marketing simulations",

            "Research supervision",

            "Seminars",

            "Industry internship",

            "Project-based learning",

            "Group discussions",

            "Technology-assisted learning"

        ],

        assessmentMethods: [

            "Written examinations",

            "Marketing case analyses",

            "Marketing plans",

            "Research projects",

            "Group assignments",

            "Individual assignments",

            "Presentations",

            "Campaign development projects",

            "Internship assessment",

            "Final year project"

        ],

        programmeDemands: [

            "Analytical thinking",

            "Strategic decision making",

            "Customer understanding",

            "Market analysis",

            "Creative problem solving",

            "Professional communication",

            "Evidence-based judgement",

            "Collaboration",

            "Adaptability",

            "Research capability",

            "Presentation skills",

            "Independent learning"

        ],

        professionalCompetencies: [

            "Marketing strategy development",

            "Market research",

            "Customer insight generation",

            "Brand management",

            "Marketing communication",

            "Sales management",

            "Digital marketing",

            "Business development",

            "Strategic planning",

            "Marketing analytics",

            "Professional communication",

            "Project management"

        ],

        graduateExpectations: [

            "Apply marketing principles to solve business problems.",

            "Conduct and interpret marketing research to support decision-making.",

            "Develop customer-focused marketing strategies.",

            "Plan and manage marketing communication programmes.",

            "Build and manage brands in competitive markets.",

            "Apply digital marketing tools and techniques appropriately.",

            "Communicate marketing recommendations professionally.",

            "Demonstrate ethical judgement and professional responsibility.",

            "Adapt marketing strategies to changing business environments.",

            "Work effectively in multidisciplinary business environments."

        ],

        careerPathways: [

            "Marketing Management",

            "Brand Management",

            "Digital Marketing",

            "Sales Management",

            "Marketing Research",

            "Advertising",

            "Public Relations",

            "Business Development",

            "Retail Management",

            "Customer Relationship Management",

            "Market Intelligence",

            "Corporate Communications",

            "Product Management",

            "Tourism Marketing",

            "Sports Marketing"

        ],

        workEnvironment: [

            "Corporate marketing departments",

            "Advertising agencies",

            "Marketing consulting firms",

            "Retail organisations",

            "Consumer goods companies",

            "Financial institutions",

            "Telecommunications companies",

            "Hospitality and tourism organisations",

            "Sports organisations",

            "Media organisations",

            "Digital marketing agencies",

            "Public sector organisations",

            "International businesses"

        ],

        // =====================================================
        // UI Summary Fields
        // =====================================================

        environment:
            "A collaborative and customer-focused learning environment where students analyse markets, understand customers, develop brands and create marketing strategies using research, communication and business evidence.",

        workStyle:
            "Working with customers, market information, brands, business data and communication strategies to develop marketing solutions, support organisational growth and build long-term customer value.",

        demands:
            "Requires analytical reasoning, strategic thinking, creativity, professional communication, customer understanding, research capability and the ability to adapt marketing strategies to changing market conditions.",

        realityCheck: {

            workload: "High",

            mathematics: "Moderate",

            reading: "High",

            writing: "High",

            teamwork: "High",

            presentations: "High"

        }

    },
    
    behaviouralRequirements: {

        customerInsight: {

            description:
                "Understand customer behaviour, market needs and purchasing decisions through systematic analysis and research.",

            requiredBecause:
                "The curriculum repeatedly emphasises Understanding Customer Dynamics, Consumer-focused Marketing, Marketing Research and Strategic Marketing Planning.",

            observableBehaviours: [

                "Interprets customer behaviour using evidence",

                "Identifies customer needs and market opportunities",

                "Analyses consumer decision-making objectively",

                "Uses market insights to support business decisions"

            ],

            assessmentIndicators: [

                "Marketing research reports",

                "Customer analysis assignments",

                "Case study analyses",

                "Written examinations"

            ],

            evidenceWeight: 10,

            behaviouralDimensions: {

                inclusion: 78,

                control: 68,

                affection: 74,

                thinking: 90,

                flexibility: 72

            }

        },

        strategicMarketing: {

            description:
                "Develop marketing strategies that align customer needs with organisational objectives using structured planning and commercial judgement.",

            requiredBecause:
                "Strategic Marketing Planning, Marketing Management and Business Strategy require students to evaluate alternatives and develop competitive marketing solutions.",

            observableBehaviours: [

                "Develops structured marketing plans",

                "Evaluates strategic alternatives",

                "Balances commercial objectives with customer value",

                "Makes evidence-based marketing decisions"

            ],

            assessmentIndicators: [

                "Marketing plans",

                "Case analyses",

                "Strategic reports",

                "Presentations"

            ],

            evidenceWeight: 10,

            behaviouralDimensions: {

                inclusion: 66,

                control: 82,

                affection: 56,

                thinking: 92,

                flexibility: 80

            }

        },

        marketingCommunication: {

            description:
                "Communicate marketing ideas, brand messages and business recommendations clearly across multiple audiences and communication channels.",

            requiredBecause:
                "Integrated Marketing Communication, Digital Marketing, Brand Management and Sales Management consistently require persuasive professional communication.",

            observableBehaviours: [

                "Communicates ideas clearly",

                "Adapts messages to different audiences",

                "Develops persuasive communication",

                "Presents recommendations confidently"

            ],

            assessmentIndicators: [

                "Presentations",

                "Campaign proposals",

                "Communication projects",

                "Group assignments"

            ],

            evidenceWeight: 9,

            behaviouralDimensions: {

                inclusion: 90,

                control: 62,

                affection: 82,

                thinking: 76,

                flexibility: 82

            }

        },

        brandAndMarketDevelopment: {

            description:
                "Develop brands, products and market positioning strategies that create sustainable customer value.",

            requiredBecause:
                "Brand Management, Retail Marketing, International Marketing, Tourism Marketing and Sports Marketing require students to evaluate diverse markets and positioning strategies.",

            observableBehaviours: [

                "Evaluates competitive markets",

                "Develops brand positioning",

                "Identifies market opportunities",

                "Integrates customer and business perspectives"

            ],

            assessmentIndicators: [

                "Brand development projects",

                "Marketing strategy reports",

                "Case studies",

                "Project work"

            ],

            evidenceWeight: 9,

            behaviouralDimensions: {

                inclusion: 74,

                control: 70,

                affection: 66,

                thinking: 88,

                flexibility: 88

            }

        },

        analyticalMarketingResearch: {

            description:
                "Collect, analyse and interpret market information to support evidence-based marketing decisions.",

            requiredBecause:
                "Marketing Research, Quantitative Methods, Research Methods and Financial Information for Marketing consistently require analytical investigation.",

            observableBehaviours: [

                "Interprets market evidence objectively",

                "Applies research methods appropriately",

                "Draws justified conclusions",

                "Supports decisions with data"

            ],

            assessmentIndicators: [

                "Research projects",

                "Statistical assignments",

                "Marketing reports",

                "Final year project"

            ],

            evidenceWeight: 10,

            behaviouralDimensions: {

                inclusion: 56,

                control: 78,

                affection: 42,

                thinking: 94,

                flexibility: 68

            }

        },

        collaborativeProfessionalPractice: {

            description:
                "Work effectively with colleagues, customers and stakeholders while demonstrating professional responsibility and ethical judgement.",

            requiredBecause:
                "Internship, Project Work, Business Ethics, Sales Management and marketing projects require collaboration and professional interaction.",

            observableBehaviours: [

                "Works effectively in teams",

                "Builds professional relationships",

                "Demonstrates ethical judgement",

                "Collaborates to achieve shared objectives"

            ],

            assessmentIndicators: [

                "Group projects",

                "Internship assessment",

                "Presentations",

                "Project work"

            ],

            evidenceWeight: 8,

            behaviouralDimensions: {

                inclusion: 86,

                control: 70,

                affection: 78,

                thinking: 72,

                flexibility: 80

            }

        },

        adaptabilityAndInnovation: {

            description:
                "Respond effectively to changing customer expectations, technologies and market environments by adapting marketing approaches appropriately.",

            requiredBecause:
                "Digital Marketing, International Marketing, Social Marketing and emerging marketing contexts require continuous adaptation and innovation.",

            observableBehaviours: [

                "Adjusts strategies to changing environments",

                "Learns new approaches quickly",

                "Responds constructively to market change",

                "Applies innovative thinking within business objectives"

            ],

            assessmentIndicators: [

                "Digital marketing projects",

                "Campaign development",

                "Case studies",

                "Project work"

            ],

            evidenceWeight: 8,

            behaviouralDimensions: {

                inclusion: 72,

                control: 64,

                affection: 60,

                thinking: 82,

                flexibility: 94

            }

        }

    },

    programmeBehaviouralModel: {

        inclusion: {

            score: 84,

            derivedFrom: [

                "customerInsight",

                "marketingCommunication",

                "brandAndMarketDevelopment",

                "collaborativeProfessionalPractice"

            ]

        },

        control: {

            score: 76,

            derivedFrom: [

                "strategicMarketing",

                "analyticalMarketingResearch",

                "customerInsight"

            ]

        },

        affection: {

            score: 72,

            derivedFrom: [

                "customerInsight",

                "marketingCommunication",

                "collaborativeProfessionalPractice"

            ]

        },

        thinking: {

            score: 91,

            derivedFrom: [

                "analyticalMarketingResearch",

                "strategicMarketing",

                "customerInsight",

                "brandAndMarketDevelopment"

            ]

        },

        flexibility: {

            score: 88,

            derivedFrom: [

                "adaptabilityAndInnovation",

                "brandAndMarketDevelopment",

                "marketingCommunication",

                "strategicMarketing"

            ]

        },

        traits: [

            "customer-focused",

            "strategic",

            "analytical",

            "persuasive",

            "adaptive",

            "commercially aware",

            "collaborative",

            "market-oriented"

        ]

    },

    behaviourProfile: {

        inclusion: 84,

        control: 76,

        affection: 72,

        thinking: 91,

        flexibility: 88,

        traits: [

            "customer-focused",

            "strategic",

            "analytical",

            "persuasive",

            "adaptive",

            "commercially aware",

            "collaborative",

            "market-oriented"

        ]

    },
      
    fitExplanations: {

        High:
            "Marketing requires strong customer understanding, analytical thinking, strategic decision-making and effective communication. Your behavioural profile aligns well with these demands, suggesting you are likely to thrive in the programme's collaborative and market-focused learning environment.",

        Moderate:
            "Marketing combines customer analysis, market research, strategic planning and communication across a variety of business contexts. Your behavioural profile aligns with several of these demands, although some areas may require further development as you progress through the programme.",

        Low:
            "Marketing places strong emphasis on understanding customers, adapting to changing market conditions, communicating ideas effectively and making evidence-based marketing decisions. If these demands are less aligned with your current behavioural strengths, some aspects of the programme may require more deliberate practice. Developing these capabilities can strengthen your ability to succeed."

    },

    applicationNarratives: {

        High: {

            title:
                "Why this may suit your learning style",

            message:
                "Your behavioural profile aligns well with the demands of Marketing. The programme rewards customer understanding, strategic thinking, analytical reasoning and effective communication when developing solutions to real business and market challenges."

        },

        Moderate: {

            title:
                "Why this may suit your learning style",

            message:
                "Your behavioural profile aligns naturally with several aspects of Marketing, particularly where strategic thinking, customer understanding and evidence-based decision-making are required. As the programme progresses, developing greater confidence in communication, market analysis and adapting to changing business environments will strengthen your ability to thrive."

        },

        Low: {

            title:
                "Why this may suit your learning style",

            message:
                "Marketing requires strong customer awareness, communication, adaptability and the ability to develop strategies using market evidence and business insight. If these demands are less aligned with your current behavioural strengths, some aspects of the programme may require additional effort. Developing stronger communication, analytical reasoning and strategic decision-making skills will help you adapt more successfully."

        }

    },

    fitDimensions: {

        analytical: 90,

        social: 84,

        structure: 74,

        creativity: 80,

        emotionalDemand: 58,

        adaptability: 90

    },

    environmentTraits: {

        structure: 72,

        collaboration: 86,

        autonomy: 68,

        pace: 82,

        uncertainty: 76,

        leadership: 74

    },

    semanticTypes: [

        "marketing_program",

        "customer_insight_program",

        "brand_management_program",

        "strategic_communication_program",

        "market_research_program",

        "commercial_strategy_program"

    ],

    learningEnvironment: {

        independence: 72,

        collaboration: 86,

        practical: 88,

        theoretical: 70,

        pace: 82

    },

    
    skillsToStrengthen: [

        "Customer understanding",

        "Analytical reasoning",

        "Strategic decision-making",

        "Professional communication",

        "Evidence-based problem solving",

        "Adaptability to changing markets",

        "Collaborative teamwork",

        "Creative commercial thinking"

    ],

    explanationBlueprint: {

        programmeIdentity: {

            title: "Marketing",

            summary:
                "Marketing develops the ability to understand customers, analyse markets and create value through evidence-based strategy, communication and innovation.",

            purpose:
                "The programme prepares students to investigate customer behaviour, develop marketing strategies, build brands and solve business challenges by combining analytical reasoning with creativity and effective communication."

        },

        behaviouralStory: {

            whoThrives: {

                summary:
                    "Students who enjoy understanding people, analysing markets, solving business problems and communicating ideas creatively often thrive in Marketing.",

                keyBehaviours: [

                    "Customer understanding",

                    "Strategic thinking",

                    "Analytical reasoning",

                    "Professional communication"

                ]

            },

            whyBehaviourMatters: {

                summary:
                    "Marketing consistently rewards behaviours that combine customer insight, evidence-based decision-making, creativity and adaptability to changing market conditions.",

                evidenceAreas: [

                    "customerInsight",

                    "strategicMarketing",

                    "marketingCommunication",

                    "analyticalMarketingResearch"

                ]

            },

            learningJourney: {

                summary:
                    "Students progressively develop from understanding customer behaviour and marketing principles to designing strategies, managing brands, conducting research and solving increasingly complex market challenges."

            },

            professionalMindset: {

                summary:
                    "Successful marketing professionals combine analytical thinking, creativity, commercial awareness and effective communication to create meaningful value for customers and organisations."

            }

        },

        fitInterpretation: {

            high: {

                message:
                    "Your behavioural profile already reflects many of the habits that Marketing consistently rewards.",

                coachingFocus: [

                    "Strategic brand leadership",

                    "Customer-centred innovation",

                    "Advanced marketing strategy"

                ]

            },

            moderate: {

                message:
                    "Your behavioural profile provides a strong foundation while highlighting behaviours that will continue developing through market research, strategic planning and customer engagement.",

                coachingFocus: [

                    "Market analysis",

                    "Professional communication",

                    "Strategic creativity"

                ]

            },

            low: {

                message:
                    "Some of the behaviours consistently rewarded throughout Marketing may not yet be among your strongest natural preferences. These behaviours become stronger through practical marketing experience, customer interaction and strategic problem-solving.",

                coachingFocus: [

                    "Customer understanding",

                    "Communication",

                    "Strategic thinking",

                    "Adaptability"

                ]

            }

        },

        futureOutlook: {

            summary:
                "Graduates who strengthen these behaviours become increasingly prepared to understand customers, build successful brands and create marketing strategies that contribute to organisational growth.",

            careerMindset:
                "Marketing professionals create value by combining customer insight, business strategy, creativity and evidence-based decision-making in rapidly changing markets."

        }

    },

    behaviouralNarrative: {

        High({ applicantInterpretation }) {

            return `
    Your assessment suggests that many of your natural behavioural tendencies already align well with the customer-focused and strategically creative nature of Marketing. You appear comfortable analysing situations, understanding different perspectives and combining logical thinking with adaptable problem-solving.

    These behaviours provide a strong foundation for a programme that requires students to understand customer behaviour, develop marketing strategies, build brands and communicate ideas that create value for both organisations and their customers. Throughout your studies you will investigate market trends, interpret research findings and develop campaigns that balance analytical evidence with creative thinking.

    As your knowledge develops, you will strengthen your ability to understand changing customer needs, evaluate market opportunities and design marketing solutions that support business growth. Continuing to develop these behaviours will prepare you for careers where customer understanding, strategic thinking and professional communication are essential.

    Overall, your behavioural profile already reflects many of the habits that Marketing is intentionally designed to strengthen.
    `;

        },

        Moderate: {

            opening:
                "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Marketing, although some behaviours will continue developing as customer analysis and strategic decision-making become increasingly sophisticated.",

            strengths:
                "You already demonstrate behaviours that support analytical reasoning, customer awareness and structured decision-making, all of which contribute positively throughout the programme.",

            programmeConnection:
                "Marketing requires students to understand customer behaviour, analyse market evidence, communicate ideas effectively and develop strategies that respond to changing business environments. While many of these behaviours already appear within your profile, strategic communication, creative problem-solving and market adaptability typically strengthen through practical learning and professional experience.",

            coaching:
                "Focus on strengthening customer insight, strategic communication, market analysis and confidence when adapting marketing solutions to changing customer needs.",

            conclusion:
                "With deliberate practice and continued development, your behavioural profile provides a strong platform for succeeding within Marketing."

        },

        Low: {

            opening:
                "Your assessment suggests that some of the behaviours most consistently rewarded throughout Marketing may not yet be among your strongest natural preferences.",

            strengths:
                "This does not prevent success, but it means you may need to approach customer understanding, communication and strategic problem-solving more deliberately than students whose behavioural tendencies already align closely with the programme.",

            programmeConnection:
                "Marketing consistently rewards analytical thinking, customer awareness, creativity, adaptability and professional communication. These behaviours become increasingly important as students progress into brand management, market research, digital marketing and strategic marketing planning.",

            coaching:
                "Deliberately strengthening customer understanding, communication, analytical reasoning and adaptability will make both the academic programme and future marketing environments progressively more manageable.",

            conclusion:
                "Professional marketing capability develops through learning and experience. Students who intentionally cultivate these behaviours often become highly effective marketing professionals capable of creating value for customers, organisations and society."

        }

    },
},

    "BSc Information Technology Management": {
    
    programmeIntelligence: {

        description:
            "The BSc Information Technology Management programme develops graduates who can design, implement, manage and improve information systems that support organisational objectives. The curriculum combines computing, software development, networking, databases, information systems, business management and technology governance to prepare students to bridge technical and managerial functions within modern organisations.",


        knowledgeAreas: [

            "Programming and software development",
            "Information systems",
            "Database management",
            "Computer networks",
            "Computer hardware",
            "Operating systems",
            "Systems analysis and design",
            "Business process automation",
            "IT infrastructure",
            "Information management",
            "Network administration",
            "Systems implementation",
            "Systems maintenance",
            "Systems auditing",
            "Computer and network security",
            "IT service management",
            "IT sourcing and procurement",
            "Software quality management",
            "Electronic business",
            "Mobile web development",
            "Project management",
            "Research methods",
            "Business strategy",
            "Leadership",
            "Business ethics",
            "Business finance",
            "Business statistics",
            "Quantitative methods"
        ],


        learningEnvironment: [

            "Laboratory-based practical computing",
            "Systems development projects",
            "Business-oriented technology case studies",
            "Programming exercises",
            "Technical problem-solving activities",
            "Research-driven learning",
            "Team-based project work",
            "Industrial internship",
            "Independent project work",
            "Technology management scenarios"
        ],


        learningActivities: [

            "Programming assignments",
            "Software design",
            "Database development",
            "Network configuration",
            "Systems analysis",
            "Business process modelling",
            "Requirements analysis",
            "Research projects",
            "Technical report writing",
            "Presentations",
            "Case study analysis",
            "Laboratory exercises",
            "Internship activities",
            "Final year project"
        ],


        teachingMethods: [

            "Lectures",
            "Computer laboratory sessions",
            "Practical demonstrations",
            "Hands-on programming",
            "Case-based learning",
            "Project-based learning",
            "Research supervision",
            "Industry internship",
            "Group discussions",
            "Independent study"
        ],


        assessmentMethods: [

            "Programming assignments",
            "Laboratory practical work",
            "Database and systems projects",
            "Research reports",
            "Case study analysis",
            "Technical presentations",
            "Continuous assessment",
            "Examinations",
            "Internship reports",
            "Final year project"
        ],


        programmeDemands: [

            "Logical and analytical reasoning",
            "Structured problem solving",
            "Programming competence",
            "Systems thinking",
            "Attention to technical detail",
            "Continuous learning of evolving technologies",
            "Integration of business and technology concepts",
            "Research capability",
            "Project planning and execution",
            "Technical documentation",
            "Decision making using data",
            "Professional and ethical judgement"
        ],


        professionalCompetencies: [

            "Software development",
            "Systems analysis and design",
            "Database design and administration",
            "Network administration",
            "Information systems management",
            "IT service management",
            "Business process improvement",
            "Technology implementation",
            "IT governance awareness",
            "Information security awareness",
            "Technical communication",
            "Project execution",
            "Technology procurement",
            "Systems auditing",
            "Research and innovation"
        ],


        graduateExpectations: [

            "Analyse organisational technology needs",
            "Develop technology-enabled business solutions",
            "Design and manage information systems",
            "Implement enterprise technology solutions",
            "Support organisational decision making using information systems",
            "Manage IT infrastructure and services",
            "Evaluate technology risks and controls",
            "Lead technology projects",
            "Communicate technical solutions to business stakeholders",
            "Operate professionally within multidisciplinary business environments"
        ],

        careerPathways: [

            "Systems Analyst",
            "Information Systems Analyst",
            "Business Systems Analyst",
            "IT Project Coordinator",
            "IT Consultant",
            "Database Administrator",
            "Network Administrator",
            "Systems Administrator",
            "Information Manager",
            "IT Service Manager",
            "Technology Support Manager",
            "Business Technology Specialist",
            "IT Auditor",
            "Information Security Analyst",
            "Software Developer",
            "Technology Implementation Specialist"
        ],

        workEnvironment: [

            "Corporate IT departments",
            "Financial institutions",
            "Government organisations",
            "Technology consulting firms",
            "Software companies",
            "Telecommunications organisations",
            "Enterprise technology teams",
            "Business operations environments",
            "Project-based technology teams",
            "Cross-functional organisational settings"
        ],

        environment:
            "A structured, technology-intensive learning environment that combines practical computing with business management and organisational problem solving.",

        workStyle:
            "Students regularly analyse business problems, develop technology solutions, complete technical projects, work in teams, conduct independent research and apply computing knowledge in organisational contexts.",

        demands:
            "Success requires sustained analytical thinking, technical precision, structured problem solving, continuous learning, project execution, effective communication and the ability to integrate technology with business objectives.",

        realityCheck: {

            workload:
                "High. Students balance programming, laboratory work, business courses, technical projects, internship and a substantial final-year project.",

            mathematics:
                "Moderate. Quantitative reasoning is required through statistics, quantitative methods, algorithms and analytical problem solving rather than advanced theoretical mathematics.",

            reading:
                "High. Students regularly engage with technical documentation, research literature, systems concepts, business case studies and technology standards.",

            writing:
                "High. The programme requires technical reports, project documentation, research writing, internship reports and professional communication.",

            teamwork:
                "High. Many learning activities involve collaborative systems development, business projects and organisational case studies.",

            presentations:
                "Moderate to High. Students present research findings, project outcomes and technology solutions throughout the programme."

        }

    },
    
    behaviouralRequirements: {

        evidenceAreas: {

            technicalProblemSolving: {

                description:
                    "Students repeatedly analyse technical problems, develop computing solutions, debug systems, evaluate alternatives and implement structured technology solutions throughout the programme.",

                requiredBecause:
                    "Programming, systems analysis, software development, databases, networking, operating systems, implementation and project work all require disciplined analytical problem solving.",

                observableBehaviours: [

                    "Breaks complex technical problems into manageable components",
                    "Develops logical solution pathways",
                    "Evaluates multiple technical alternatives",
                    "Diagnoses system faults systematically",
                    "Applies structured reasoning during implementation",
                    "Uses evidence to refine technical solutions"

                ],

                assessmentIndicators: [

                    "Programming assignments",
                    "Systems development projects",
                    "Database implementation",
                    "Systems analysis exercises",
                    "Laboratory practicals",
                    "Final year project"

                ],

                evidenceWeight: 23,

                behaviouralDimensions: {

                    inclusion: 30,
                    control: 82,
                    affection: 18,
                    thinking: 96,
                    flexibility: 68

                }

            },

            enterpriseSystemsIntegration: {

                description:
                    "Students learn to align technology with organisational goals by analysing business processes, designing information systems and integrating technology into business operations.",

                requiredBecause:
                    "Management Information Systems, Information Management, Business Policy, Systems Analysis, IT Service Management, Procurement and Business Process Automation consistently require business-technology integration.",

                observableBehaviours: [

                    "Considers organisational objectives before selecting solutions",
                    "Balances technical and business requirements",
                    "Evaluates system impact across departments",
                    "Coordinates multiple system components",
                    "Thinks beyond isolated technical tasks"

                ],

                assessmentIndicators: [

                    "Case studies",
                    "Systems analysis projects",
                    "Business process modelling",
                    "Technology strategy assignments",
                    "Implementation planning"

                ],

                evidenceWeight: 20,

                behaviouralDimensions: {

                    inclusion: 48,
                    control: 78,
                    affection: 36,
                    thinking: 90,
                    flexibility: 74

                }

            },

            technologyImplementationAndGovernance: {

                description:
                    "Students repeatedly plan, implement, administer, monitor and improve organisational technology resources while considering quality, security, audit and governance requirements.",

                requiredBecause:
                    "Courses in implementation, maintenance, auditing, procurement, quality management, IT services, administration and security all emphasise disciplined technology governance.",

                observableBehaviours: [

                    "Follows structured implementation procedures",
                    "Maintains technical standards",
                    "Monitors system quality",
                    "Evaluates operational risks",
                    "Applies governance principles",
                    "Documents implementation activities"

                ],

                assessmentIndicators: [

                    "Implementation projects",
                    "Systems audit exercises",
                    "Infrastructure administration",
                    "Quality management assignments",
                    "Security evaluations"

                ],

                evidenceWeight: 19,

                behaviouralDimensions: {

                    inclusion: 34,
                    control: 92,
                    affection: 20,
                    thinking: 88,
                    flexibility: 56

                }

            },

            collaborativeTechnologyProjects: {

                description:
                    "Students develop technology solutions through collaborative projects, internships, presentations and multidisciplinary business activities requiring effective coordination with others.",

                requiredBecause:
                    "Project work, internship, leadership, communication, research, systems development and organisational case studies consistently require collaboration.",

                observableBehaviours: [

                    "Coordinates with team members",
                    "Communicates technical ideas clearly",
                    "Shares responsibilities",
                    "Supports collective problem solving",
                    "Works effectively with business stakeholders"

                ],

                assessmentIndicators: [

                    "Group projects",
                    "Presentations",
                    "Internship evaluation",
                    "Research activities",
                    "Project demonstrations"

                ],

                evidenceWeight: 18,

                behaviouralDimensions: {

                    inclusion: 70,
                    control: 58,
                    affection: 60,
                    thinking: 66,
                    flexibility: 72

                }

            },

            continuousLearningAndProfessionalPractice: {

                description:
                    "Students continually expand technical knowledge, conduct research, adapt to emerging technologies and demonstrate ethical professional behaviour throughout the programme.",

                requiredBecause:
                    "Research Methods, Professional Computing Practice, Ethics, Information Management, Electronic Business, Security, Mobile Development and the final project require continuous learning.",

                observableBehaviours: [

                    "Learns unfamiliar technologies",
                    "Conducts independent research",
                    "Applies professional ethics",
                    "Keeps technical knowledge current",
                    "Reflects on technology decisions",
                    "Transfers learning between contexts"

                ],

                assessmentIndicators: [

                    "Research proposal",
                    "Project work",
                    "Independent study",
                    "Professional practice assignments",
                    "Emerging technology projects"

                ],

                evidenceWeight: 20,

                behaviouralDimensions: {

                    inclusion: 40,
                    control: 70,
                    affection: 34,
                    thinking: 84,
                    flexibility: 90

                }

            }

        }

    },
    
    programmeBehaviouralModel: {

        inclusion: {

            score: 48,

            derivedFrom: [

                "Collaborative Technology Projects",
                "Enterprise Systems Integration",
                "Leadership",
                "Internship",
                "Project Work"

            ]

        },


        control: {

            score: 89,

            derivedFrom: [

                "Technical Problem Solving",
                "Technology Implementation and Governance",
                "Systems Analysis",
                "Systems Audit",
                "Software Quality Management",
                "Project Planning"

            ]

        },


        affection: {

            score: 34,

            derivedFrom: [

                "Professional Communication",
                "Collaborative Technology Projects",
                "Business Ethics",
                "Internship"

            ]

        },


        thinking: {

            score: 94,

            derivedFrom: [

                "Programming",
                "Systems Analysis",
                "Database Management",
                "Computer Networks",
                "Information Systems",
                "Research Methods",
                "Problem Solving",
                "Business Process Automation"

            ]

        },


        flexibility: {

            score: 79,

            derivedFrom: [

                "Continuous Learning",
                "Emerging Technologies",
                "Technology Implementation",
                "Information Management",
                "Professional Computing Practice",
                "Electronic Business",
                "Mobile Web Development"

            ]

        },


        traits: [

            "Systems-oriented thinker",

            "Structured technical problem solver",

            "Business technology integrator",

            "Process optimiser",

            "Methodical decision maker",

            "Analytical investigator",

            "Technology implementation specialist",

            "Continuous learner",

            "Quality-conscious practitioner",

            "Evidence-based planner"

        ]

    },

    behaviourProfile: {

        inclusion: 48,

        control: 89,

        affection: 34,

        thinking: 94,

        flexibility: 79,

        traits: [

            "systems-oriented",

            "methodical",

            "analytical",

            "enterprise-focused",

            "technology integrator",

            "process-driven",

            "quality-conscious",

            "continuous learner"

        ]

    },

    fitExplanations: {

        High:
            "Your behavioural tendencies align strongly with the structured, analytical and systems-focused nature of Information Technology Management. You are likely to thrive in environments that require solving complex problems, implementing technology solutions and managing information systems that support organisational goals.",

        Moderate:
            "Several aspects of Information Technology Management align with your behavioural strengths. You may perform well in structured technical work, although the programme's emphasis on systems thinking, continuous learning and disciplined implementation may require additional development in some areas.",

        Low:
            "Information Technology Management may feel demanding if you naturally prefer environments with less structure, limited analytical problem solving or minimal engagement with technology and organisational systems. Success in this programme depends on developing confidence in structured reasoning, technology integration and continuous learning."

    },

    applicationNarratives: {

        High: {

            title:
                "Why this programme may suit you",

            message:
                "Your behavioural tendencies appear well aligned with the demands of Information Technology Management. The programme rewards analytical thinking, structured problem solving, disciplined execution and the ability to connect technology with organisational needs. These qualities are consistently reinforced throughout the curriculum."

        },

        Moderate: {

            title:
                "Why this programme may suit you",

            message:
                "You already demonstrate behavioural qualities that support success in Information Technology Management. As the programme progresses, strengthening your systems thinking, technical discipline and adaptability to evolving technologies will help you meet its increasing demands."

        },

        Low: {

            title:
                "Why this programme may suit you",

            message:
                "Information Technology Management places sustained emphasis on analytical reasoning, structured implementation and technology-driven problem solving. If these are not yet your strongest behavioural tendencies, you may need deliberate practice to become comfortable working with complex organisational systems and evolving technologies."

        }

    },

    fitDimensions: {

        analytical: 94,

        social: 46,

        structure: 91,

        creativity: 60,

        emotionalDemand: 34,

        adaptability: 80

    },

    environmentTraits: {

        structure: 90,

        collaboration: 66,

        autonomy: 72,

        pace: 82,

        uncertainty: 58,

        leadership: 52

    },

    semanticTypes: [

        "enterprise_technology",

        "information_systems",

        "technology_management",

        "systems_analysis",

        "business_technology",

        "analytical_program",

        "structured_learning",

        "project_based_learning"

    ],

    learningEnvironment: {

        independence: 78,

        collaboration: 68,

        practical: 92,

        theoretical: 70,

        pace: 82

    },

        skillsToStrengthen: [

            "Systems Thinking",

            "Structured Problem Solving",

            "Programming Discipline",

            "Business Process Analysis",

            "Technical Communication",

            "Project Planning",

            "Technology Adaptability",

            "Research and Investigation"

        ],

        explanationBlueprint: {

            programmeIdentity: {

                title: "Information Technology Management",

                summary:
                    "Information Technology Management develops the ability to design, implement and manage technology solutions that improve organisational performance by integrating computing expertise with business strategy.",

                purpose:
                    "The programme prepares students to analyse organisational needs, develop technology-enabled solutions and manage information systems that support effective business operations and digital transformation."

            },

            behaviouralStory: {

                whoThrives: {

                    summary:
                        "Students who enjoy solving complex problems, thinking systematically, analysing business processes and applying technology to practical organisational challenges often thrive in Information Technology Management.",

                    keyBehaviours: [

                        "Systems thinking",

                        "Analytical reasoning",

                        "Structured problem solving",

                        "Continuous learning"

                    ]

                },

                whyBehaviourMatters: {

                    summary:
                        "Information Technology Management consistently rewards behaviours that support structured analysis, disciplined implementation, technology integration and evidence-based organisational decision-making.",

                    evidenceAreas: [

                        "technicalProblemSolving",

                        "enterpriseSystemsIntegration",

                        "technologyImplementationAndGovernance",

                        "continuousLearningAndProfessionalPractice"

                    ]

                },

                learningJourney: {

                    summary:
                        "Students progressively develop from understanding computing technologies to analysing organisational systems, implementing enterprise solutions, managing technology projects and improving business processes through information systems."

                },

                professionalMindset: {

                    summary:
                        "Successful technology managers combine analytical thinking, structured execution, business awareness and continuous learning to ensure technology creates meaningful organisational value."

                }

            },

            fitInterpretation: {

                high: {

                    message:
                        "Your behavioural profile already reflects many of the habits that Information Technology Management consistently rewards.",

                    coachingFocus: [

                        "Enterprise systems leadership",

                        "Technology strategy",

                        "Digital transformation"

                    ]

                },

                moderate: {

                    message:
                        "Your behavioural profile provides a strong foundation while highlighting behaviours that will continue developing through systems analysis, technology implementation and organisational problem solving.",

                    coachingFocus: [

                        "Systems integration",

                        "Technical communication",

                        "Project execution"

                    ]

                },

                low: {

                    message:
                        "Some of the behaviours consistently rewarded throughout Information Technology Management may not yet be among your strongest natural preferences. These behaviours become stronger through structured technical practice, project experience and continuous professional learning.",

                    coachingFocus: [

                        "Analytical reasoning",

                        "Systems thinking",

                        "Structured implementation",

                        "Technology adaptability"

                    ]

                }

            },

            futureOutlook: {

                summary:
                    "Graduates who strengthen these behaviours become increasingly prepared to design, implement and manage technology solutions that improve organisational performance across diverse industries.",

                careerMindset:
                    "Technology professionals create organisational value by combining technical expertise, business understanding and disciplined systems thinking to solve complex organisational problems."

            }

        },

        behaviouralNarrative: {

            High({ applicantInterpretation }) {

                return `
        Your assessment suggests that many of your natural behavioural tendencies already align well with the structured and systems-oriented nature of Information Technology Management. You appear comfortable analysing complex situations, organising information systematically and approaching problems through logical, evidence-based reasoning.

        These behaviours provide a strong foundation for a programme that requires students to design information systems, analyse organisational processes, develop technology solutions and manage enterprise technologies that support business objectives. Throughout your studies you will integrate technical knowledge with business understanding while solving practical organisational challenges through computing and information systems.

        As your knowledge develops, you will strengthen your ability to implement technology solutions, coordinate systems projects and support digital transformation within organisations. Continuing to develop these behaviours will prepare you for careers where structured thinking, technical discipline and organisational awareness are essential.

        Overall, your behavioural profile already reflects many of the habits that Information Technology Management is intentionally designed to strengthen.
        `;

            },

            Moderate: {

                opening:
                    "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Information Technology Management, although some behaviours will continue developing as technology projects and organisational systems become increasingly complex.",

                strengths:
                    "You already demonstrate behaviours that support analytical reasoning, structured thinking and systematic problem solving, all of which contribute positively throughout the programme.",

                programmeConnection:
                    "Information Technology Management requires students to analyse business needs, implement technology solutions, coordinate systems and manage information resources that support organisational performance. While many of these behaviours already appear within your profile, systems thinking, technical communication and technology integration typically strengthen through practical learning and project experience.",

                coaching:
                    "Focus on strengthening systems analysis, technical communication, project planning and confidence when integrating technology with organisational objectives.",

                conclusion:
                    "With deliberate practice and continued development, your behavioural profile provides a strong platform for succeeding within Information Technology Management."

            },

            Low: {

                opening:
                    "Your assessment suggests that some of the behaviours most consistently rewarded throughout Information Technology Management may not yet be among your strongest natural preferences.",

                strengths:
                    "This does not prevent success, but it means you may need to approach structured problem solving, systems thinking and technology integration more deliberately than students whose behavioural tendencies already align closely with the programme.",

                programmeConnection:
                    "Information Technology Management consistently rewards analytical reasoning, disciplined implementation, continuous learning and the ability to connect technology with organisational needs. These behaviours become increasingly important as students progress into enterprise systems, technology governance, systems analysis and digital transformation projects.",

                coaching:
                    "Deliberately strengthening systems thinking, analytical reasoning, structured implementation and continuous technology learning will make both the academic programme and future professional environments progressively more manageable.",

                conclusion:
                    "Professional technology capability develops through learning and experience. Students who intentionally cultivate these behaviours often become highly effective technology professionals capable of improving organisations through information systems and digital innovation."

            }

        },
    },

    "BSc Data Science and Analytics": {
        behaviourProfile: {
            inclusion: 52,
            control: 86,
            affection: 38,
            thinking: 98,
            flexibility: 74,
            traits: [
                "analytical",
                "curious",
                "evidence-driven"
            ]
        },

        fitDimensions: {
            analytical: 95,

            social: 60,

            structure: 80,

            creativity: 70,

            emotionalDemand: 34,

            adaptability: 75
        },

        category: "Data Science & Analytics",

        description:
            "Combines statistics, programming, machine learning, and business analytics to transform data into strategic insights and evidence-based decision-making.",

        environment:
            "Technology-driven environments involving data modelling, artificial intelligence, business intelligence, predictive analytics, and evidence-based decision making.",

        workStyle:
            "Requires analytical thinking, logical reasoning, structured problem-solving, attention to detail, and continuous learning of emerging technologies.",

        demands:
            "Strong quantitative reasoning, programming ability, statistical analysis, data interpretation, and the ability to communicate technical insights clearly.",

        idealTraits: [
            "analytical",
            "logical",
            "curious",
            "detail-oriented",
            "systematic"
        ],

        personalityFit: {
            strengths: [
                "Analytical reasoning",
                "Pattern recognition",
                "Logical problem solving",
                "Evidence-based decision making"
            ],

            watchOutFor: [
                "Large volumes of complex data",
                "Rapid technological changes",
                "Balancing technical analysis with business understanding"
            ]
        },

        environmentTraits: {

            structureLevel: 80,

            collaborationLevel: 66,

            ambiguityLevel: 76,

            technicalIntensity: 94,

            creativityDemand: 64,

            emotionalDemand: 26,

            leadershipDemand: 48,

            communicationIntensity: 68

        },

        semanticTypes: [
            "technology_program",
            "analytics_program",
            "research_heavy_program",
            "quantitative_program"
        ],

        learningEnvironment: {

            pace: 84,

            independence: 86,

            practicalLearning: 90,

            teamwork: 68,

            assessmentStructure: 82,

            feedbackFrequency: 58

        },

        careerPaths: [
            "Data Analyst",
            "Business Intelligence Analyst",
            "Data Scientist",
            "Machine Learning Engineer",
            "Analytics Consultant"
        ],

        realityCheck: {
            workload:
                "High analytical workload involving programming, statistical modelling, data cleaning, visualization, and solving complex business problems.",

            environment:
                "Technology-focused environments where data is collected, analysed, and translated into strategic business or organisational decisions.",

            pressureLevel:
                "Moderate to high due to project deadlines, accuracy expectations, and rapidly evolving technologies.",

            keyChallenge:
                "Balancing technical expertise with business understanding while communicating complex analytical findings to diverse audiences."
        },

        skillsToStrengthen: [
            "Programming",
            "Statistical Analysis",
            "Machine Learning",
            "Data Visualization",
            "Business Communication"
        ],
    },

    "BA Communication Studies": {
    behaviourProfile: {
        inclusion: 92,
        control: 64,
        affection: 88,
        thinking: 72,
        flexibility: 90,
        traits: [
            "expressive",
            "curious",
            "people-oriented"
        ]
    },

    category: "Media & Communication",

    description:
        "Explores media, messaging, public interaction, storytelling, and communication strategy.",

    environment:
        "Collaborative and expressive environments involving presentations, teamwork, audience engagement, and media communication.",

    workStyle:
        "Requires creativity, confidence, communication ability, and adaptability across different social and media contexts.",

    demands:
        "Strong verbal expression, interpersonal skills, creativity, and comfort engaging with audiences.",

    idealTraits: [
        "expressive",
        "social",
        "creative",
        "adaptable"
    ],

    fitExplanations: {
        High:
        "Your personality aligns strongly with the expressive and socially engaging nature of Communication Studies. You are likely to thrive in environments involving storytelling, communication, and audience interaction.",

        Moderate:
        "Some aspects of Communication Studies may suit your strengths, though the highly social and presentation-driven nature of the field may occasionally require adjustment.",

        Low:
        "Communication Studies may feel overly public-facing or socially demanding if you naturally prefer highly technical, analytical, or independent environments."
    },

    applicationNarratives: {
        High: {
            title:
                "Why this may suit your learning style",

            message:
                "Your personality appears strongly aligned with the expressive and collaborative nature of Communication Studies. The field rewards confidence, creativity, audience awareness, and the ability to communicate ideas effectively across different environments."
        },

        Moderate: {
            title:
                "Why this may suit your learning style",

            message:
                "Certain aspects of Communication Studies may align naturally with your strengths, particularly where communication, creativity, and audience engagement are involved. However, highly public-facing or presentation-heavy environments may occasionally require adjustment."
        },

        Low: {
            title:
                "Why this may suit your learning style",

            message:
                "Communication Studies may feel socially demanding or heavily expression-focused if you naturally prefer highly analytical, technical, or independent environments. Developing comfort with public engagement and audience interaction may require deliberate growth."
        }
    },

    fitDimensions: {
        analytical: 52,
        social: 98,
        structure: 44,
        creativity: 90,
        emotionalDemand: 78,
        adaptability: 92
    },

    environmentTraits: {

        structureLevel: 56,

        collaborationLevel: 92,

        ambiguityLevel: 82,

        technicalIntensity: 36,

        creativityDemand: 84,

        emotionalDemand: 72,

        leadershipDemand: 68,

        communicationIntensity: 100

    },

    semanticTypes: [
        "communication_program",
        "creative_program",
        "people_centered_program",
        "expression_oriented_program"
    ],

    learningEnvironment: {

        pace: 74,

        independence: 70,

        practicalLearning: 88,

        teamwork: 90,

        assessmentStructure: 56,

        feedbackFrequency: 86

    },

    careerPaths: [
        "Media Coordinator",
        "Communications Officer",
        "Content Strategist",
        "Broadcast Assistant",
        "Corporate Communications Associate"
        ],

    realityCheck: {
    workload:
        "Moderate creative and communication-focused workload involving presentations, writing, media work, and audience engagement.",

    environment:
        "Dynamic communication environments requiring collaboration, confidence, and adaptability.",

    pressureLevel:
        "Moderate due to public-facing communication expectations and deadline-driven media tasks.",

    keyChallenge:
        "Communicating clearly and effectively across different audiences and platforms."
    },

    skillsToStrengthen: [
    "Public Speaking",
    "Writing",
    "Media Communication",
    "Creativity",
    "Audience Awareness"
    ],
    },

    "BA Applied French and Communications": {
    behaviourProfile: {
        inclusion: 90,
        control: 60,
        affection: 90,
        thinking: 74,
        flexibility: 94,
        traits: [
            "adaptable",
            "communicative",
            "globally-minded"
        ]
    },

        category: "Languages & Communication",

        description:
            "Develops advanced French language skills alongside communication, intercultural competence, media, diplomacy, and international business communication.",

        environment:
            "Interactive multicultural environments involving language, communication, public engagement, international collaboration, and cross-cultural exchange.",

        workStyle:
            "Requires strong communication, adaptability, cultural awareness, creativity, confidence, and the ability to work effectively across languages and cultures.",

        demands:
            "Excellent written and spoken communication, language proficiency, presentation skills, critical thinking, and intercultural sensitivity.",

        idealTraits: [
            "communicative",
            "adaptable",
            "confident",
            "creative",
            "people-oriented"
        ],

        personalityFit: {
            strengths: [
                "Language learning",
                "Public communication",
                "Relationship building",
                "Cross-cultural collaboration"
            ],

            watchOutFor: [
                "Maintaining language fluency",
                "Public speaking pressure",
                "Communicating across different cultural expectations"
            ]
        },

        fitDimensions: {
            analytical: 66,

            social: 96,

            structure: 56,

            creativity: 82,

            emotionalDemand: 72,

            adaptability: 96

        },

        environmentTraits: {

            structureLevel: 54,

            collaborationLevel: 90,

            ambiguityLevel: 80,

            technicalIntensity: 24,

            creativityDemand: 76,

            emotionalDemand: 70,

            leadershipDemand: 62,

            communicationIntensity: 100

        },

        semanticTypes: [
            "communication_program",
            "language_program",
            "people_centered_program",
            "international_program"
        ],

        learningEnvironment: {

            pace: 72,

            independence: 74,

            practicalLearning: 86,

            teamwork: 88,

            assessmentStructure: 58,

            feedbackFrequency: 90

        },

        careerPaths: [
            "Translator",
            "Interpreter",
            "Corporate Communications Officer",
            "Diplomatic Service Officer",
            "International Relations Officer"
        ],

        realityCheck: {
            workload:
                "Moderate workload involving language practice, presentations, writing, cultural studies, and communication projects.",

            environment:
                "Collaborative environments where communication, language proficiency, and cultural understanding are essential.",

            pressureLevel:
                "Moderate due to language accuracy, presentations, and real-time communication expectations.",

            keyChallenge:
                "Maintaining high language proficiency while effectively communicating across diverse cultural and professional settings."
        },

        skillsToStrengthen: [
            "French Language",
            "Public Speaking",
            "Intercultural Communication",
            "Writing",
            "Presentation Skills"
        ],
    },

    "BA Public Relations": {
    programmeIntelligence: {

        description:
            "The Bachelor of Arts in Public Relations Management prepares graduates to build, manage and sustain relationships between organisations and their stakeholders through strategic communication, research, media engagement, reputation management and ethical professional practice. The curriculum integrates communication theory, behavioural sciences, public relations strategy, media studies, organisational communication and professional practice to develop communication professionals capable of managing organisational reputation in diverse sectors.",

        knowledgeAreas: [

            "Public relations theory",
            "Mass communication",
            "Communication theory",
            "Strategic communication",
            "Integrated marketing communication",
            "Public relations planning",
            "Campaign management",
            "Communication research",
            "Public opinion",
            "Media relations",
            "Media law",
            "Issues management",
            "Crisis communication",
            "Corporate social responsibility",
            "Public relations ethics",
            "Political communication",
            "International public relations",
            "Employee relations",
            "Event management",
            "Technical writing",
            "Public speaking",
            "Organisational behaviour",
            "Psychology",
            "Sociology",
            "Leadership",
            "Research methods",
            "Management",
            "Management information systems"

        ],

        learningEnvironment: [

            "Communication-focused classroom discussions",
            "Campaign planning exercises",
            "Media analysis",
            "Public relations workshops",
            "Applied research projects",
            "Case study analysis",
            "Presentation-intensive learning",
            "Writing-intensive coursework",
            "Internship placement",
            "Final-year project"

        ],

        learningActivities: [

            "Communication planning",
            "Campaign development",
            "Public speaking",
            "Professional writing",
            "Media analysis",
            "Stakeholder analysis",
            "Research projects",
            "Case study evaluation",
            "Event planning",
            "Crisis simulations",
            "Group projects",
            "Internship activities"

        ],

        teachingMethods: [

            "Lectures",
            "Case-based learning",
            "Group discussions",
            "Workshops",
            "Presentations",
            "Research supervision",
            "Industry-oriented practical activities",
            "Field-based internship",
            "Project-based learning"

        ],

        assessmentMethods: [

            "Communication campaigns",
            "Research reports",
            "Professional writing assignments",
            "Presentations",
            "Case studies",
            "Group projects",
            "Continuous assessment",
            "Examinations",
            "Internship reports",
            "Final-year project"

        ],

        programmeDemands: [

            "Strategic thinking",
            "Excellent written communication",
            "Professional oral communication",
            "Audience analysis",
            "Relationship management",
            "Research and evidence gathering",
            "Critical evaluation",
            "Ethical judgement",
            "Campaign planning",
            "Media awareness",
            "Adaptability across audiences",
            "Professional collaboration"

        ],

        professionalCompetencies: [

            "Strategic communication",
            "Public relations planning",
            "Campaign management",
            "Media relations",
            "Communication research",
            "Stakeholder engagement",
            "Corporate communication",
            "Issues management",
            "Crisis communication",
            "Professional writing",
            "Public speaking",
            "Event management",
            "Corporate reputation management",
            "Ethical communication",
            "Communication evaluation"

        ],

        graduateExpectations: [

            "Develop communication strategies",
            "Manage organisational reputation",
            "Build stakeholder relationships",
            "Conduct communication research",
            "Plan and evaluate communication campaigns",
            "Communicate effectively across diverse audiences",
            "Respond professionally during organisational crises",
            "Advise organisations on communication strategy",
            "Represent organisations ethically",
            "Operate effectively in public, private and non-profit sectors"

        ],

        careerPathways: [

            "Public Relations Officer",
            "Corporate Communications Officer",
            "Communications Specialist",
            "Media Relations Officer",
            "Corporate Affairs Officer",
            "Communications Consultant",
            "Public Affairs Officer",
            "Stakeholder Engagement Officer",
            "Corporate Reputation Manager",
            "CSR Communications Officer",
            "Political Communications Officer",
            "Campaign Coordinator",
            "Event Communications Manager",
            "Internal Communications Officer"

        ],

        workEnvironment: [

            "Corporate organisations",
            "Government institutions",
            "Non-governmental organisations",
            "Educational institutions",
            "Media organisations",
            "Public sector agencies",
            "Political organisations",
            "Communication consultancies",
            "International organisations",
            "Event management environments"

        ],

        environment:
            "A collaborative, communication-intensive learning environment centred on research, strategic planning, stakeholder engagement and professional communication.",

        workStyle:
            "Students continually analyse audiences, develop communication strategies, conduct research, write professionally, present ideas and collaborate on campaigns addressing organisational communication challenges.",

        demands:
            "Success requires excellent communication, analytical thinking, relationship building, strategic planning, ethical judgement, adaptability, research capability and confidence when engaging diverse audiences.",

        realityCheck: {

            workload:
                "High. Students balance extensive reading, professional writing, presentations, research, campaign development, internship and a substantial final-year project.",

            mathematics:
                "Low to Moderate. Quantitative skills are primarily required for research methods, communication research and interpreting evidence rather than advanced mathematics.",

            reading:
                "Very High. Students engage extensively with communication theory, behavioural sciences, media studies, public policy, law and professional literature.",

            writing:
                "Very High. Professional writing is one of the programme's central academic and professional competencies.",

            teamwork:
                "Very High. Campaign planning, workshops, events, presentations and internship require sustained collaboration.",

            presentations:
                "Very High. Public speaking, campaign pitches, stakeholder communication and professional presentations are core components throughout the programme."

        }

    },

    behaviouralRequirements: {

        evidenceAreas: {

            strategicCommunication: {

                description:
                    "Students repeatedly develop, organise and deliver communication that aligns with organisational objectives while considering audience needs, reputation and context.",

                requiredBecause:
                    "Public Relations Theory, Strategic Planning, Integrated Marketing Communications, Public Speaking, Technical Writing, Public Relations Practice and Campaign Planning consistently require deliberate communication that supports organisational goals.",

                observableBehaviours: [

                    "Communicates clearly with different audiences",
                    "Adapts communication style to context",
                    "Develops structured communication strategies",
                    "Explains complex ideas persuasively",
                    "Maintains professionalism during communication",
                    "Aligns messages with organisational objectives"

                ],

                assessmentIndicators: [

                    "Communication campaigns",
                    "Professional writing assignments",
                    "Presentations",
                    "Campaign strategy projects",
                    "Public speaking exercises"

                ],

                evidenceWeight: 22,

                behaviouralDimensions: {

                    inclusion: 82,
                    control: 70,
                    affection: 76,
                    thinking: 74,
                    flexibility: 78

                }

            },



            stakeholderRelationshipManagement: {

                description:
                    "Students learn to understand diverse stakeholder groups, build productive relationships and maintain organisational trust across public, private and non-profit environments.",

                requiredBecause:
                    "Employee Relations, Corporate Social Responsibility, International Public Relations, Political Communication, Public Sector PR, NGO Public Relations and Public Relations Practice all emphasise stakeholder engagement.",

                observableBehaviours: [

                    "Builds productive professional relationships",
                    "Understands stakeholder expectations",
                    "Balances competing interests",
                    "Works collaboratively across groups",
                    "Maintains trust and credibility"

                ],

                assessmentIndicators: [

                    "Stakeholder analysis",
                    "Case studies",
                    "Internship evaluation",
                    "Campaign planning",
                    "Relationship management exercises"

                ],

                evidenceWeight: 20,

                behaviouralDimensions: {

                    inclusion: 90,
                    control: 62,
                    affection: 88,
                    thinking: 68,
                    flexibility: 74

                }

            },



            communicationResearchAndInsight: {

                description:
                    "Students investigate communication problems, gather evidence, analyse audiences and use research findings to support communication decisions.",

                requiredBecause:
                    "Research Methods, Communication Research, Public Relations Research, Sociology, Psychology, Communication Theory and Project Work consistently require evidence-based decision making.",

                observableBehaviours: [

                    "Collects relevant evidence",
                    "Analyses audience behaviour",
                    "Interprets research findings",
                    "Uses data to guide communication",
                    "Evaluates communication effectiveness"

                ],

                assessmentIndicators: [

                    "Research proposals",
                    "Research reports",
                    "Audience analysis",
                    "Project work",
                    "Communication evaluations"

                ],

                evidenceWeight: 20,

                behaviouralDimensions: {

                    inclusion: 54,
                    control: 74,
                    affection: 50,
                    thinking: 92,
                    flexibility: 70

                }

            },



            reputationAndCrisisManagement: {

                description:
                    "Students learn to anticipate communication risks, manage organisational reputation and respond strategically to emerging issues and crises.",

                requiredBecause:
                    "Issues and Crisis Management, Media Relations, Media Law, Ethics, Corporate Communication and Public Relations Strategy repeatedly emphasise responsible communication under pressure.",

                observableBehaviours: [

                    "Remains composed under pressure",
                    "Evaluates communication risks",
                    "Makes balanced communication decisions",
                    "Protects organisational reputation",
                    "Responds strategically during uncertainty"

                ],

                assessmentIndicators: [

                    "Crisis simulations",
                    "Media case studies",
                    "Strategic communication plans",
                    "Ethics assignments",
                    "Scenario analysis"

                ],

                evidenceWeight: 18,

                behaviouralDimensions: {

                    inclusion: 62,
                    control: 82,
                    affection: 60,
                    thinking: 86,
                    flexibility: 82

                }

            },



            professionalCommunicationPractice: {

                description:
                    "Students continually refine professional communication through writing, presentations, workshops, internships and practical engagement with organisational communication.",

                requiredBecause:
                    "Technical Writing, Public Writing, Public Speaking, Internship, Workshops, Event Management and Project Work all reinforce professional communication practice.",

                observableBehaviours: [

                    "Produces professional written work",
                    "Delivers confident presentations",
                    "Works effectively within teams",
                    "Receives and applies feedback",
                    "Demonstrates professional conduct",
                    "Adapts to different professional environments"

                ],

                assessmentIndicators: [

                    "Professional writing",
                    "Presentations",
                    "Workshop participation",
                    "Internship reports",
                    "Project presentations"

                ],

                evidenceWeight: 20,

                behaviouralDimensions: {

                    inclusion: 80,
                    control: 72,
                    affection: 78,
                    thinking: 70,
                    flexibility: 84

                }

            }

        }

    },

    programmeBehaviouralModel: {

        inclusion: {

            score: 86,

            derivedFrom: [

                "Strategic Communication",
                "Stakeholder Relationship Management",
                "Professional Communication Practice",
                "Public Speaking",
                "Campaign Planning",
                "Internship"

            ]

        },


        control: {

            score: 74,

            derivedFrom: [

                "Strategic Communication",
                "Communication Research and Insight",
                "Reputation and Crisis Management",
                "Campaign Planning",
                "Professional Practice"

            ]

        },


        affection: {

            score: 82,

            derivedFrom: [

                "Stakeholder Relationship Management",
                "Professional Communication Practice",
                "Corporate Social Responsibility",
                "Employee Relations",
                "Public Relations Practice"

            ]

        },


        thinking: {

            score: 84,

            derivedFrom: [

                "Communication Research and Insight",
                "Strategic Communication",
                "Communication Theory",
                "Psychology",
                "Sociology",
                "Media Analysis",
                "Research Methods"

            ]

        },


        flexibility: {

            score: 88,

            derivedFrom: [

                "Reputation and Crisis Management",
                "Professional Communication Practice",
                "International Public Relations",
                "Political Communication",
                "Issues Management",
                "Integrated Marketing Communications"

            ]

        },


        traits: [

            "Strategic communicator",

            "Relationship builder",

            "Audience-centred thinker",

            "Persuasive communicator",

            "Research-informed decision maker",

            "Reputation manager",

            "Collaborative professional",

            "Adaptable communicator",

            "Ethically responsible practitioner",

            "Situational problem solver"

        ]

    },
    
    behaviourProfile: {
        inclusion: 86,
        control: 74,
        affection: 82,
        thinking: 84,
        flexibility: 88,
        traits: [
            "strategic communicator",
            "relationship builder",
            "audience-centred thinker",
            "persuasive communicator",
            "research-informed decision maker",
            "reputation manager",
            "collaborative professional",
            "adaptable communicator"
        ]
    },

    fitExplanations: {

        High:
        "Your behavioural preferences align strongly with the demands of Public Relations Management. You are likely to thrive in environments that require strategic communication, stakeholder engagement, persuasive thinking, and adapting messages to different audiences while maintaining strong professional relationships.",

        Moderate:
        "Many aspects of Public Relations Management match your strengths, particularly in communication, collaboration, and problem-solving. Some areas—such as managing competing stakeholder expectations, responding to rapidly changing situations, and maintaining consistent public engagement—may require additional development.",

        Low:
        "Public Relations Management may feel demanding if you naturally prefer highly independent, highly structured, or predominantly technical environments. The programme requires frequent interaction with people, strategic communication, adaptability, and confidence in managing relationships across diverse situations."

    },

    applicationNarratives: {

        High: {

            title:
                "Why this may suit your learning style",

            message:
                "Your behavioural preferences align well with the collaborative and strategically driven nature of Public Relations Management. The programme rewards relationship building, persuasive communication, analytical thinking, and the ability to adapt confidently across different audiences, industries, and communication challenges."

        },

        Moderate: {

            title:
                "Why this may suit your learning style",

            message:
                "Several aspects of Public Relations Management complement your strengths, particularly where communication, teamwork, and structured problem-solving are involved. Developing greater confidence in managing diverse stakeholders and responding to rapidly changing communication environments will strengthen your success."

        },

        Low: {

            title:
                "Why this may suit your learning style",

            message:
                "Public Relations Management may be more challenging if you naturally prefer solitary, highly predictable, or technically focused work. Success in this programme depends on communicating effectively, building relationships, adapting to changing situations, and making informed decisions under public and organisational pressure."

        }

    },

    fitDimensions: {

        analytical: 82,
        social: 94,
        structure: 70,
        creativity: 80,
        emotionalDemand: 82,
        adaptability: 90

    },

    environmentTraits: {

        structureLevel: 68,

        collaborationLevel: 92,

        ambiguityLevel: 86,

        technicalIntensity: 34,

        creativityDemand: 80,

        emotionalDemand: 82,

        leadershipDemand: 76,

        communicationIntensity: 98

    },

    semanticTypes: [

        "strategic_communication_program",

        "stakeholder_engagement_program",
        
        "relationship_management_program",
        
        "research_informed_program",
        
        "adaptive_professional_program"

    ],

    learningEnvironment: {

        pace: 80,

        independence: 68,

        practicalLearning: 92,

        teamwork: 94,

        assessmentStructure: 66,

        feedbackFrequency: 86

    },

    skillsToStrengthen: [

        "Strategic Communication",

        "Stakeholder Relationship Management",
        
        "Communication Research",
        
        "Campaign Planning",
        
        "Reputation and Crisis Management"
    ],

    explanationBlueprint: {

        programmeIdentity: {

            title: "Public Relations Management",

            summary:
                "Public Relations Management develops the ability to build, manage and sustain trust between organisations and their stakeholders through strategic communication, research, ethical judgement and relationship management.",

            purpose:
                "The programme prepares students to analyse stakeholder needs, develop communication strategies, protect organisational reputation and manage communication across complex organisational environments."

        },

        behaviouralStory: {

            whoThrives: {

                summary:
                    "Students who naturally enjoy understanding people, building relationships, communicating strategically and adapting to changing situations often thrive in Public Relations Management.",

                keyBehaviours: [

                    "Strategic communication",

                    "Relationship building",

                    "Audience awareness",

                    "Adaptability"

                ]

            },

            whyBehaviourMatters: {

                summary:
                    "Public Relations consistently rewards behaviours that support stakeholder engagement, communication research, reputation management and ethical decision-making across diverse audiences.",

                evidenceAreas: [

                    "strategicCommunication",

                    "stakeholderRelationshipManagement",

                    "communicationResearchAndInsight",

                    "reputationAndCrisisManagement"

                ]

            },

            learningJourney: {

                summary:
                    "Students progressively develop from understanding communication principles to planning campaigns, conducting research, managing stakeholder relationships and leading strategic communication during complex organisational situations."

            },

            professionalMindset: {

                summary:
                    "Successful public relations professionals combine empathy, strategic thinking, communication expertise and ethical judgement to strengthen relationships between organisations and society."

            }

        },

        fitInterpretation: {

            high: {

                message:
                    "Your behavioural profile already reflects many of the habits that Public Relations Management consistently rewards.",

                coachingFocus: [

                    "Strategic communication leadership",

                    "Corporate reputation management",

                    "Stakeholder influence"

                ]

            },

            moderate: {

                message:
                    "Your behavioural profile provides a strong foundation while highlighting behaviours that will continue developing through stakeholder engagement, communication planning and reputation management.",

                coachingFocus: [

                    "Audience analysis",

                    "Professional communication",

                    "Relationship management"

                ]

            },

            low: {

                message:
                    "Some of the behaviours consistently rewarded throughout Public Relations Management may not yet be among your strongest natural preferences. These behaviours become stronger through practical communication experience, collaboration and professional engagement.",

                coachingFocus: [

                    "Communication confidence",

                    "Relationship building",

                    "Adaptability",

                    "Strategic thinking"

                ]

            }

        },

        futureOutlook: {

            summary:
                "Graduates who strengthen these behaviours become increasingly prepared to build organisational trust, manage reputation and lead communication strategies across public, private and non-profit organisations.",

            careerMindset:
                "Public Relations professionals create long-term organisational value by strengthening relationships, communicating responsibly and maintaining public confidence."

        }

    },

    behaviouralNarrative: {

        High({ applicantInterpretation }) {

            return `
    Your assessment suggests that many of your natural behavioural tendencies already align well with the relationship-focused and strategically communicative nature of Public Relations Management. You appear comfortable understanding different perspectives, communicating thoughtfully and adapting your approach to different situations while maintaining strong professional relationships.

    These behaviours provide a strong foundation for a programme that requires students to analyse stakeholder needs, develop communication strategies, manage organisational reputation and respond professionally to changing communication environments. Throughout your studies you will conduct communication research, plan campaigns, evaluate public opinion and develop strategies that strengthen trust between organisations and the communities they serve.

    As your knowledge develops, you will strengthen your ability to build stakeholder relationships, manage communication during challenging situations and provide strategic advice that supports organisational objectives while maintaining ethical professional standards. Continuing to develop these behaviours will prepare you for careers where communication, trust and professional judgement are essential.

    Overall, your behavioural profile already reflects many of the habits that Public Relations Management is intentionally designed to strengthen.
    `;

        },

        Moderate: {

            opening:
                "Your assessment indicates that many aspects of your behavioural profile provide a good foundation for studying Public Relations Management, although some behaviours will continue developing as stakeholder engagement and strategic communication become increasingly sophisticated.",

            strengths:
                "You already demonstrate behaviours that support communication, collaboration, analytical thinking and professional judgement, all of which contribute positively throughout the programme.",

            programmeConnection:
                "Public Relations Management requires students to understand audiences, build productive relationships, conduct communication research and manage organisational reputation across diverse communication environments. While many of these behaviours already appear within your profile, stakeholder engagement, strategic communication and adaptability typically strengthen through practical learning and professional experience.",

            coaching:
                "Focus on strengthening audience analysis, communication confidence, relationship management and strategic campaign planning.",

            conclusion:
                "With deliberate practice and continued development, your behavioural profile provides a strong platform for succeeding within Public Relations Management."

        },

        Low: {

            opening:
                "Your assessment suggests that some of the behaviours most consistently rewarded throughout Public Relations Management may not yet be among your strongest natural preferences.",

            strengths:
                "This does not prevent success, but it means you may need to approach stakeholder engagement, strategic communication and relationship management more deliberately than students whose behavioural tendencies already align closely with the programme.",

            programmeConnection:
                "Public Relations Management consistently rewards communication, collaboration, adaptability, ethical judgement and the ability to build trust across diverse audiences. These behaviours become increasingly important as students progress into campaign planning, media relations, crisis communication and organisational reputation management.",

            coaching:
                "Deliberately strengthening communication confidence, audience awareness, relationship building and adaptability will make both the academic programme and future professional environments progressively more manageable.",

            conclusion:
                "Professional communication capability develops through learning and experience. Students who intentionally cultivate these behaviours often become highly effective public relations professionals capable of strengthening trust between organisations and the people they serve."

        }

    },

}
    
};